import React, { useState, useEffect, createContext, useContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation, useParams, Link } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";
import { format } from "date-fns";
import { QRCodeSVG } from "qrcode.react";
import { 
  Package, Users, ShoppingCart, TrendingUp, AlertTriangle, DollarSign, 
  BarChart3, LogOut, Plus, Edit, Trash2, Eye, EyeOff, Calendar as CalendarIcon,
  Menu, X, Home, Truck, FileText, Bell, Minus, MapPin, CreditCard,
  Clock, CheckCircle, User, Phone, Mail, ChevronRight, Heart, Star, Upload, MessageCircle, Share2, Printer, Download
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const SHOP_API = `${BACKEND_URL}/api/shop`;

// ==========================================
// CENTRALIZED CONFIG
// ==========================================
const CONFIG = {
  // Payment Details
  UPI_ID: 'paytm.s274py4@pty',
  GPAY_NUMBER: '9248444687',
  PAYMENT_QR: '/payment-qr.jpg',
  
  // Contact Numbers
  ADMIN_WHATSAPP: '917075769452',
  ADMIN_PHONE_1: '7075769452',
  ADMIN_PHONE_2: '8125995777',
  STATUS_PHONES: ['918978152777', '917075769452'],
  
  // Business Info
  BUSINESS_NAME: 'Freshwala Dairy',
  BUSINESS_TAGLINE: 'Fresh Dairy Delivered Daily',
  LOGO_URL: 'https://customer-assets.emergentagent.com/job_ecf1a18d-b8c4-4d51-9a48-b9c5614b6399/artifacts/50hdzzeb_freshwala%20logo.jpg',
  
  // Delivery Settings
  MIN_ORDER_VALUE: 100,
  LOCAL_DELIVERY_FEE: 15,
  OUT_OF_CITY_BASE_FEE: 50,
  OUT_OF_CITY_PER_KG_FEE: 10,
};

// Default product images
const defaultImages = {
  'Milk': 'https://images.unsplash.com/photo-1563636619-e9143da7973b?w=400',
  'Curd': 'https://images.unsplash.com/photo-1488477181946-6428a0291777?w=400',
  'Ghee': 'https://images.unsplash.com/photo-1631452180519-c014fe946bc7?w=400',
  'Paneer': 'https://images.unsplash.com/photo-1631452180775-37c89bd93bcb?w=400',
  'Pickles': 'https://customer-assets.emergentagent.com/job_15199004-28da-4fd9-96c3-06baada2ea38/artifacts/vh64s80p_image.png',
  'Batters': 'https://customer-assets.emergentagent.com/job_15199004-28da-4fd9-96c3-06baada2ea38/artifacts/dz5uv2zx_image.png',
  'Others': 'https://images.unsplash.com/photo-1606787366850-de6330128bfc?w=400',
  'default': 'https://images.unsplash.com/photo-1628088062854-d1870b4553da?w=400'
};

// ==========================================
// AUTH CONTEXTS
// ==========================================

// Admin Auth Context
const AuthContext = createContext(null);
const useAuth = () => useContext(AuthContext);

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('admin_token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      localStorage.removeItem('admin_token');
      setToken(null);
      delete axios.defaults.headers.common['Authorization'];
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { access_token, user } = response.data;
    localStorage.setItem('admin_token', access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(user);
    return user;
  };

  const register = async (name, email, password) => {
    const response = await axios.post(`${API}/auth/register`, { name, email, password });
    const { access_token, user } = response.data;
    localStorage.setItem('admin_token', access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(user);
    return user;
  };

  const logout = () => {
    localStorage.removeItem('admin_token');
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, token, login, register, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

// Customer Auth Context
const CustomerAuthContext = createContext(null);
const useCustomerAuth = () => useContext(CustomerAuthContext);

const CustomerAuthProvider = ({ children }) => {
  const [customer, setCustomer] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('customer_token'));
  const [loading, setLoading] = useState(true);
  const [cart, setCart] = useState({ items: [], total: 0 });

  useEffect(() => {
    if (token) {
      fetchCustomer();
      fetchCart();
    } else {
      setLoading(false);
    }
  }, [token]);

  const getAuthHeaders = () => ({ headers: { Authorization: `Bearer ${token}` } });

  const fetchCustomer = async () => {
    try {
      const response = await axios.get(`${SHOP_API}/auth/me`, getAuthHeaders());
      setCustomer(response.data);
    } catch (error) {
      localStorage.removeItem('customer_token');
      setToken(null);
    } finally {
      setLoading(false);
    }
  };

  const fetchCart = async () => {
    try {
      const response = await axios.get(`${SHOP_API}/cart`, getAuthHeaders());
      setCart(response.data);
    } catch (error) {
      console.error('Failed to fetch cart');
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${SHOP_API}/auth/login`, { email, password });
    const { access_token, customer } = response.data;
    localStorage.setItem('customer_token', access_token);
    setToken(access_token);
    setCustomer(customer);
    return customer;
  };

  const register = async (name, email, password, phone) => {
    const response = await axios.post(`${SHOP_API}/auth/register`, { name, email, password, phone });
    const { access_token, customer } = response.data;
    localStorage.setItem('customer_token', access_token);
    setToken(access_token);
    setCustomer(customer);
    return customer;
  };

  const logout = () => {
    localStorage.removeItem('customer_token');
    setToken(null);
    setCustomer(null);
    setCart({ items: [], total: 0 });
  };

  const addToCart = async (productId, quantity = 1) => {
    try {
      await axios.post(`${SHOP_API}/cart/add?product_id=${productId}&quantity=${quantity}`, {}, getAuthHeaders());
      await fetchCart();
      toast.success('Added to cart!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add to cart');
    }
  };

  const updateCartItem = async (productId, quantity) => {
    try {
      await axios.put(`${SHOP_API}/cart/update?product_id=${productId}&quantity=${quantity}`, {}, getAuthHeaders());
      await fetchCart();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update cart');
    }
  };

  const clearCart = async () => {
    try {
      await axios.delete(`${SHOP_API}/cart/clear`, getAuthHeaders());
      await fetchCart();
    } catch (error) {
      toast.error('Failed to clear cart');
    }
  };

  return (
    <CustomerAuthContext.Provider value={{
      customer, token, login, register, logout, loading,
      cart, fetchCart, addToCart, updateCartItem, clearCart, getAuthHeaders
    }}>
      {children}
    </CustomerAuthContext.Provider>
  );
};

// ==========================================
// ADMIN COMPONENTS (Keep existing)
// ==========================================

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center bg-emerald-50"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div></div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
};

// Admin Login Page
const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isRegister, setIsRegister] = useState(false);
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, register, user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => { if (user) navigate('/dashboard'); }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (isRegister) { await register(name, email, password); toast.success('Registration successful!'); }
      else { await login(email, password); toast.success('Login successful!'); }
      navigate('/dashboard');
    } catch (error) { toast.error(error.response?.data?.detail || 'Authentication failed'); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-50 to-teal-100 p-4">
      <Card className="w-full max-w-md shadow-xl" data-testid="auth-card">
        <CardHeader className="text-center">
          <div className="mx-auto w-16 h-16 bg-emerald-600 rounded-full flex items-center justify-center mb-4">
            <Package className="w-8 h-8 text-white" />
          </div>
          <CardTitle className="text-2xl text-emerald-800">Freshwala Admin</CardTitle>
          <CardDescription>Inventory Management System</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {isRegister && (
              <div className="space-y-2">
                <Label htmlFor="name">Full Name</Label>
                <Input id="name" data-testid="name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Enter your name" required />
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" data-testid="email-input" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Enter your email" required />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Input 
                  id="password" 
                  type={showPassword ? "text" : "password"} 
                  data-testid="password-input" 
                  value={password} 
                  onChange={(e) => setPassword(e.target.value)} 
                  placeholder="Enter your password" 
                  required 
                />
                <button 
                  type="button" 
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  onClick={() => setShowPassword(!showPassword)}
                  data-testid="toggle-password-visibility"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <Button type="submit" className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="submit-btn" disabled={loading}>
              {loading ? 'Please wait...' : (isRegister ? 'Register' : 'Login')}
            </Button>
          </form>
          <div className="mt-4 text-center">
            <button type="button" className="text-emerald-600 hover:underline text-sm" data-testid="toggle-auth-btn" onClick={() => setIsRegister(!isRegister)}>
              {isRegister ? 'Already have an account? Login' : "Don't have an account? Register"}
            </button>
          </div>
          <div className="mt-4 pt-4 border-t text-center">
            <Link to="/shop" className="text-blue-600 hover:underline text-sm">🛒 Go to Customer Shop</Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Sidebar Component
const Sidebar = ({ isOpen, setIsOpen }) => {
  const { logout, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const menuItems = [
    { icon: Home, label: 'Dashboard', path: '/dashboard' },
    { icon: Package, label: 'Products', path: '/products' },
    { icon: Users, label: 'Suppliers', path: '/suppliers' },
    { icon: Truck, label: 'Purchases', path: '/purchases' },
    { icon: ShoppingCart, label: 'Sales', path: '/sales' },
    { icon: BarChart3, label: 'Reports', path: '/reports' },
    { icon: TrendingUp, label: 'Customer Analytics', path: '/customer-analytics' },
    { icon: Bell, label: 'Alerts', path: '/alerts' },
    { icon: FileText, label: 'Customer Orders', path: '/customer-orders' },
    { icon: CalendarIcon, label: 'Subscriptions', path: '/subscriptions-manage' },
  ];

  const handleLogout = () => { logout(); navigate('/login'); toast.success('Logged out successfully'); };

  return (
    <>
      {isOpen && <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setIsOpen(false)} />}
      <aside className={`fixed top-0 left-0 z-50 h-full w-64 bg-emerald-800 text-white transform transition-transform duration-300 lg:translate-x-0 ${isOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="p-4 border-b border-emerald-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center"><Package className="w-6 h-6 text-emerald-600" /></div>
              <span className="font-bold text-lg">Freshwala</span>
            </div>
            <button className="lg:hidden" onClick={() => setIsOpen(false)}><X className="w-6 h-6" /></button>
          </div>
        </div>
        <nav className="p-4 space-y-2">
          {menuItems.map((item) => (
            <button key={item.path} data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
              onClick={() => { navigate(item.path); setIsOpen(false); }}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${location.pathname === item.path ? 'bg-emerald-600 text-white' : 'hover:bg-emerald-700 text-emerald-100'}`}>
              <item.icon className="w-5 h-5" />{item.label}
            </button>
          ))}
        </nav>
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-emerald-700">
          <div className="flex items-center gap-3 mb-4 px-4">
            <div className="w-10 h-10 bg-emerald-600 rounded-full flex items-center justify-center">{user?.name?.charAt(0).toUpperCase()}</div>
            <div className="flex-1 min-w-0"><p className="font-medium truncate">{user?.name}</p><p className="text-xs text-emerald-300 truncate">{user?.email}</p></div>
          </div>
          <Button variant="outline" className="w-full border-emerald-600 text-white hover:bg-emerald-700" data-testid="logout-btn" onClick={handleLogout}>
            <LogOut className="w-4 h-4 mr-2" />Logout
          </Button>
        </div>
      </aside>
    </>
  );
};

// Layout Component
const Layout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  return (
    <div className="min-h-screen bg-gray-50">
      <Sidebar isOpen={sidebarOpen} setIsOpen={setSidebarOpen} />
      <div className="lg:ml-64">
        <header className="bg-white shadow-sm border-b px-4 py-3 flex items-center justify-between lg:hidden">
          <button onClick={() => setSidebarOpen(true)} data-testid="menu-btn"><Menu className="w-6 h-6 text-gray-600" /></button>
          <span className="font-bold text-emerald-800">Freshwala</span>
          <div className="w-6" />
        </header>
        <main className="p-4 lg:p-6">{children}</main>
      </div>
    </div>
  );
};

// Dashboard Page
const DashboardPage = () => {
  const [stats, setStats] = useState(null);
  const [recentSales, setRecentSales] = useState([]);
  const [lowStockProducts, setLowStockProducts] = useState([]);
  const [recentPayments, setRecentPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [todaySales, setTodaySales] = useState([]);
  const [allSales, setAllSales] = useState([]);
  
  // Holiday Notice State
  const [holidays, setHolidays] = useState([]);
  const [holidayDialog, setHolidayDialog] = useState({ open: false, editing: null });
  const [holidayForm, setHolidayForm] = useState({ title: '', message: '', date: '', is_active: true });
  
  // Announcements State (Promotions, Offers, etc.)
  const [announcements, setAnnouncements] = useState([]);
  const [announcementDialog, setAnnouncementDialog] = useState({ open: false, editing: null });
  const [announcementForm, setAnnouncementForm] = useState({ 
    title: '', 
    message: '', 
    announcement_type: 'promotion', 
    date: '', 
    end_date: '',
    is_active: true,
    media_url: '',
    media_type: '',
    cta_text: '',
    cta_link: ''
  });
  const [mediaUploading, setMediaUploading] = useState(false);
  const [mediaProgress, setMediaProgress] = useState(0);
  const [statusShareDialog, setStatusShareDialog] = useState({ open: false, ann: null });

  const uploadAnnouncementMedia = async (file) => {
    if (!file) return;
    if (file.size > 50 * 1024 * 1024) { toast.error('File too large (max 50MB)'); return; }
    if (!file.type.startsWith('image') && !file.type.startsWith('video')) { toast.error('Only images and videos are allowed'); return; }
    setMediaUploading(true); setMediaProgress(0);
    try {
      const uploadId = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      const chunkSize = 2 * 1024 * 1024;
      const totalChunks = Math.ceil(file.size / chunkSize) || 1;
      let result = null;
      for (let i = 0; i < totalChunks; i++) {
        const fd = new FormData();
        fd.append('upload_id', uploadId);
        fd.append('chunk_index', i);
        fd.append('total_chunks', totalChunks);
        fd.append('filename', file.name);
        fd.append('chunk', file.slice(i * chunkSize, (i + 1) * chunkSize));
        const res = await axios.post(`${API}/announcements/upload-chunk`, fd);
        result = res.data;
        setMediaProgress(Math.round(((i + 1) / totalChunks) * 100));
      }
      setAnnouncementForm(prev => ({ ...prev, media_url: result.media_url, media_type: result.media_type }));
      toast.success(`${result.media_type === 'video' ? 'Video' : 'Image'} uploaded!`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Upload failed'); }
    setMediaUploading(false);
  };

  const shareAnnouncementToStatus = (ann, phone) => {
    let msg = `*${ann.title}*\n${ann.message}`;
    if (ann.end_date) msg += `\n📅 Valid till: ${ann.end_date}`;
    if (ann.media_url) msg += `\n\n📎 Download media & post as Status:\n${BACKEND_URL}${ann.media_url}`;
    msg += `\n\n- ${CONFIG.BUSINESS_NAME}`;
    window.open(`https://wa.me/${phone}?text=${encodeURIComponent(msg)}`, '_blank');
  };

  useEffect(() => { fetchDashboardData(); fetchHolidays(); fetchAnnouncements(); }, []);

  const fetchAnnouncements = async () => {
    try {
      const res = await axios.get(`${API}/announcements`);
      setAnnouncements(res.data);
    } catch (e) { console.error('Failed to fetch announcements:', e); }
  };

  const saveAnnouncement = async () => {
    try {
      if (announcementDialog.editing) {
        await axios.put(`${API}/announcements/${announcementDialog.editing.id}`, announcementForm);
        toast.success('Announcement updated!');
        setStatusShareDialog({ open: true, ann: { ...announcementDialog.editing, ...announcementForm } });
      } else {
        const res = await axios.post(`${API}/announcements`, announcementForm);
        toast.success('Announcement added!');
        setStatusShareDialog({ open: true, ann: res.data });
      }
      setAnnouncementDialog({ open: false, editing: null });
      setAnnouncementForm({ title: '', message: '', announcement_type: 'promotion', date: '', end_date: '', is_active: true, media_url: '', media_type: '', cta_text: '', cta_link: '' });
      fetchAnnouncements();
    } catch (e) { toast.error('Failed to save announcement'); }
  };

  const deleteAnnouncement = async (id) => {
    if (!window.confirm('Delete this announcement?')) return;
    try {
      await axios.delete(`${API}/announcements/${id}`);
      toast.success('Announcement deleted!');
      fetchAnnouncements();
    } catch (e) { toast.error('Failed to delete announcement'); }
  };

  const fetchHolidays = async () => {
    try {
      const res = await axios.get(`${API}/holidays`);
      setHolidays(res.data);
    } catch (e) { console.error('Failed to fetch holidays:', e); }
  };

  const saveHoliday = async () => {
    try {
      if (holidayDialog.editing) {
        await axios.put(`${API}/holidays/${holidayDialog.editing.id}`, holidayForm);
        toast.success('Holiday updated!');
      } else {
        await axios.post(`${API}/holidays`, holidayForm);
        toast.success('Holiday added!');
      }
      setHolidayDialog({ open: false, editing: null });
      setHolidayForm({ title: '', message: '', date: '', is_active: true });
      fetchHolidays();
    } catch (e) { toast.error('Failed to save holiday'); }
  };

  const deleteHoliday = async (id) => {
    if (!window.confirm('Delete this holiday notice?')) return;
    try {
      await axios.delete(`${API}/holidays/${id}`);
      toast.success('Holiday deleted!');
      fetchHolidays();
    } catch (e) { toast.error('Failed to delete holiday'); }
  };

  const fetchDashboardData = async () => {
    try {
      const [statsRes, salesRes, lowStockRes, paymentsRes, allSalesRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats`),
        axios.get(`${API}/dashboard/recent-sales`),
        axios.get(`${API}/dashboard/low-stock-products`),
        axios.get(`${API}/recent-payments?limit=10`).catch(() => ({ data: [] })),
        axios.get(`${API}/sales`)
      ]);
      setStats(statsRes.data);
      setRecentSales(salesRes.data);
      setLowStockProducts(lowStockRes.data);
      setRecentPayments(paymentsRes.data);
      setAllSales(allSalesRes.data);
      
      // Filter today's sales
      const today = new Date().toISOString().split('T')[0];
      setTodaySales(allSalesRes.data.filter(s => s.sale_date === today));
    } catch (error) { toast.error('Failed to load dashboard data'); }
    finally { setLoading(false); }
  };

  // State for report date selection
  const [reportDate, setReportDate] = useState(new Date().toISOString().split('T')[0]);
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [selectedReportPhone, setSelectedReportPhone] = useState('');
  // Dashboard date filter
  const [dashboardDateFrom, setDashboardDateFrom] = useState('');
  const [dashboardDateTo, setDashboardDateTo] = useState('');

  // Format date as DD-MM-YYYY
  const formatDateDMY = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  // Filter recent sales by date range
  const filteredRecentSales = recentSales.filter(sale => {
    const saleDate = sale.sale_date || (sale.created_at ? sale.created_at.split('T')[0] : null);
    if (!saleDate) return true;
    if (dashboardDateFrom && saleDate < dashboardDateFrom) return false;
    if (dashboardDateTo && saleDate > dashboardDateTo) return false;
    return true;
  });

  // Send Daily Report via WhatsApp for any date
  const sendDailyReport = (target, selectedDate = null) => {
    const dateToUse = selectedDate || reportDate;
    const displayDate = new Date(dateToUse).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
    
    // Filter sales for the selected date
    const dateSales = allSales.filter(s => {
      const saleDate = s.sale_date || (s.created_at ? s.created_at.split('T')[0] : null);
      return saleDate === dateToUse;
    });
    
    // Calculate totals for the selected date
    const dateRevenue = dateSales.reduce((sum, s) => sum + (s.total_amount || 0), 0);
    const dateProfit = dateSales.reduce((sum, s) => sum + (s.total_profit || 0), 0);
    
    // Build product-wise summary
    const productTotals = {};
    dateSales.forEach(sale => {
      (sale.items || []).forEach(item => {
        const name = item.product_name || 'Other';
        if (!productTotals[name]) productTotals[name] = 0;
        productTotals[name] += item.total_price || 0;
      });
    });
    
    const productSummary = Object.entries(productTotals)
      .map(([name, amount]) => `• ${name}: ₹${amount.toLocaleString()}`)
      .join('\n');
    
    const message = `📊 *FRESHWALA DAILY SALES REPORT*
📅 Date: ${displayDate}

💰 *Day's Summary:*
• Total Sales: ${dateSales.length}
• Revenue: ₹${dateRevenue.toLocaleString()}
• Profit: ₹${dateProfit.toLocaleString()}

📦 *Product-wise Sales:*
${productSummary || '• No sales on this date'}

📈 *Overall Stats:*
• Total Revenue: ₹${stats?.total_revenue?.toLocaleString() || 0}
• Total Profit: ₹${stats?.total_profit?.toLocaleString() || 0}

_Sent from Freshwala Inventory System_`;

    const encodedMessage = encodeURIComponent(message);
    
    // For group sharing, just open WhatsApp with the message (user will select the group)
    // This opens WhatsApp and lets user select contact/group to share
    window.open(`https://wa.me/?text=${encodedMessage}`, '_blank');
    
    setReportDialogOpen(false);
    toast.success('WhatsApp opened! Select your Freshwala group to share the report.');
  };

  // Open report dialog
  const openReportDialog = (target) => {
    setSelectedReportPhone(target);
    setReportDialogOpen(true);
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div></div>;

  const statCards = [
    { title: 'Total Products', value: stats?.total_products || 0, icon: Package, color: 'bg-blue-500' },
    { title: 'Low Stock Items', value: stats?.low_stock_count || 0, icon: AlertTriangle, color: 'bg-red-500' },
    { title: 'Total Revenue', value: `₹${stats?.total_revenue?.toLocaleString() || 0}`, icon: DollarSign, color: 'bg-emerald-500' },
    { title: 'Total Profit', value: `₹${stats?.total_profit?.toLocaleString() || 0}`, icon: TrendingUp, color: 'bg-purple-500' },
    { title: 'Total Sales', value: stats?.total_sales || 0, icon: ShoppingCart, color: 'bg-orange-500' },
    { title: 'Total Suppliers', value: stats?.total_suppliers || 0, icon: Users, color: 'bg-teal-500' },
  ];

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <h1 className="text-2xl font-bold text-gray-800">Dashboard</h1>
        <div className="flex items-center gap-2 flex-wrap">
          <Badge variant="outline" className="text-emerald-600 border-emerald-600">Stock Value: ₹{stats?.stock_value?.toLocaleString() || 0}</Badge>
          
          {/* Date Filters and Send Report Button */}
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 bg-white border rounded-lg px-2 py-1">
              <span className="text-xs text-gray-500">From:</span>
              <Input 
                type="date" 
                value={dashboardDateFrom} 
                onChange={e => setDashboardDateFrom(e.target.value)}
                className="w-32 h-7 text-sm border-0 p-0"
                data-testid="dashboard-date-from"
              />
              <span className="text-xs text-gray-500">To:</span>
              <Input 
                type="date" 
                value={dashboardDateTo} 
                onChange={e => setDashboardDateTo(e.target.value)}
                className="w-32 h-7 text-sm border-0 p-0"
                data-testid="dashboard-date-to"
              />
              {(dashboardDateFrom || dashboardDateTo) && (
                <Button variant="ghost" size="sm" className="h-6 px-2" onClick={() => { setDashboardDateFrom(''); setDashboardDateTo(''); }}>
                  ✕
                </Button>
              )}
            </div>
            <Button 
              onClick={() => openReportDialog('group')} 
              className="bg-green-600 hover:bg-green-700 text-white"
              size="sm"
              data-testid="send-report-group-btn"
            >
              <MessageCircle className="w-4 h-4 mr-1" /> Share Report
            </Button>
          </div>
          
          {/* Report Date Selection Dialog */}
          <Dialog open={reportDialogOpen} onOpenChange={setReportDialogOpen}>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <MessageCircle className="w-5 h-5 text-green-600" />
                  Send Sales Report via WhatsApp
                </DialogTitle>
                <DialogDescription>
                  Select the date and share to your WhatsApp group
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div>
                  <Label>Select Date</Label>
                  <Input 
                    type="date" 
                    value={reportDate} 
                    onChange={e => setReportDate(e.target.value)}
                    max={new Date().toISOString().split('T')[0]}
                    className="mt-1"
                    data-testid="report-date-input"
                  />
                </div>
                <div className="bg-green-50 p-3 rounded-lg border border-green-200">
                  <p className="text-sm font-medium text-green-700 mb-2">📱 Share to Freshwala Group</p>
                  <p className="text-xs text-green-600">
                    Members: 9908878000, 9248444687, 7075769452
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    Report Date: {new Date(reportDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    className="flex-1"
                    onClick={() => setReportDate(new Date().toISOString().split('T')[0])}
                  >
                    Today
                  </Button>
                  <Button 
                    variant="outline" 
                    className="flex-1"
                    onClick={() => {
                      const yesterday = new Date();
                      yesterday.setDate(yesterday.getDate() - 1);
                      setReportDate(yesterday.toISOString().split('T')[0]);
                    }}
                  >
                    Yesterday
                  </Button>
                </div>
              </div>
              <DialogFooter className="flex-col sm:flex-row gap-2">
                <Button variant="outline" onClick={() => setReportDialogOpen(false)}>Cancel</Button>
                <Button 
                  className="bg-green-600 hover:bg-green-700" 
                  onClick={() => sendDailyReport('group', reportDate)}
                  data-testid="send-report-submit"
                >
                  <MessageCircle className="w-4 h-4 mr-2" />
                  Open WhatsApp
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        {statCards.map((stat) => (
          <Card key={stat.title} data-testid={`stat-card-${stat.title.toLowerCase().replace(/\s+/g, '-')}`}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div><p className="text-sm text-gray-500">{stat.title}</p><p className="text-2xl font-bold text-gray-800">{stat.value}</p></div>
                <div className={`w-12 h-12 ${stat.color} rounded-full flex items-center justify-center`}><stat.icon className="w-6 h-6 text-white" /></div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
      {lowStockProducts.length > 0 && (
        <Card className="border-red-200 bg-red-50" data-testid="low-stock-alert">
          <CardHeader className="pb-2"><CardTitle className="text-red-700 flex items-center gap-2"><AlertTriangle className="w-5 h-5" />Low Stock Alert</CardTitle></CardHeader>
          <CardContent><div className="flex flex-wrap gap-2">{lowStockProducts.map((product) => (<Badge key={product.id} variant="destructive" className="text-sm">{product.name}: {product.stock_quantity} {product.unit}</Badge>))}</div></CardContent>
        </Card>
      )}
      <Card data-testid="recent-sales-card">
        <CardHeader><CardTitle>Recent Sales {dashboardDateFrom || dashboardDateTo ? `(${formatDateDMY(dashboardDateFrom) || 'Start'} - ${formatDateDMY(dashboardDateTo) || 'End'})` : ''}</CardTitle></CardHeader>
        <CardContent>
          {filteredRecentSales.length === 0 ? <p className="text-gray-500 text-center py-4">No sales found for selected date range</p> : (
            <Table>
              <TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Customer</TableHead><TableHead>Items</TableHead><TableHead className="text-right">Amount</TableHead><TableHead className="text-right">Profit</TableHead></TableRow></TableHeader>
              <TableBody>
                {filteredRecentSales.map((sale) => (
                  <TableRow key={sale.id}>
                    <TableCell>{formatDateDMY(sale.sale_date || sale.created_at?.split('T')[0])}</TableCell>
                    <TableCell>{sale.customer_name || 'Walk-in Customer'}</TableCell>
                    <TableCell>{sale.items?.length || 0} items</TableCell>
                    <TableCell className="text-right">₹{sale.total_amount?.toLocaleString()}</TableCell>
                    <TableCell className="text-right text-emerald-600">₹{sale.total_profit?.toLocaleString()}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
      
      {/* Recent Milk Bill Payments */}
      <Card data-testid="recent-payments-card" className="border-emerald-200">
        <CardHeader className="pb-2">
          <CardTitle className="text-emerald-700 flex items-center gap-2">
            <DollarSign className="w-5 h-5" />
            Recent Milk Bill Payments
          </CardTitle>
        </CardHeader>
        <CardContent>
          {recentPayments.length === 0 ? (
            <p className="text-gray-500 text-center py-4">No payments recorded yet</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Payment Date</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead>Month</TableHead>
                  <TableHead className="text-right">Bill Amount</TableHead>
                  <TableHead className="text-right">Paid</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {recentPayments.map((payment) => (
                  <TableRow key={payment.id}>
                    <TableCell className="font-medium text-emerald-600">{payment.payment_date}</TableCell>
                    <TableCell>{payment.customer_name}</TableCell>
                    <TableCell>{payment.month}</TableCell>
                    <TableCell className="text-right">₹{payment.final_amount?.toLocaleString()}</TableCell>
                    <TableCell className="text-right font-medium text-emerald-600">₹{payment.amount_paid?.toLocaleString()}</TableCell>
                    <TableCell>
                      <Badge className={payment.payment_status === 'paid' ? 'bg-emerald-600' : 'bg-yellow-500'}>
                        {payment.payment_status === 'paid' ? '✓ Paid' : 'Partial'}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
      
      {/* Holiday Notice Management */}
      <Card data-testid="holiday-notice-card" className="border-orange-200">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-orange-700 flex items-center gap-2">
              <CalendarIcon className="w-5 h-5" />
              Holiday Notices
            </CardTitle>
            <Button 
              size="sm" 
              onClick={() => {
                setHolidayForm({ title: '', message: '', date: new Date().toISOString().split('T')[0], is_active: true });
                setHolidayDialog({ open: true, editing: null });
              }}
              className="bg-orange-500 hover:bg-orange-600"
            >
              <Plus className="w-4 h-4 mr-1" /> Add Holiday
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {holidays.length === 0 ? (
            <p className="text-gray-500 text-center py-4">No holiday notices. Add one to notify customers!</p>
          ) : (
            <div className="space-y-2">
              {holidays.map(holiday => (
                <div key={holiday.id} className={`flex items-center justify-between p-3 rounded-lg border ${holiday.is_active ? 'bg-orange-50 border-orange-200' : 'bg-gray-50 border-gray-200'}`}>
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">{holiday.is_active ? '🔔' : '🔕'}</span>
                    <div>
                      <p className="font-semibold">{holiday.title}</p>
                      <p className="text-sm text-gray-600">{holiday.message}</p>
                      <p className="text-xs text-gray-400">📅 {holiday.date}</p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="ghost" size="sm" onClick={() => {
                      setHolidayForm({ title: holiday.title, message: holiday.message, date: holiday.date, is_active: holiday.is_active });
                      setHolidayDialog({ open: true, editing: holiday });
                    }}>
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => deleteHoliday(holiday.id)}>
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Holiday Dialog */}
      <Dialog open={holidayDialog.open} onOpenChange={(open) => setHolidayDialog({ ...holidayDialog, open })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{holidayDialog.editing ? 'Edit Holiday Notice' : 'Add Holiday Notice'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">Title</label>
              <Input 
                placeholder="e.g., Shop Closed" 
                value={holidayForm.title} 
                onChange={e => setHolidayForm({...holidayForm, title: e.target.value})} 
              />
            </div>
            <div>
              <label className="text-sm font-medium">Message</label>
              <Input 
                placeholder="e.g., No delivery on this day due to festival" 
                value={holidayForm.message} 
                onChange={e => setHolidayForm({...holidayForm, message: e.target.value})} 
              />
            </div>
            <div>
              <label className="text-sm font-medium">Date</label>
              <Input 
                type="date" 
                value={holidayForm.date} 
                onChange={e => setHolidayForm({...holidayForm, date: e.target.value})} 
              />
            </div>
            <div className="flex items-center gap-2">
              <input 
                type="checkbox" 
                id="holiday-active"
                checked={holidayForm.is_active} 
                onChange={e => setHolidayForm({...holidayForm, is_active: e.target.checked})} 
                className="w-4 h-4"
              />
              <label htmlFor="holiday-active" className="text-sm">Active (visible to customers)</label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setHolidayDialog({ open: false, editing: null })}>Cancel</Button>
            <Button onClick={saveHoliday} className="bg-orange-500 hover:bg-orange-600">
              {holidayDialog.editing ? 'Update' : 'Add'} Holiday
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Promotions & Announcements Section */}
      <Card data-testid="announcements-card" className="border-purple-200">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-purple-700 flex items-center gap-2">
              <Bell className="w-5 h-5" />
              Promotions & Announcements
            </CardTitle>
            <Button 
              size="sm" 
              onClick={() => {
                setAnnouncementForm({ title: '', message: '', announcement_type: 'promotion', date: new Date().toISOString().split('T')[0], end_date: '', is_active: true, media_url: '', media_type: '', cta_text: '', cta_link: '' });
                setAnnouncementDialog({ open: true, editing: null });
              }}
              className="bg-purple-500 hover:bg-purple-600"
            >
              <Plus className="w-4 h-4 mr-1" /> Add Promotion
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {announcements.length === 0 ? (
            <p className="text-gray-500 text-center py-4">No promotions or announcements. Add one to attract customers!</p>
          ) : (
            <div className="space-y-2">
              {announcements.map(ann => (
                <div key={ann.id} className={`flex items-center justify-between p-3 rounded-lg border ${ann.is_active ? 'bg-purple-50 border-purple-200' : 'bg-gray-50 border-gray-200'}`}>
                  <div className="flex items-center gap-3">
                    {ann.media_url ? (
                      ann.media_type === 'video' ? (
                        <video src={`${BACKEND_URL}${ann.media_url}`} className="w-14 h-14 rounded-lg object-cover border" muted data-testid={`announcement-media-thumb-${ann.id}`} />
                      ) : (
                        <img src={`${BACKEND_URL}${ann.media_url}`} alt={ann.title} className="w-14 h-14 rounded-lg object-cover border" data-testid={`announcement-media-thumb-${ann.id}`} />
                      )
                    ) : (
                      <span className="text-2xl">
                        {ann.announcement_type === 'promotion' ? '🎉' : ann.announcement_type === 'offer' ? '💰' : '📢'}
                      </span>
                    )}
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-semibold">{ann.title}</p>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                          ann.announcement_type === 'promotion' ? 'bg-purple-100 text-purple-700' :
                          ann.announcement_type === 'offer' ? 'bg-green-100 text-green-700' :
                          'bg-blue-100 text-blue-700'
                        }`}>
                          {ann.announcement_type}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600">{ann.message}</p>
                      <p className="text-xs text-gray-400">
                        📅 {ann.date} {ann.end_date ? `→ ${ann.end_date}` : ''} <span className="ml-2 text-purple-500 font-medium" data-testid={`announcement-views-${ann.id}`}>👁 {ann.views || 0} views</span>
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="ghost" size="sm" data-testid={`share-status-btn-${ann.id}`} title="Send to WhatsApp Status" onClick={() => setStatusShareDialog({ open: true, ann })}>
                      <Share2 className="w-4 h-4 text-green-600" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => {
                      setAnnouncementForm({ 
                        title: ann.title, 
                        message: ann.message, 
                        announcement_type: ann.announcement_type,
                        date: ann.date, 
                        end_date: ann.end_date || '',
                        is_active: ann.is_active,
                        media_url: ann.media_url || '',
                        media_type: ann.media_type || '',
                        cta_text: ann.cta_text || '',
                        cta_link: ann.cta_link || ''
                      });
                      setAnnouncementDialog({ open: true, editing: ann });
                    }}>
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => deleteAnnouncement(ann.id)}>
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Announcement Dialog */}
      <Dialog open={announcementDialog.open} onOpenChange={(open) => setAnnouncementDialog({ ...announcementDialog, open })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{announcementDialog.editing ? 'Edit Announcement' : 'Add Promotion/Announcement'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">Type</label>
              <Select value={announcementForm.announcement_type} onValueChange={v => setAnnouncementForm({...announcementForm, announcement_type: v})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="promotion">🎉 Promotion</SelectItem>
                  <SelectItem value="offer">💰 Special Offer</SelectItem>
                  <SelectItem value="announcement">📢 General Announcement</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium">Title</label>
              <Input 
                placeholder="e.g., Diwali Sale - 20% Off!" 
                value={announcementForm.title} 
                onChange={e => setAnnouncementForm({...announcementForm, title: e.target.value})} 
              />
            </div>
            <div>
              <label className="text-sm font-medium">Message</label>
              <Input 
                placeholder="e.g., Get 20% off on all dairy products this Diwali!" 
                value={announcementForm.message} 
                onChange={e => setAnnouncementForm({...announcementForm, message: e.target.value})} 
              />
            </div>
            <div>
              <label className="text-sm font-medium">Image / Video (Optional)</label>
              <div className="mt-1 space-y-2">
                {announcementForm.media_url ? (
                  <div className="relative border rounded-lg p-2 bg-gray-50">
                    {announcementForm.media_type === 'video' ? (
                      <video src={`${BACKEND_URL}${announcementForm.media_url}`} controls className="w-full max-h-48 rounded-lg" data-testid="announcement-media-preview" />
                    ) : (
                      <img src={`${BACKEND_URL}${announcementForm.media_url}`} alt="Preview" className="w-full max-h-48 object-contain rounded-lg" data-testid="announcement-media-preview" />
                    )}
                    <Button 
                      variant="destructive" size="sm" 
                      className="absolute top-3 right-3"
                      data-testid="remove-media-btn"
                      onClick={() => setAnnouncementForm({...announcementForm, media_url: '', media_type: ''})}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ) : (
                  <label className="flex flex-col items-center justify-center border-2 border-dashed border-purple-300 rounded-lg p-4 cursor-pointer hover:bg-purple-50 transition-colors">
                    <Upload className="w-6 h-6 text-purple-400 mb-1" />
                    <span className="text-sm text-purple-600 font-medium">{mediaUploading ? `Uploading... ${mediaProgress}%` : 'Click to upload image or video'}</span>
                    <span className="text-xs text-gray-400">JPG, PNG, WEBP, GIF, MP4, WEBM (max 50MB)</span>
                    <input 
                      type="file" 
                      accept="image/*,video/*" 
                      className="hidden" 
                      disabled={mediaUploading}
                      data-testid="announcement-media-input"
                      onChange={e => { uploadAnnouncementMedia(e.target.files[0]); e.target.value = ''; }}
                    />
                  </label>
                )}
                {mediaUploading && (
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div className="bg-purple-500 h-2 rounded-full transition-all" style={{ width: `${mediaProgress}%` }} />
                  </div>
                )}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">Button Text (Optional)</label>
                <Input 
                  placeholder="e.g., Order Now" 
                  value={announcementForm.cta_text} 
                  onChange={e => setAnnouncementForm({...announcementForm, cta_text: e.target.value})} 
                  data-testid="announcement-cta-text-input"
                />
              </div>
              <div>
                <label className="text-sm font-medium">Button Link</label>
                <Input 
                  placeholder="e.g., /shop/products" 
                  value={announcementForm.cta_link} 
                  onChange={e => setAnnouncementForm({...announcementForm, cta_link: e.target.value})} 
                  data-testid="announcement-cta-link-input"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">Start Date</label>
                <Input 
                  type="date" 
                  value={announcementForm.date} 
                  onChange={e => setAnnouncementForm({...announcementForm, date: e.target.value})} 
                />
              </div>
              <div>
                <label className="text-sm font-medium">End Date (Optional)</label>
                <Input 
                  type="date" 
                  value={announcementForm.end_date} 
                  onChange={e => setAnnouncementForm({...announcementForm, end_date: e.target.value})} 
                />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <input 
                type="checkbox" 
                id="announcement-active"
                checked={announcementForm.is_active} 
                onChange={e => setAnnouncementForm({...announcementForm, is_active: e.target.checked})} 
                className="w-4 h-4"
              />
              <label htmlFor="announcement-active" className="text-sm">Active (visible to customers)</label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAnnouncementDialog({ open: false, editing: null })}>Cancel</Button>
            <Button onClick={saveAnnouncement} className="bg-purple-500 hover:bg-purple-600">
              {announcementDialog.editing ? 'Update' : 'Add'} Announcement
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* WhatsApp Status Share Dialog */}
      <Dialog open={statusShareDialog.open} onOpenChange={(open) => setStatusShareDialog({ ...statusShareDialog, open })}>
        <DialogContent data-testid="status-share-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-green-600">
              <Share2 className="w-5 h-5" /> Post to WhatsApp Status
            </DialogTitle>
          </DialogHeader>
          {statusShareDialog.ann && (
            <div className="space-y-4">
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                <p className="font-bold text-purple-700">{statusShareDialog.ann.title}</p>
                <p className="text-sm text-gray-600">{statusShareDialog.ann.message}</p>
                {statusShareDialog.ann.media_url && (
                  statusShareDialog.ann.media_type === 'video' ? (
                    <video src={`${BACKEND_URL}${statusShareDialog.ann.media_url}`} controls className="w-full max-h-40 rounded-lg mt-2" />
                  ) : (
                    <img src={`${BACKEND_URL}${statusShareDialog.ann.media_url}`} alt="" className="w-full max-h-40 object-contain rounded-lg mt-2" />
                  )
                )}
              </div>
              <p className="text-sm text-gray-500">
                Tap a button below to send this announcement to the phone via WhatsApp. On that phone, open the message, download the media, and tap <strong>Forward → My Status</strong> to post it.
              </p>
              <div className="grid grid-cols-1 gap-2">
                <Button 
                  data-testid="send-status-8978152777"
                  className="bg-green-500 hover:bg-green-600 w-full"
                  onClick={() => shareAnnouncementToStatus(statusShareDialog.ann, '918978152777')}
                >
                  <MessageCircle className="w-4 h-4 mr-2" /> Send to 8978152777
                </Button>
                <Button 
                  data-testid="send-status-7075769452"
                  className="bg-green-500 hover:bg-green-600 w-full"
                  onClick={() => shareAnnouncementToStatus(statusShareDialog.ann, '917075769452')}
                >
                  <MessageCircle className="w-4 h-4 mr-2" /> Send to 7075769452
                </Button>
              </div>
              {statusShareDialog.ann.media_url && (
                <a 
                  href={`${BACKEND_URL}${statusShareDialog.ann.media_url}`} 
                  download target="_blank" rel="noreferrer"
                  className="flex items-center justify-center gap-2 text-sm text-purple-600 hover:underline"
                  data-testid="download-media-link"
                >
                  <Download className="w-4 h-4" /> Download media file
                </a>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setStatusShareDialog({ open: false, ann: null })}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Products Page (Admin)
const ProductsPage = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [formData, setFormData] = useState({ name: '', category: 'Milk', unit: 'liters', price: '', cost_price: '', stock_quantity: '0', low_stock_threshold: '10', description: '', image_url: '' });

  const categories = ['Milk', 'Curd', 'Ghee', 'Paneer', 'Pickles', 'Batters', 'Others'];
  const units = ['liters', 'kg', 'pieces', 'packets', 'bottles'];

  useEffect(() => { fetchProducts(); }, []);

  const fetchProducts = async () => {
    try { const response = await axios.get(`${API}/products`); setProducts(response.data); }
    catch (error) { toast.error('Failed to load products'); }
    finally { setLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = { ...formData, price: parseFloat(formData.price), cost_price: parseFloat(formData.cost_price), stock_quantity: parseFloat(formData.stock_quantity), low_stock_threshold: parseFloat(formData.low_stock_threshold) };
      if (editingProduct) { await axios.put(`${API}/products/${editingProduct.id}`, data); toast.success('Product updated successfully'); }
      else { await axios.post(`${API}/products`, data); toast.success('Product added successfully'); }
      setDialogOpen(false); resetForm(); fetchProducts();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to save product'); }
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setFormData({ name: product.name, category: product.category, unit: product.unit, price: product.price.toString(), cost_price: product.cost_price.toString(), stock_quantity: product.stock_quantity.toString(), low_stock_threshold: product.low_stock_threshold.toString(), description: product.description || '', image_url: product.image_url || '' });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this product?')) {
      try { await axios.delete(`${API}/products/${id}`); toast.success('Product deleted successfully'); fetchProducts(); }
      catch (error) { toast.error('Failed to delete product'); }
    }
  };

  const resetForm = () => { setEditingProduct(null); setFormData({ name: '', category: 'Milk', unit: 'liters', price: '', cost_price: '', stock_quantity: '0', low_stock_threshold: '10', description: '', image_url: '' }); };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div></div>;

  return (
    <div className="space-y-6" data-testid="products-page">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-800">Products</h1>
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild><Button className="bg-emerald-600 hover:bg-emerald-700" data-testid="add-product-btn"><Plus className="w-4 h-4 mr-2" /> Add Product</Button></DialogTrigger>
          <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>{editingProduct ? 'Edit Product' : 'Add New Product'}</DialogTitle></DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2"><Label>Product Name</Label><Input value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} required data-testid="product-name-input" /></div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2"><Label>Category</Label><Select value={formData.category} onValueChange={(v) => setFormData({...formData, category: v})}><SelectTrigger data-testid="product-category-select"><SelectValue /></SelectTrigger><SelectContent>{categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select></div>
                <div className="space-y-2"><Label>Unit</Label><Select value={formData.unit} onValueChange={(v) => setFormData({...formData, unit: v})}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{units.map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent></Select></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2"><Label>Selling Price (₹)</Label><Input type="number" step="0.01" value={formData.price} onChange={(e) => setFormData({...formData, price: e.target.value})} required data-testid="product-price-input" /></div>
                <div className="space-y-2"><Label>Cost Price (₹)</Label><Input type="number" step="0.01" value={formData.cost_price} onChange={(e) => setFormData({...formData, cost_price: e.target.value})} required data-testid="product-cost-input" /></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2"><Label>Stock Quantity</Label><Input type="number" step="0.01" value={formData.stock_quantity} onChange={(e) => setFormData({...formData, stock_quantity: e.target.value})} data-testid="product-stock-input" /></div>
                <div className="space-y-2"><Label>Low Stock Threshold</Label><Input type="number" step="0.01" value={formData.low_stock_threshold} onChange={(e) => setFormData({...formData, low_stock_threshold: e.target.value})} /></div>
              </div>
              <div className="space-y-2"><Label>Image URL (optional)</Label><Input value={formData.image_url} onChange={(e) => setFormData({...formData, image_url: e.target.value})} placeholder="https://..." /></div>
              <div className="space-y-2"><Label>Description</Label><Textarea value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} /></div>
              <DialogFooter><Button type="submit" className="bg-emerald-600 hover:bg-emerald-700" data-testid="save-product-btn">{editingProduct ? 'Update' : 'Add'} Product</Button></DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Category</TableHead><TableHead>Price</TableHead><TableHead>Cost</TableHead><TableHead>Stock</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
            <TableBody>
              {products.length === 0 ? <TableRow><TableCell colSpan={7} className="text-center py-8 text-gray-500">No products found. Add your first product!</TableCell></TableRow> : (
                products.map((product) => (
                  <TableRow key={product.id} data-testid={`product-row-${product.id}`}>
                    <TableCell className="font-medium">{product.name}</TableCell>
                    <TableCell><Badge variant="outline">{product.category}</Badge></TableCell>
                    <TableCell>₹{product.price}</TableCell>
                    <TableCell>₹{product.cost_price}</TableCell>
                    <TableCell>{product.stock_quantity} {product.unit}</TableCell>
                    <TableCell>{product.stock_quantity <= product.low_stock_threshold ? <Badge variant="destructive">Low Stock</Badge> : <Badge className="bg-emerald-500">In Stock</Badge>}</TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="sm" title="Copy Buy Now link" onClick={() => {
                        const buyLink = `${window.location.origin}/buy/${product.name.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')}`;
                        navigator.clipboard.writeText(buyLink).then(() => toast.success(`Buy link copied! ${buyLink}`)).catch(() => toast.error('Copy failed'));
                      }} data-testid={`copy-buy-link-${product.id}`}><Share2 className="w-4 h-4 text-green-600" /></Button>
                      <Button variant="ghost" size="sm" onClick={() => handleEdit(product)} data-testid={`edit-product-${product.id}`}><Edit className="w-4 h-4" /></Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(product.id)} className="text-red-600" data-testid={`delete-product-${product.id}`}><Trash2 className="w-4 h-4" /></Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

// Placeholder pages for Admin (simplified versions)
const SuppliersPage = () => {
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({ name: '', contact_person: '', phone: '', email: '', address: '', products_supplied: [] });

  useEffect(() => { fetchSuppliers(); }, []);
  const fetchSuppliers = async () => { try { const res = await axios.get(`${API}/suppliers`); setSuppliers(res.data); } catch(e) { toast.error('Failed'); } finally { setLoading(false); } };
  const handleSubmit = async (e) => { e.preventDefault(); try { await axios.post(`${API}/suppliers`, formData); toast.success('Added!'); setDialogOpen(false); fetchSuppliers(); } catch(e) { toast.error('Failed'); } };
  const handleDelete = async (id) => { if (window.confirm('Delete?')) { try { await axios.delete(`${API}/suppliers/${id}`); toast.success('Deleted'); fetchSuppliers(); } catch(e) { toast.error('Failed'); } } };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div></div>;

  return (
    <div className="space-y-6" data-testid="suppliers-page">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-800">Suppliers</h1>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild><Button className="bg-emerald-600 hover:bg-emerald-700"><Plus className="w-4 h-4 mr-2" /> Add Supplier</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Add Supplier</DialogTitle></DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <Input placeholder="Company Name" value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} required />
              <Input placeholder="Contact Person" value={formData.contact_person} onChange={e => setFormData({...formData, contact_person: e.target.value})} required />
              <Input placeholder="Phone" value={formData.phone} onChange={e => setFormData({...formData, phone: e.target.value})} required />
              <Input placeholder="Email" type="email" value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} />
              <Textarea placeholder="Address" value={formData.address} onChange={e => setFormData({...formData, address: e.target.value})} required />
              <Button type="submit" className="w-full bg-emerald-600">Add Supplier</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {suppliers.map(s => (
          <Card key={s.id}>
            <CardHeader className="pb-2"><CardTitle className="text-lg">{s.name}</CardTitle><CardDescription>{s.contact_person}</CardDescription></CardHeader>
            <CardContent><p className="text-sm text-gray-600">📞 {s.phone}</p><p className="text-sm text-gray-600">📍 {s.address}</p></CardContent>
            <CardFooter><Button variant="ghost" size="sm" className="text-red-600" onClick={() => handleDelete(s.id)}><Trash2 className="w-4 h-4" /></Button></CardFooter>
          </Card>
        ))}
      </div>
    </div>
  );
};

const PurchasesPage = () => {
  const [purchases, setPurchases] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedPurchase, setSelectedPurchase] = useState(null);
  const [formData, setFormData] = useState({ 
    supplier_id: '', 
    supplier_name: '', 
    items: [], 
    notes: '', 
    purchase_date_from: new Date().toISOString().split('T')[0],
    purchase_date_to: new Date().toISOString().split('T')[0]
  });
  const [currentItem, setCurrentItem] = useState({ product_id: '', product_name: '', quantity: '', unit_cost: '' });
  const [editingItemIndex, setEditingItemIndex] = useState(null);
  // Default to current month
  const currentMonthStart = new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0];
  const today = new Date().toISOString().split('T')[0];
  const [dateFrom, setDateFrom] = useState(currentMonthStart);
  const [dateTo, setDateTo] = useState(today);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  // Edit purchase state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingPurchase, setEditingPurchase] = useState(null);
  const [editFormData, setEditFormData] = useState({ supplier_id: '', supplier_name: '', items: [], notes: '', purchase_date: '' });
  // Edit summary cell state
  const [editSummaryDialogOpen, setEditSummaryDialogOpen] = useState(false);
  const [editSummaryData, setEditSummaryData] = useState({ date: '', supplier: '', amount: '', liters: '', purchaseId: null });

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [purchasesRes, suppliersRes, productsRes] = await Promise.all([
        axios.get(`${API}/purchases`),
        axios.get(`${API}/suppliers`),
        axios.get(`${API}/products`)
      ]);
      setPurchases(purchasesRes.data);
      setSuppliers(suppliersRes.data);
      setProducts(productsRes.data);
    } catch (error) { toast.error('Failed to load data'); }
    finally { setLoading(false); }
  };

  const handleExcelImport = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      toast.error('Please select an Excel file (.xlsx or .xls)');
      return;
    }
    
    setImporting(true);
    setImportResult(null);
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await axios.post(`${API}/import-excel`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setImportResult(response.data);
      toast.success(response.data.message);
      fetchData(); // Refresh the list
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to import Excel file';
      toast.error(errorMsg);
      setImportResult({ success: false, message: errorMsg });
    } finally {
      setImporting(false);
      event.target.value = ''; // Reset file input
    }
  };

  const deletePurchase = async (purchaseId) => {
    if (!window.confirm('⚠️ Delete this purchase?\n\nThis will reverse the stock quantity that was added.')) return;
    try {
      await axios.delete(`${API}/purchases/${purchaseId}`);
      toast.success('Purchase deleted. Stock reversed.');
      fetchData();
    } catch (error) { toast.error('Failed to delete purchase'); }
  };

  // Edit purchase functions
  const openEditDialog = (purchase) => {
    setEditingPurchase(purchase);
    setEditFormData({
      supplier_id: purchase.supplier_id,
      supplier_name: purchase.supplier_name,
      items: purchase.items || [],
      notes: purchase.notes || '',
      purchase_date: purchase.purchase_date || ''
    });
    setEditDialogOpen(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/purchases/${editingPurchase.id}`, editFormData);
      toast.success('Purchase updated successfully!');
      setEditDialogOpen(false);
      setEditingPurchase(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to update purchase');
    }
  };

  const updateEditItem = (index, field, value) => {
    const newItems = [...editFormData.items];
    newItems[index] = { ...newItems[index], [field]: value };
    if (field === 'quantity' || field === 'unit_cost') {
      newItems[index].total_cost = (parseFloat(newItems[index].quantity) || 0) * (parseFloat(newItems[index].unit_cost) || 0);
    }
    setEditFormData({ ...editFormData, items: newItems });
  };

  const removeEditItem = (index) => {
    const newItems = editFormData.items.filter((_, i) => i !== index);
    setEditFormData({ ...editFormData, items: newItems });
  };

  // Open edit summary dialog for a specific date and supplier
  const openSummaryEdit = (date, supplierName, currentAmount, currentLiters) => {
    // Find the purchase for this date and supplier
    const purchase = filteredPurchases.find(p => p.purchase_date === date && p.supplier_name === supplierName);
    setEditSummaryData({
      date,
      supplier: supplierName,
      amount: currentAmount?.toString() || '0',
      liters: currentLiters?.toString() || '0',
      purchaseId: purchase?.id || null,
      supplierId: purchase?.supplier_id || null
    });
    setEditSummaryDialogOpen(true);
  };

  // Save edited summary cell
  const saveSummaryEdit = async () => {
    const amount = parseFloat(editSummaryData.amount) || 0;
    const liters = parseFloat(editSummaryData.liters) || 0;
    
    if (editSummaryData.purchaseId) {
      // Update existing purchase
      try {
        // Get the existing purchase to update items
        const existingPurchase = purchases.find(p => p.id === editSummaryData.purchaseId);
        if (existingPurchase) {
          // Calculate unit cost from amount and liters
          const unitCost = liters > 0 ? amount / liters : 0;
          
          // Update the purchase with new values
          const updatedItems = existingPurchase.items?.length > 0 
            ? existingPurchase.items.map(item => ({
                ...item,
                quantity: liters,
                unit_cost: unitCost,
                total_cost: amount
              }))
            : [{
                product_id: 'milk',
                product_name: 'Buffalo Milk',
                quantity: liters,
                unit_cost: unitCost,
                total_cost: amount
              }];
          
          await axios.put(`${API}/purchases/${editSummaryData.purchaseId}`, {
            ...existingPurchase,
            items: updatedItems,
            total_amount: amount,
            total_liters: liters
          });
          toast.success('Purchase updated!');
        }
      } catch (error) {
        toast.error('Failed to update purchase');
        return;
      }
    } else if (amount > 0 || liters > 0) {
      // Create new purchase for this date and supplier
      try {
        const supplier = suppliers.find(s => s.name === editSummaryData.supplier);
        const unitCost = liters > 0 ? amount / liters : 0;
        
        await axios.post(`${API}/purchases`, {
          supplier_id: supplier?.id || '',
          supplier_name: editSummaryData.supplier,
          items: [{
            product_id: 'milk',
            product_name: 'Buffalo Milk',
            quantity: liters,
            unit_cost: unitCost,
            total_cost: amount
          }],
          notes: 'Created from summary edit',
          purchase_date: editSummaryData.date
        });
        toast.success('Purchase created!');
      } catch (error) {
        toast.error('Failed to create purchase');
        return;
      }
    }
    
    setEditSummaryDialogOpen(false);
    fetchData();
  };

  // Filter purchases by date range
  const filteredPurchases = purchases.filter(p => {
    const purchaseDate = p.purchase_date || p.created_at?.split('T')[0];
    if (dateFrom && purchaseDate < dateFrom) return false;
    if (dateTo && purchaseDate > dateTo) return false;
    return true;
  });

  const handleSupplierChange = (supplierId) => {
    const supplier = suppliers.find(s => s.id === supplierId);
    setFormData({ ...formData, supplier_id: supplierId, supplier_name: supplier?.name || '' });
  };

  const handleProductSelect = (productId) => {
    const product = products.find(p => p.id === productId);
    if (product) {
      setCurrentItem({ ...currentItem, product_id: productId, product_name: product.name, unit_cost: product.cost_price.toString() });
    }
  };

  const addItemToList = () => {
    if (!currentItem.product_id || !currentItem.quantity || !currentItem.unit_cost) {
      toast.error('Please fill all item fields'); return;
    }
    const newItem = {
      product_id: currentItem.product_id,
      product_name: currentItem.product_name,
      quantity: parseFloat(currentItem.quantity),
      unit_cost: parseFloat(currentItem.unit_cost),
      total_cost: parseFloat(currentItem.quantity) * parseFloat(currentItem.unit_cost)
    };
    
    if (editingItemIndex !== null) {
      // Update existing item
      const updatedItems = [...formData.items];
      updatedItems[editingItemIndex] = newItem;
      setFormData({ ...formData, items: updatedItems });
      setEditingItemIndex(null);
    } else {
      // Add new item
      setFormData({ ...formData, items: [...formData.items, newItem] });
    }
    setCurrentItem({ product_id: '', product_name: '', quantity: '', unit_cost: '' });
  };

  const editItem = (index) => {
    const item = formData.items[index];
    setCurrentItem({
      product_id: item.product_id,
      product_name: item.product_name,
      quantity: item.quantity.toString(),
      unit_cost: item.unit_cost.toString()
    });
    setEditingItemIndex(index);
  };

  const removeItem = (index) => {
    setFormData({ ...formData, items: formData.items.filter((_, i) => i !== index) });
    if (editingItemIndex === index) {
      setEditingItemIndex(null);
      setCurrentItem({ product_id: '', product_name: '', quantity: '', unit_cost: '' });
    }
  };

  const getTotalAmount = () => formData.items.reduce((sum, item) => sum + item.total_cost, 0);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.supplier_id) { toast.error('Please select a supplier'); return; }
    if (formData.items.length === 0) { toast.error('Please add at least one item'); return; }
    try {
      await axios.post(`${API}/purchases`, {
        supplier_id: formData.supplier_id,
        supplier_name: formData.supplier_name,
        items: formData.items,
        notes: formData.notes,
        purchase_date: formData.purchase_date_from,
        purchase_date_to: formData.purchase_date_to
      });
      toast.success('Purchase recorded successfully! Stock updated.');
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to record purchase'); }
  };

  const resetForm = () => {
    setFormData({ 
      supplier_id: '', 
      supplier_name: '', 
      items: [], 
      notes: '', 
      purchase_date_from: new Date().toISOString().split('T')[0],
      purchase_date_to: new Date().toISOString().split('T')[0]
    });
    setCurrentItem({ product_id: '', product_name: '', quantity: '', unit_cost: '' });
    setEditingItemIndex(null);
  };

  const viewPurchase = (purchase) => { setSelectedPurchase(purchase); setViewDialogOpen(true); };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div></div>;

  return (
    <div className="space-y-6" data-testid="purchases-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Purchases</h1>
          <p className="text-gray-500">Record and track stock purchases from suppliers</p>
        </div>
        <div className="flex gap-2">
          {/* Excel Import Dialog */}
          <Dialog open={importDialogOpen} onOpenChange={(open) => { setImportDialogOpen(open); if (!open) { setImportResult(null); } }}>
            <DialogTrigger asChild>
              <Button variant="outline" className="border-blue-500 text-blue-600 hover:bg-blue-50" data-testid="import-excel-btn">
                <Upload className="w-4 h-4 mr-2" /> Import Excel
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>Import Purchases from Excel</DialogTitle>
                <DialogDescription>
                  Upload Excel file with supplier-wise purchase data.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                {/* Expected Format Info */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm">
                  <p className="font-medium text-blue-800 mb-1">Expected Excel Format:</p>
                  <code className="text-xs bg-white px-2 py-1 rounded block">
                    Date | Supplier1 | liters | Supplier2 | liters | ...
                  </code>
                  <p className="text-blue-600 text-xs mt-2">
                    Suppliers will be auto-detected from column headers.
                  </p>
                </div>
                
                {/* File Upload */}
                <div className="border-2 border-dashed border-blue-400 hover:border-blue-500 bg-blue-50 rounded-lg p-6 text-center transition-colors">
                  <Upload className="w-10 h-10 mx-auto text-blue-500 mb-2" />
                  <Label htmlFor="excel-file" className="cursor-pointer">
                    <span className="text-blue-600 hover:text-blue-700 font-medium">Click to select Excel file</span>
                  </Label>
                  <p className="text-xs text-gray-400 mt-1">Supports .xlsx and .xls files</p>
                  <Input 
                    id="excel-file"
                    type="file" 
                    accept=".xlsx,.xls"
                    onChange={handleExcelImport}
                    className="hidden"
                    disabled={importing}
                  />
                </div>
                
                {importing && (
                  <div className="flex items-center justify-center gap-2 text-blue-600">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                    <span>Processing Excel file...</span>
                  </div>
                )}
                
                {importResult && (
                  <div className={`p-4 rounded-lg ${importResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                    <p className={`font-medium ${importResult.success ? 'text-green-700' : 'text-red-700'}`}>
                      {importResult.message}
                    </p>
                    {importResult.details && (
                      <div className="mt-2 text-sm">
                        {importResult.details.suppliers_found?.length > 0 && (
                          <p className="text-gray-600">Suppliers found: <strong>{importResult.details.suppliers_found.join(', ')}</strong></p>
                        )}
                        <p className="text-gray-600">Purchases imported: <strong>{importResult.details.purchases_imported}</strong></p>
                        {importResult.details.errors?.length > 0 && (
                          <details className="mt-2">
                            <summary className="cursor-pointer text-orange-600">View {importResult.details.errors.length} warnings</summary>
                            <ul className="mt-1 text-xs text-gray-500 max-h-32 overflow-y-auto">
                              {importResult.details.errors.slice(0, 10).map((err, i) => (
                                <li key={i}>• {err}</li>
                              ))}
                            </ul>
                          </details>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => { setImportDialogOpen(false); setImportResult(null); }}>
                  Close
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="bg-emerald-600 hover:bg-emerald-700" data-testid="add-purchase-btn">
                <Plus className="w-4 h-4 mr-2" /> Add Purchase
              </Button>
            </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Record New Purchase</DialogTitle>
              <DialogDescription>Add stock purchase from supplier. This will automatically update inventory.</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Supplier *</Label>
                  <Select value={formData.supplier_id} onValueChange={handleSupplierChange}>
                    <SelectTrigger><SelectValue placeholder="Select supplier" /></SelectTrigger>
                    <SelectContent>
                      {suppliers.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4 p-3 bg-blue-50 rounded-lg">
                <div>
                  <Label className="text-blue-700">📅 From Date *</Label>
                  <Input 
                    type="date" 
                    value={formData.purchase_date_from} 
                    onChange={e => setFormData({...formData, purchase_date_from: e.target.value})}
                  />
                </div>
                <div>
                  <Label className="text-blue-700">📅 To Date *</Label>
                  <Input 
                    type="date" 
                    value={formData.purchase_date_to} 
                    onChange={e => setFormData({...formData, purchase_date_to: e.target.value})}
                  />
                </div>
                {formData.purchase_date_from && formData.purchase_date_to && formData.purchase_date_from !== formData.purchase_date_to && (
                  <p className="col-span-2 text-sm text-blue-600">
                    📆 Recording purchase for {Math.ceil((new Date(formData.purchase_date_to) - new Date(formData.purchase_date_from)) / (1000 * 60 * 60 * 24)) + 1} days
                  </p>
                )}
              </div>

              <Card className="bg-gray-50">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">
                    {editingItemIndex !== null ? '✏️ Edit Item' : 'Add Items'}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="grid grid-cols-4 gap-2">
                    <div className="col-span-2">
                      <Select value={currentItem.product_id} onValueChange={handleProductSelect}>
                        <SelectTrigger><SelectValue placeholder="Select product" /></SelectTrigger>
                        <SelectContent>
                          {products.map(p => <SelectItem key={p.id} value={p.id}>{p.name} ({p.unit})</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <Input type="number" placeholder="Qty" value={currentItem.quantity} onChange={e => setCurrentItem({...currentItem, quantity: e.target.value})} />
                    <Input type="number" placeholder="Cost/unit" value={currentItem.unit_cost} onChange={e => setCurrentItem({...currentItem, unit_cost: e.target.value})} />
                  </div>
                  <div className="flex gap-2">
                    <Button type="button" variant={editingItemIndex !== null ? "default" : "outline"} size="sm" onClick={addItemToList} className="flex-1">
                      <Plus className="w-4 h-4 mr-1" /> {editingItemIndex !== null ? 'Update Item' : 'Add Item'}
                    </Button>
                    {editingItemIndex !== null && (
                      <Button type="button" variant="ghost" size="sm" onClick={() => {
                        setEditingItemIndex(null);
                        setCurrentItem({ product_id: '', product_name: '', quantity: '', unit_cost: '' });
                      }}>
                        Cancel
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>

              {formData.items.length > 0 && (
                <Card>
                  <CardHeader className="pb-2"><CardTitle className="text-sm">Items Added ({formData.items.length})</CardTitle></CardHeader>
                  <CardContent>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Product</TableHead>
                          <TableHead className="text-right">Qty</TableHead>
                          <TableHead className="text-right">Cost/Unit</TableHead>
                          <TableHead className="text-right">Total</TableHead>
                          <TableHead className="text-center">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {formData.items.map((item, index) => (
                          <TableRow key={index} className={editingItemIndex === index ? 'bg-blue-50' : ''}>
                            <TableCell>{item.product_name}</TableCell>
                            <TableCell className="text-right">{item.quantity}</TableCell>
                            <TableCell className="text-right">₹{item.unit_cost}</TableCell>
                            <TableCell className="text-right">₹{item.total_cost.toFixed(2)}</TableCell>
                            <TableCell className="text-center">
                              <div className="flex justify-center gap-1">
                                <Button type="button" variant="ghost" size="sm" onClick={() => editItem(index)} title="Edit">
                                  <Edit className="w-4 h-4 text-blue-500" />
                                </Button>
                                <Button type="button" variant="ghost" size="sm" onClick={() => removeItem(index)} title="Delete">
                                  <Trash2 className="w-4 h-4 text-red-500" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                        <TableRow className="bg-emerald-50 font-bold">
                          <TableCell colSpan={3}>Total Amount</TableCell>
                          <TableCell className="text-right text-emerald-700">₹{getTotalAmount().toFixed(2)}</TableCell>
                          <TableCell></TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              )}

              <div>
                <Label>Notes (Optional)</Label>
                <Textarea placeholder="Invoice number, payment details, etc." value={formData.notes} onChange={e => setFormData({...formData, notes: e.target.value})} />
              </div>

              <Button type="submit" className="w-full bg-emerald-600 hover:bg-emerald-700" disabled={formData.items.length === 0}>
                Record Purchase (₹{getTotalAmount().toFixed(2)})
              </Button>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Summary Cards - Show FILTERED data only */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Purchases ({dateFrom ? `${dateFrom.slice(5)}` : 'All'} - {dateTo ? `${dateTo.slice(5)}` : 'All'})</p>
                <p className="text-2xl font-bold">{filteredPurchases.length}</p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                <Truck className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Amount (Filtered)</p>
                <p className="text-2xl font-bold">₹{filteredPurchases.reduce((sum, p) => sum + (p.total_amount || 0), 0).toLocaleString()}</p>
              </div>
              <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Suppliers (This Period)</p>
                <p className="text-2xl font-bold">{[...new Set(filteredPurchases.map(p => p.supplier_id))].length}</p>
              </div>
              <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                <Users className="w-6 h-6 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Supplier-wise Summary Table - Clean Format */}
      <Card className="mb-6">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-lg">
            <BarChart3 className="w-5 h-5 text-emerald-600" /> Supplier-wise Summary
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {(() => {
            // Get unique suppliers and dates FROM FILTERED PURCHASES
            const supplierNames = [...new Set(filteredPurchases.map(p => p.supplier_name))].filter(Boolean).sort();
            const dates = [...new Set(filteredPurchases.map(p => p.purchase_date))].filter(Boolean).sort().reverse();
            
            // Dates are already filtered, just limit to 31
            const filteredDates = dates.slice(0, 31);
            
            if (supplierNames.length === 0 || filteredDates.length === 0) {
              return <p className="text-gray-500 text-center py-6">No purchase data to display for selected period</p>;
            }
            
            // Create a map of date -> supplier -> {amount, liters} FROM FILTERED DATA
            const dataMap = {};
            filteredPurchases.forEach(p => {
              if (!p.purchase_date || !p.supplier_name) return;
              if (!dataMap[p.purchase_date]) dataMap[p.purchase_date] = {};
              if (!dataMap[p.purchase_date][p.supplier_name]) {
                dataMap[p.purchase_date][p.supplier_name] = { amount: 0, liters: 0 };
              }
              dataMap[p.purchase_date][p.supplier_name].amount += p.total_amount || 0;
              // Use total_liters field if available, otherwise sum from items
              if (p.total_liters) {
                dataMap[p.purchase_date][p.supplier_name].liters += p.total_liters;
              } else {
                (p.items || []).forEach(item => {
                  dataMap[p.purchase_date][p.supplier_name].liters += item.quantity || 0;
                });
              }
            });
            
            // Calculate totals per supplier
            const supplierTotals = {};
            supplierNames.forEach(s => { supplierTotals[s] = { amount: 0, liters: 0 }; });
            filteredDates.forEach(date => {
              supplierNames.forEach(s => {
                if (dataMap[date]?.[s]) {
                  supplierTotals[s].amount += dataMap[date][s].amount || 0;
                  supplierTotals[s].liters += dataMap[date][s].liters || 0;
                }
              });
            });
            
            const grandTotal = {
              amount: Object.values(supplierTotals).reduce((sum, t) => sum + t.amount, 0),
              liters: Object.values(supplierTotals).reduce((sum, t) => sum + t.liters, 0)
            };
            
            return (
              <div className="overflow-x-auto">
                <Table className="text-sm">
                  <TableHeader>
                    <TableRow className="bg-slate-100 border-b-2">
                      <TableHead className="font-bold text-slate-700 w-24">Date</TableHead>
                      {supplierNames.map(supplier => (
                        <TableHead key={supplier} colSpan={3} className="text-center font-bold text-slate-700 border-l">
                          {supplier}
                        </TableHead>
                      ))}
                      <TableHead colSpan={2} className="text-center font-bold bg-emerald-100 text-emerald-800 border-l">
                        Day Total
                      </TableHead>
                    </TableRow>
                    <TableRow className="bg-slate-50 text-xs">
                      <TableHead></TableHead>
                      {supplierNames.map(supplier => (
                        <React.Fragment key={`${supplier}-header`}>
                          <TableHead className="text-right text-slate-500 border-l px-2">₹ Amt</TableHead>
                          <TableHead className="text-right text-slate-500 px-2">Liters</TableHead>
                          <TableHead className="text-center text-slate-500 px-1 w-8"></TableHead>
                        </React.Fragment>
                      ))}
                      <TableHead className="text-right text-emerald-700 border-l px-2">₹ Amt</TableHead>
                      <TableHead className="text-right text-emerald-700 px-2">Liters</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredDates.map((date, idx) => {
                      const dayTotal = supplierNames.reduce((acc, s) => ({
                        amount: acc.amount + (dataMap[date]?.[s]?.amount || 0),
                        liters: acc.liters + (dataMap[date]?.[s]?.liters || 0)
                      }), { amount: 0, liters: 0 });
                      
                      return (
                        <TableRow key={date} className={idx % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                          <TableCell className="font-medium text-slate-700">{date}</TableCell>
                          {supplierNames.map(supplier => (
                            <React.Fragment key={`${supplier}-${date}`}>
                              <TableCell className="text-right border-l px-2">
                                {dataMap[date]?.[supplier]?.amount ? `₹${dataMap[date][supplier].amount.toLocaleString()}` : '-'}
                              </TableCell>
                              <TableCell className="text-right text-blue-600 px-2">
                                {dataMap[date]?.[supplier]?.liters ? dataMap[date][supplier].liters.toFixed(1) : '-'}
                              </TableCell>
                              <TableCell className="text-center px-1">
                                <Button 
                                  variant="ghost" 
                                  size="sm" 
                                  className="h-6 w-6 p-0 hover:bg-blue-100"
                                  onClick={() => openSummaryEdit(date, supplier, dataMap[date]?.[supplier]?.amount || 0, dataMap[date]?.[supplier]?.liters || 0)}
                                  title={`Edit ${supplier} - ${date}`}
                                  data-testid={`edit-summary-${supplier}-${date}`}
                                >
                                  <Edit className="w-3 h-3 text-blue-500" />
                                </Button>
                              </TableCell>
                            </React.Fragment>
                          ))}
                          <TableCell className="text-right font-semibold bg-emerald-50 text-emerald-700 border-l px-2">
                            ₹{dayTotal.amount.toLocaleString()}
                          </TableCell>
                          <TableCell className="text-right font-semibold bg-emerald-50 text-blue-600 px-2">
                            {dayTotal.liters.toFixed(1)}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    {/* Totals Row */}
                    <TableRow className="bg-blue-100 font-bold border-t-2">
                      <TableCell className="text-blue-800">TOTAL</TableCell>
                      {supplierNames.map(supplier => (
                        <React.Fragment key={`${supplier}-total`}>
                          <TableCell className="text-right text-blue-800 border-l px-2">
                            ₹{supplierTotals[supplier].amount.toLocaleString()}
                          </TableCell>
                          <TableCell className="text-right text-blue-600 px-2">
                            {supplierTotals[supplier].liters.toFixed(1)}
                          </TableCell>
                          <TableCell className="px-1"></TableCell>
                        </React.Fragment>
                      ))}
                      <TableCell className="text-right bg-emerald-200 text-emerald-800 border-l px-2">
                        ₹{grandTotal.amount.toLocaleString()}
                      </TableCell>
                      <TableCell className="text-right bg-emerald-200 text-blue-700 px-2">
                        {grandTotal.liters.toFixed(1)}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </div>
            );
          })()}
        </CardContent>
      </Card>

      {/* Edit Summary Cell Dialog */}
      <Dialog open={editSummaryDialogOpen} onOpenChange={setEditSummaryDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="w-5 h-5 text-blue-500" />
              Edit Purchase Entry
            </DialogTitle>
            <DialogDescription>
              Update the purchase for <strong>{editSummaryData.supplier}</strong> on <strong>{editSummaryData.date}</strong>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Amount (₹)</Label>
                <Input 
                  type="number" 
                  value={editSummaryData.amount} 
                  onChange={(e) => setEditSummaryData({...editSummaryData, amount: e.target.value})}
                  placeholder="Enter amount"
                  data-testid="edit-summary-amount"
                />
              </div>
              <div>
                <Label>Liters</Label>
                <Input 
                  type="number" 
                  step="0.1"
                  value={editSummaryData.liters} 
                  onChange={(e) => setEditSummaryData({...editSummaryData, liters: e.target.value})}
                  placeholder="Enter liters"
                  data-testid="edit-summary-liters"
                />
              </div>
            </div>
            {editSummaryData.amount && editSummaryData.liters && parseFloat(editSummaryData.liters) > 0 && (
              <p className="text-sm text-gray-500">
                Rate: ₹{(parseFloat(editSummaryData.amount) / parseFloat(editSummaryData.liters)).toFixed(2)} per liter
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditSummaryDialogOpen(false)}>Cancel</Button>
            <Button 
              className="bg-emerald-600 hover:bg-emerald-700" 
              onClick={saveSummaryEdit}
              data-testid="save-summary-edit"
            >
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Purchases Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between flex-wrap gap-4">
            <CardTitle>Purchase History</CardTitle>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2">
                <Label className="text-sm text-gray-500">From:</Label>
                <Input 
                  type="date" 
                  value={dateFrom} 
                  onChange={e => setDateFrom(e.target.value)}
                  className="w-40"
                />
              </div>
              <div className="flex items-center gap-2">
                <Label className="text-sm text-gray-500">To:</Label>
                <Input 
                  type="date" 
                  value={dateTo} 
                  onChange={e => setDateTo(e.target.value)}
                  className="w-40"
                />
              </div>
              {(dateFrom || dateTo) && (
                <Button variant="ghost" size="sm" onClick={() => { setDateFrom(''); setDateTo(''); }}>
                  Clear
                </Button>
              )}
            </div>
          </div>
          {(dateFrom || dateTo) && (
            <p className="text-sm text-gray-500 mt-2">
              Showing {filteredPurchases.length} of {purchases.length} purchases
              {dateFrom && dateTo && ` from ${dateFrom} to ${dateTo}`}
            </p>
          )}
        </CardHeader>
        <CardContent>
          {filteredPurchases.length === 0 ? (
            <div className="text-center py-8">
              <Truck className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">{purchases.length === 0 ? 'No purchases recorded yet' : 'No purchases in selected date range'}</p>
              <p className="text-sm text-gray-400">{purchases.length === 0 ? 'Click "Add Purchase" to record your first purchase' : 'Try adjusting the date filter'}</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Purchase Date</TableHead>
                  <TableHead>Supplier</TableHead>
                  <TableHead>Items</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead>Notes</TableHead>
                  <TableHead className="text-center">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredPurchases.map(purchase => (
                  <TableRow key={purchase.id}>
                    <TableCell>
                      <div>
                        <p className="font-medium">
                          {purchase.purchase_date || new Date(purchase.created_at).toLocaleDateString('en-IN')}
                          {purchase.purchase_date_to && purchase.purchase_date_to !== purchase.purchase_date && (
                            <span className="text-blue-600"> → {purchase.purchase_date_to}</span>
                          )}
                        </p>
                        <p className="text-xs text-gray-400">Recorded: {new Date(purchase.created_at).toLocaleDateString('en-IN')}</p>
                      </div>
                    </TableCell>
                    <TableCell className="font-medium">{purchase.supplier_name}</TableCell>
                    <TableCell>
                      <div className="space-y-1">
                        {purchase.items?.slice(0, 2).map((item, idx) => (
                          <Badge key={idx} variant="outline" className="mr-1 text-xs">
                            {item.product_name}: {item.quantity}
                          </Badge>
                        ))}
                        {purchase.items?.length > 2 && (
                          <Badge variant="secondary" className="text-xs">+{purchase.items.length - 2} more</Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-right font-semibold">₹{purchase.total_amount?.toLocaleString()}</TableCell>
                    <TableCell className="max-w-[150px] truncate">{purchase.notes || '-'}</TableCell>
                    <TableCell>
                      <div className="flex justify-center gap-1">
                        <Button variant="ghost" size="sm" onClick={() => viewPurchase(purchase)} title="View Details">
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => openEditDialog(purchase)} title="Edit Purchase">
                          <Edit className="w-4 h-4 text-blue-500" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => deletePurchase(purchase.id)} title="Delete Purchase">
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* View Purchase Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Purchase Details</DialogTitle>
            <DialogDescription>
              Purchase Date: {selectedPurchase?.purchase_date || (selectedPurchase && new Date(selectedPurchase.created_at).toLocaleDateString('en-IN'))}
            </DialogDescription>
          </DialogHeader>
          {selectedPurchase && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-gray-50 p-3 rounded-lg">
                  <p className="text-sm text-gray-500">Supplier</p>
                  <p className="font-semibold">{selectedPurchase.supplier_name}</p>
                </div>
                <div className="bg-blue-50 p-3 rounded-lg">
                  <p className="text-sm text-gray-500">Purchase Date</p>
                  <p className="font-semibold">{selectedPurchase.purchase_date || 'Not specified'}</p>
                </div>
              </div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Product</TableHead>
                    <TableHead className="text-right">Qty</TableHead>
                    <TableHead className="text-right">Cost</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {selectedPurchase.items?.map((item, i) => (
                    <TableRow key={i}>
                      <TableCell>{item.product_name}</TableCell>
                      <TableCell className="text-right">{item.quantity}</TableCell>
                      <TableCell className="text-right">₹{item.unit_cost}</TableCell>
                      <TableCell className="text-right">₹{item.total_cost?.toFixed(2)}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow className="bg-emerald-50 font-bold">
                    <TableCell colSpan={3}>Total</TableCell>
                    <TableCell className="text-right text-emerald-700">₹{selectedPurchase.total_amount?.toLocaleString()}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
              {selectedPurchase.notes && (
                <div className="bg-yellow-50 p-3 rounded-lg">
                  <p className="text-sm text-gray-500">Notes</p>
                  <p>{selectedPurchase.notes}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Purchase Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={(open) => { setEditDialogOpen(open); if (!open) setEditingPurchase(null); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Purchase</DialogTitle>
            <DialogDescription>Update purchase details for {editingPurchase?.supplier_name}</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEditSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Supplier</Label>
                <Select 
                  value={editFormData.supplier_id} 
                  onValueChange={(val) => {
                    const sup = suppliers.find(s => s.id === val);
                    setEditFormData({...editFormData, supplier_id: val, supplier_name: sup?.name || ''});
                  }}
                >
                  <SelectTrigger><SelectValue placeholder="Select supplier" /></SelectTrigger>
                  <SelectContent>
                    {suppliers.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Purchase Date</Label>
                <Input 
                  type="date" 
                  value={editFormData.purchase_date} 
                  onChange={e => setEditFormData({...editFormData, purchase_date: e.target.value})}
                />
              </div>
            </div>
            
            <div>
              <Label className="mb-2 block">Items</Label>
              {editFormData.items.map((item, idx) => (
                <div key={idx} className="flex items-center gap-2 mb-2 p-2 bg-gray-50 rounded">
                  <Input 
                    value={item.product_name} 
                    onChange={e => updateEditItem(idx, 'product_name', e.target.value)}
                    placeholder="Product"
                    className="flex-1"
                  />
                  <Input 
                    type="number" 
                    value={item.quantity} 
                    onChange={e => updateEditItem(idx, 'quantity', e.target.value)}
                    placeholder="Qty"
                    className="w-20"
                  />
                  <Input 
                    type="number" 
                    value={item.unit_cost} 
                    onChange={e => updateEditItem(idx, 'unit_cost', e.target.value)}
                    placeholder="Cost"
                    className="w-24"
                  />
                  <span className="text-sm font-medium w-24 text-right">₹{(item.total_cost || 0).toLocaleString()}</span>
                  <Button type="button" variant="ghost" size="sm" onClick={() => removeEditItem(idx)}>
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </Button>
                </div>
              ))}
              <div className="text-right font-bold mt-2">
                Total: ₹{editFormData.items.reduce((sum, i) => sum + (i.total_cost || 0), 0).toLocaleString()}
              </div>
            </div>
            
            <div>
              <Label>Notes</Label>
              <Textarea 
                value={editFormData.notes} 
                onChange={e => setEditFormData({...editFormData, notes: e.target.value})}
                placeholder="Optional notes"
              />
            </div>
            
            <div className="flex gap-2 justify-end">
              <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700">Save Changes</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};
const SalesPage = () => {
  const [sales, setSales] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedSale, setSelectedSale] = useState(null);
  const [formData, setFormData] = useState({ items: [], customer_name: '', notes: '', sale_date: new Date().toISOString().split('T')[0] });
  const [currentItem, setCurrentItem] = useState({ product_id: '', product_name: '', quantity: '', unit_price: '', cost_price: 0 });
  const [editingItemIndex, setEditingItemIndex] = useState(null);
  const [editSaleDialog, setEditSaleDialog] = useState({ open: false, sale: null });
  const [editSaleData, setEditSaleData] = useState({ items: [], customer_name: '', notes: '', source: 'admin', sale_date: '' });
  // Import Sales Excel states
  const [importSalesDialogOpen, setImportSalesDialogOpen] = useState(false);
  const [importingSales, setImportingSales] = useState(false);
  const [importSalesResult, setImportSalesResult] = useState(null);
  // Month filter - default to most recent month with data or current month
  const [selectedMonth, setSelectedMonth] = useState('');
  // Bill Dialog state for QR code payment
  const [billDialogOpen, setBillDialogOpen] = useState(false);
  const [generatedBill, setGeneratedBill] = useState(null);
  
  // Set default month after data loads
  useEffect(() => {
    if (sales.length > 0 && !selectedMonth) {
      const months = [...new Set(sales.map(s => s.sale_date?.slice(0, 7)).filter(Boolean))].sort().reverse();
      if (months.length > 0) {
        setSelectedMonth(months[0]); // Most recent month with data
      }
    }
  }, [sales, selectedMonth]);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [salesRes, productsRes] = await Promise.all([
        axios.get(`${API}/sales`),
        axios.get(`${API}/products`)
      ]);
      setSales(salesRes.data);
      setProducts(productsRes.data);
    } catch (error) { toast.error('Failed to load data'); }
    finally { setLoading(false); }
  };

  const deleteSale = async (saleId) => {
    if (!window.confirm('⚠️ Delete this sale?\n\nStock will be restored.')) return;
    try {
      await axios.delete(`${API}/sales/${saleId}`);
      toast.success('Sale deleted. Stock restored.');
      fetchData();
    } catch (error) { toast.error('Failed to delete sale'); }
  };

  const openEditSaleDialog = (sale) => {
    setEditSaleData({
      items: sale.items?.map(item => ({
        product_id: item.product_id,
        product_name: item.product_name,
        quantity: item.quantity,
        unit_price: item.unit_price,
        cost_price: item.cost_price
      })) || [],
      customer_name: sale.customer_name || '',
      notes: sale.notes || '',
      source: sale.source || 'admin',
      sale_date: sale.sale_date || ''
    });
    setEditSaleDialog({ open: true, sale });
  };

  const saveEditedSale = async () => {
    try {
      await axios.put(`${API}/sales/${editSaleDialog.sale.id}`, {
        items: editSaleData.items,
        customer_name: editSaleData.customer_name,
        notes: editSaleData.notes,
        source: editSaleData.source,
        sale_date: editSaleData.sale_date
      });
      toast.success('Sale updated!');
      setEditSaleDialog({ open: false, sale: null });
      fetchData();
    } catch (error) { toast.error('Failed to update sale'); }
  };

  const handleProductSelect = (productId) => {
    const product = products.find(p => p.id === productId);
    if (product) {
      setCurrentItem({ 
        ...currentItem, 
        product_id: productId, 
        product_name: product.name, 
        unit_price: product.price.toString(),
        cost_price: product.cost_price
      });
    }
  };

  const addItemToList = () => {
    if (!currentItem.product_id || !currentItem.quantity || !currentItem.unit_price) {
      toast.error('Please fill all item fields'); return;
    }
    const product = products.find(p => p.id === currentItem.product_id);
    if (product && parseFloat(currentItem.quantity) > product.stock_quantity) {
      toast.error(`Only ${product.stock_quantity} ${product.unit} available in stock`); return;
    }
    const qty = parseFloat(currentItem.quantity);
    const price = parseFloat(currentItem.unit_price);
    const cost = currentItem.cost_price;
    const newItem = {
      product_id: currentItem.product_id,
      product_name: currentItem.product_name,
      quantity: qty,
      unit_price: price,
      cost_price: cost,
      total_price: qty * price,
      profit: (price - cost) * qty
    };
    
    if (editingItemIndex !== null) {
      const updatedItems = [...formData.items];
      updatedItems[editingItemIndex] = newItem;
      setFormData({ ...formData, items: updatedItems });
      setEditingItemIndex(null);
    } else {
      setFormData({ ...formData, items: [...formData.items, newItem] });
    }
    setCurrentItem({ product_id: '', product_name: '', quantity: '', unit_price: '', cost_price: 0 });
  };

  const editItem = (index) => {
    const item = formData.items[index];
    setCurrentItem({
      product_id: item.product_id,
      product_name: item.product_name,
      quantity: item.quantity.toString(),
      unit_price: item.unit_price.toString(),
      cost_price: item.cost_price
    });
    setEditingItemIndex(index);
  };

  const removeItem = (index) => {
    setFormData({ ...formData, items: formData.items.filter((_, i) => i !== index) });
    if (editingItemIndex === index) {
      setEditingItemIndex(null);
      setCurrentItem({ product_id: '', product_name: '', quantity: '', unit_price: '', cost_price: 0 });
    }
  };

  const getTotalAmount = () => formData.items.reduce((sum, item) => sum + item.total_price, 0);
  const getTotalProfit = () => formData.items.reduce((sum, item) => sum + item.profit, 0);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.items.length === 0) { toast.error('Please add at least one item'); return; }
    try {
      const response = await axios.post(`${API}/sales`, {
        items: formData.items,
        customer_name: formData.customer_name || null,
        notes: formData.notes || null,
        sale_date: formData.sale_date
      });
      toast.success('Sale recorded successfully! Stock updated.');
      setDialogOpen(false);
      
      // Generate bill data for QR code dialog
      const billData = {
        id: response.data.id || Date.now().toString(),
        customer_name: formData.customer_name || 'Walk-in Customer',
        items: formData.items,
        total_amount: getTotalAmount(),
        sale_date: formData.sale_date,
        notes: formData.notes,
        loyalty: response.data.loyalty || null
      };
      setGeneratedBill(billData);
      setBillDialogOpen(true);
      
      // Send WhatsApp notification to admin
      const adminPhone = CONFIG.ADMIN_WHATSAPP;
      const itemsList = formData.items.map(item => `• ${item.product_name} x${item.quantity} = ₹${item.total_price.toFixed(2)}`).join('\n');
      const waMsg = `🧾 *New Sale Recorded*\n\n👤 Customer: ${billData.customer_name}\n📅 Date: ${formData.sale_date}\n\n📦 Items:\n${itemsList}\n\n💰 *Total: ₹${getTotalAmount().toFixed(2)}*\n\n🏪 Freshwala Dairy`;
      window.open(`https://wa.me/${adminPhone}?text=${encodeURIComponent(waMsg)}`, '_blank');
      
      resetForm();
      fetchData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to record sale'); }
  };

  const resetForm = () => {
    setFormData({ items: [], customer_name: '', notes: '', sale_date: new Date().toISOString().split('T')[0] });
    setCurrentItem({ product_id: '', product_name: '', quantity: '', unit_price: '', cost_price: 0 });
    setEditingItemIndex(null);
  };

  const viewSale = (sale) => { setSelectedSale(sale); setViewDialogOpen(true); };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div></div>;

  // Filter sales by selected month
  const filteredSales = sales.filter(s => s.sale_date?.startsWith(selectedMonth));
  
  const totalSalesAmount = filteredSales.reduce((sum, s) => sum + (s.total_amount || 0), 0);
  const totalProfit = filteredSales.reduce((sum, s) => sum + (s.total_profit || 0), 0);
  const shopSales = filteredSales.filter(s => s.source === 'shop').length;
  const adminSales = filteredSales.filter(s => s.source === 'admin').length;
  
  // Get unique months from sales data for month selector
  const availableMonths = [...new Set(sales.map(s => s.sale_date?.slice(0, 7)).filter(Boolean))].sort().reverse();

  const handleSalesExcelImport = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    setImportingSales(true);
    setImportSalesResult(null);
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await axios.post(`${API}/sales/import-excel`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setImportSalesResult(response.data);
      toast.success(response.data.message);
      fetchData();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to import';
      toast.error(errorMsg);
      setImportSalesResult({ success: false, message: errorMsg });
    } finally {
      setImportingSales(false);
      event.target.value = '';
    }
  };
  
  const formatMonth = (monthStr) => {
    if (!monthStr) return '';
    const [year, month] = monthStr.split('-');
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${monthNames[parseInt(month) - 1]} ${year}`;
  };

  return (
    <div className="space-y-6" data-testid="sales-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Sales</h1>
          <p className="text-gray-500">Record and track all sales (admin & customer orders)</p>
        </div>
        <div className="flex gap-2">
          {/* Import Sales Excel Dialog */}
          <Dialog open={importSalesDialogOpen} onOpenChange={(open) => { setImportSalesDialogOpen(open); if (!open) setImportSalesResult(null); }}>
            <DialogTrigger asChild>
              <Button variant="outline" className="border-blue-500 text-blue-600 hover:bg-blue-50" data-testid="import-sales-excel-btn">
                <Upload className="w-4 h-4 mr-2" /> Import Excel
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>Import Daily Sales from Excel</DialogTitle>
                <DialogDescription>Upload Excel file with daily sales data</DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm">
                  <p className="font-medium text-blue-800 mb-1">Expected Excel Format:</p>
                  <code className="text-xs bg-white px-2 py-1 rounded block">
                    Date/Collection | Milk | Curd | Paneer | Ghee | Pickles | Batters | Malai | Total | Box
                  </code>
                  <p className="text-blue-600 text-xs mt-2">
                    Values should be amounts (₹). Dates in first column.
                  </p>
                </div>
                
                <div className="border-2 border-dashed border-blue-400 hover:border-blue-500 bg-blue-50 rounded-lg p-6 text-center transition-colors">
                  <Upload className="w-10 h-10 mx-auto text-blue-500 mb-2" />
                  <Label htmlFor="sales-excel-file" className="cursor-pointer">
                    <span className="text-blue-600 hover:text-blue-700 font-medium">Click to select Excel file</span>
                  </Label>
                  <p className="text-xs text-gray-400 mt-1">Supports .xlsx and .xls files</p>
                  <Input 
                    id="sales-excel-file"
                    type="file" 
                    accept=".xlsx,.xls"
                    onChange={handleSalesExcelImport}
                    className="hidden"
                    disabled={importingSales}
                  />
                </div>
                
                {importingSales && (
                  <div className="flex items-center justify-center gap-2 text-blue-600">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                    <span>Processing...</span>
                  </div>
                )}
                
                {importSalesResult && (
                  <div className={`p-4 rounded-lg ${importSalesResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                    <p className={`font-medium ${importSalesResult.success ? 'text-green-700' : 'text-red-700'}`}>
                      {importSalesResult.message}
                    </p>
                    {importSalesResult.details && (
                      <div className="mt-2 text-sm">
                        <p className="text-gray-600">Sales imported: <strong>{importSalesResult.details.sales_imported}</strong></p>
                        <p className="text-gray-600">Total revenue: <strong>₹{importSalesResult.details.total_revenue?.toLocaleString()}</strong></p>
                      </div>
                    )}
                  </div>
                )}
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => { setImportSalesDialogOpen(false); setImportSalesResult(null); }}>Close</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="bg-emerald-600 hover:bg-emerald-700" data-testid="add-sale-btn">
                <Plus className="w-4 h-4 mr-2" /> New Sale
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Record New Sale</DialogTitle>
              <DialogDescription>Add a walk-in sale. Stock will be automatically deducted.</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Customer Name (Optional)</Label>
                  <Input placeholder="Walk-in customer" value={formData.customer_name} onChange={e => setFormData({...formData, customer_name: e.target.value})} />
                </div>
                <div>
                  <Label>Sale Date *</Label>
                  <Input 
                    type="date" 
                    value={formData.sale_date} 
                    onChange={e => setFormData({...formData, sale_date: e.target.value})}
                  />
                </div>
              </div>

              <Card className="bg-gray-50">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">
                    {editingItemIndex !== null ? '✏️ Edit Item' : 'Add Items'}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="grid grid-cols-5 gap-2">
                    <div className="col-span-2">
                      <Select value={currentItem.product_id} onValueChange={handleProductSelect}>
                        <SelectTrigger><SelectValue placeholder="Select product" /></SelectTrigger>
                        <SelectContent>
                          {products.filter(p => p.stock_quantity > 0).map(p => (
                            <SelectItem key={p.id} value={p.id}>{p.name} (Stock: {p.stock_quantity})</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <Input type="number" placeholder="Qty" value={currentItem.quantity} onChange={e => setCurrentItem({...currentItem, quantity: e.target.value})} />
                    <Input type="number" placeholder="Price" value={currentItem.unit_price} onChange={e => setCurrentItem({...currentItem, unit_price: e.target.value})} />
                    <Input 
                      type="number" 
                      placeholder="Total ₹" 
                      className="bg-green-50 border-green-300 font-semibold"
                      value={(parseFloat(currentItem.quantity) || 0) * (parseFloat(currentItem.unit_price) || 0) || ''}
                      onChange={e => {
                        const totalAmount = parseFloat(e.target.value) || 0;
                        const price = parseFloat(currentItem.unit_price) || 0;
                        if (price > 0) {
                          const calculatedQty = Math.round((totalAmount / price) * 100) / 100;
                          setCurrentItem({...currentItem, quantity: calculatedQty.toString()});
                        }
                      }}
                      title="Shows total (Qty × Price). Edit to auto-calculate Qty"
                    />
                  </div>
                  <p className="text-xs text-gray-500">💡 Total = Qty × Price. Change Total to auto-calculate Qty</p>
                  <div className="flex gap-2">
                    <Button type="button" variant={editingItemIndex !== null ? "default" : "outline"} size="sm" onClick={addItemToList} className="flex-1">
                      <Plus className="w-4 h-4 mr-1" /> {editingItemIndex !== null ? 'Update Item' : 'Add Item'}
                    </Button>
                    {editingItemIndex !== null && (
                      <Button type="button" variant="ghost" size="sm" onClick={() => {
                        setEditingItemIndex(null);
                        setCurrentItem({ product_id: '', product_name: '', quantity: '', unit_price: '', cost_price: 0 });
                      }}>
                        Cancel
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>

              {formData.items.length > 0 && (
                <Card>
                  <CardHeader className="pb-2"><CardTitle className="text-sm">Items ({formData.items.length})</CardTitle></CardHeader>
                  <CardContent>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Product</TableHead>
                          <TableHead className="text-right">Qty</TableHead>
                          <TableHead className="text-right">Price</TableHead>
                          <TableHead className="text-right">Total</TableHead>
                          <TableHead className="text-right">Profit</TableHead>
                          <TableHead className="text-center">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {formData.items.map((item, index) => (
                          <TableRow key={index} className={editingItemIndex === index ? 'bg-blue-50' : ''}>
                            <TableCell>{item.product_name}</TableCell>
                            <TableCell className="text-right">{item.quantity}</TableCell>
                            <TableCell className="text-right">₹{item.unit_price}</TableCell>
                            <TableCell className="text-right">₹{item.total_price.toFixed(2)}</TableCell>
                            <TableCell className="text-right text-emerald-600">₹{item.profit.toFixed(2)}</TableCell>
                            <TableCell className="text-center">
                              <div className="flex justify-center gap-1">
                                <Button type="button" variant="ghost" size="sm" onClick={() => editItem(index)} title="Edit">
                                  <Edit className="w-4 h-4 text-blue-500" />
                                </Button>
                                <Button type="button" variant="ghost" size="sm" onClick={() => removeItem(index)} title="Delete">
                                  <Trash2 className="w-4 h-4 text-red-500" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                        <TableRow className="bg-emerald-50 font-bold">
                          <TableCell colSpan={3}>Total</TableCell>
                          <TableCell className="text-right">₹{getTotalAmount().toFixed(2)}</TableCell>
                          <TableCell className="text-right text-emerald-700">₹{getTotalProfit().toFixed(2)}</TableCell>
                          <TableCell></TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              )}

              <div>
                <Label>Notes (Optional)</Label>
                <Textarea placeholder="Any additional notes..." value={formData.notes} onChange={e => setFormData({...formData, notes: e.target.value})} />
              </div>

              <Button type="submit" className="w-full bg-emerald-600 hover:bg-emerald-700" disabled={formData.items.length === 0}>
                Record Sale (₹{getTotalAmount().toFixed(2)})
              </Button>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Month Selector Tabs */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-sm text-gray-500 font-medium">View Month:</span>
        {availableMonths.length > 0 ? (
          availableMonths.map(month => (
            <Button
              key={month}
              variant={selectedMonth === month ? "default" : "outline"}
              size="sm"
              onClick={() => setSelectedMonth(month)}
              className={selectedMonth === month ? "bg-emerald-600 hover:bg-emerald-700" : ""}
            >
              {formatMonth(month)}
            </Button>
          ))
        ) : (
          <span className="text-gray-400 text-sm">No sales data</span>
        )}
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Sales ({formatMonth(selectedMonth)})</p>
                <p className="text-2xl font-bold">{filteredSales.length}</p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                <ShoppingCart className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Revenue</p>
                <p className="text-2xl font-bold">₹{totalSalesAmount.toLocaleString()}</p>
              </div>
              <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Profit</p>
                <p className="text-2xl font-bold text-emerald-600">₹{totalProfit.toLocaleString()}</p>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Shop Orders</p>
                <p className="text-2xl font-bold">{shopSales}</p>
                <p className="text-xs text-gray-400">Admin: {adminSales}</p>
              </div>
              <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                <Users className="w-6 h-6 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Sales Table - Excel-like Format with Product Columns */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-emerald-600" />
            Sales History - {formatMonth(selectedMonth)}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {filteredSales.length === 0 ? (
            <div className="text-center py-8">
              <ShoppingCart className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">No sales for {formatMonth(selectedMonth)}</p>
              <p className="text-sm text-gray-400">Select a different month or import Excel data</p>
            </div>
          ) : (
            (() => {
              // Group sales by date and aggregate product amounts
              const productColumns = ['Milk', 'Curd', 'Paneer', 'Ghee', 'Pickles', 'Batters', 'Malai'];
              const salesByDate = {};
              
              filteredSales.forEach(sale => {
                const date = sale.sale_date || new Date(sale.created_at).toISOString().split('T')[0];
                if (!salesByDate[date]) {
                  salesByDate[date] = {
                    date,
                    products: {},
                    total: 0,
                    profit: 0,
                    online: 0,
                    saleIds: []
                  };
                  productColumns.forEach(p => salesByDate[date].products[p] = 0);
                }
                
                // Add sale amounts to the date
                salesByDate[date].total += sale.total_amount || 0;
                salesByDate[date].profit += sale.total_profit || 0;
                salesByDate[date].saleIds.push(sale.id);
                
                // Check if this is an online sale (from shop)
                if (sale.source === 'shop') {
                  salesByDate[date].online += sale.total_amount || 0;
                }
                
                // Aggregate product amounts
                (sale.items || []).forEach(item => {
                  const productName = item.product_name;
                  // Match product to column (case-insensitive)
                  const matchedColumn = productColumns.find(col => 
                    productName?.toLowerCase().includes(col.toLowerCase()) ||
                    col.toLowerCase().includes(productName?.toLowerCase())
                  );
                  if (matchedColumn) {
                    salesByDate[date].products[matchedColumn] += item.total_price || 0;
                  } else {
                    // If no match, add to the closest category or create Other
                    if (!salesByDate[date].products['Other']) {
                      salesByDate[date].products['Other'] = 0;
                    }
                    salesByDate[date].products['Other'] += item.total_price || 0;
                  }
                });
              });
              
              const sortedDates = Object.keys(salesByDate).sort().reverse();
              
              // Calculate totals
              const grandTotal = {
                products: {},
                total: 0,
                profit: 0,
                online: 0
              };
              productColumns.forEach(p => grandTotal.products[p] = 0);
              grandTotal.products['Other'] = 0;
              
              sortedDates.forEach(date => {
                grandTotal.total += salesByDate[date].total;
                grandTotal.profit += salesByDate[date].profit;
                grandTotal.online += salesByDate[date].online;
                productColumns.forEach(p => {
                  grandTotal.products[p] += salesByDate[date].products[p] || 0;
                });
                grandTotal.products['Other'] += salesByDate[date].products['Other'] || 0;
              });
              
              // Check if Other column has any data
              const hasOther = grandTotal.products['Other'] > 0;
              const displayColumns = hasOther ? [...productColumns, 'Other'] : productColumns;
              
              return (
                <div className="overflow-x-auto">
                  <Table className="text-sm">
                    <TableHeader>
                      <TableRow className="bg-slate-100 border-b-2">
                        <TableHead className="font-bold text-slate-700 w-28 sticky left-0 bg-slate-100">Date</TableHead>
                        {displayColumns.map(col => (
                          <TableHead key={col} className="text-right font-bold text-slate-700 px-3 min-w-[80px]">
                            {col}
                          </TableHead>
                        ))}
                        <TableHead className="text-right font-bold bg-emerald-100 text-emerald-800 px-3 min-w-[90px] border-l">
                          Total
                        </TableHead>
                        <TableHead className="text-right font-bold bg-blue-100 text-blue-800 px-3 min-w-[80px]">
                          Online
                        </TableHead>
                        <TableHead className="text-right font-bold bg-green-100 text-green-800 px-3 min-w-[80px]">
                          Profit
                        </TableHead>
                        <TableHead className="text-center font-bold text-slate-700 px-2 w-24">
                          Actions
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {sortedDates.map((date, idx) => {
                        const dayData = salesByDate[date];
                        return (
                          <TableRow key={date} className={idx % 2 === 0 ? 'bg-white' : 'bg-slate-50'} data-testid={`sales-row-${date}`}>
                            <TableCell className="font-medium text-slate-700 sticky left-0 bg-inherit">{date}</TableCell>
                            {displayColumns.map(col => (
                              <TableCell key={col} className="text-right px-3">
                                {dayData.products[col] ? `₹${dayData.products[col].toLocaleString()}` : '-'}
                              </TableCell>
                            ))}
                            <TableCell className="text-right font-semibold bg-emerald-50 text-emerald-700 px-3 border-l">
                              ₹{dayData.total.toLocaleString()}
                            </TableCell>
                            <TableCell className="text-right bg-blue-50 text-blue-700 px-3">
                              {dayData.online > 0 ? `₹${dayData.online.toLocaleString()}` : '-'}
                            </TableCell>
                            <TableCell className="text-right font-semibold bg-green-50 text-green-700 px-3">
                              ₹{dayData.profit.toLocaleString()}
                            </TableCell>
                            <TableCell className="text-center px-2">
                              <div className="flex justify-center gap-1">
                                <Button 
                                  variant="ghost" 
                                  size="sm" 
                                  onClick={() => {
                                    // Find first sale of this date to view
                                    const sale = filteredSales.find(s => s.sale_date === date || (s.created_at && s.created_at.startsWith(date)));
                                    if (sale) viewSale(sale);
                                  }} 
                                  title="View Details"
                                  data-testid={`view-sale-${date}`}
                                >
                                  <Eye className="w-4 h-4" />
                                </Button>
                                <Button 
                                  variant="ghost" 
                                  size="sm" 
                                  onClick={() => {
                                    // Find first sale of this date to edit
                                    const sale = filteredSales.find(s => s.sale_date === date || (s.created_at && s.created_at.startsWith(date)));
                                    if (sale) openEditSaleDialog(sale);
                                  }} 
                                  title="Edit Sale"
                                  data-testid={`edit-sale-${date}`}
                                >
                                  <Edit className="w-4 h-4 text-blue-500" />
                                </Button>
                                <Button 
                                  variant="ghost" 
                                  size="sm" 
                                  onClick={() => {
                                    if (window.confirm(`Delete all sales for ${date}? This cannot be undone.`)) {
                                      // Delete all sales for this date
                                      dayData.saleIds.forEach(saleId => deleteSale(saleId));
                                    }
                                  }} 
                                  title="Delete Sales"
                                  data-testid={`delete-sale-${date}`}
                                >
                                  <Trash2 className="w-4 h-4 text-red-500" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                      {/* Totals Row */}
                      <TableRow className="bg-blue-100 font-bold border-t-2">
                        <TableCell className="text-blue-800 sticky left-0 bg-blue-100">TOTAL</TableCell>
                        {displayColumns.map(col => (
                          <TableCell key={col} className="text-right text-blue-800 px-3">
                            {grandTotal.products[col] > 0 ? `₹${grandTotal.products[col].toLocaleString()}` : '-'}
                          </TableCell>
                        ))}
                        <TableCell className="text-right bg-emerald-200 text-emerald-800 px-3 border-l">
                          ₹{grandTotal.total.toLocaleString()}
                        </TableCell>
                        <TableCell className="text-right bg-blue-200 text-blue-800 px-3">
                          {grandTotal.online > 0 ? `₹${grandTotal.online.toLocaleString()}` : '-'}
                        </TableCell>
                        <TableCell className="text-right bg-green-200 text-green-800 px-3">
                          ₹{grandTotal.profit.toLocaleString()}
                        </TableCell>
                        <TableCell></TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </div>
              );
            })()
          )}
        </CardContent>
      </Card>

      {/* Edit Sale Dialog */}
      <Dialog open={editSaleDialog.open} onOpenChange={(open) => !open && setEditSaleDialog({ open: false, sale: null })}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>✏️ Edit Sale</DialogTitle>
            <DialogDescription>Modify sale details, items, and source</DialogDescription>
          </DialogHeader>
          {editSaleDialog.sale && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Customer Name</Label>
                  <Input 
                    value={editSaleData.customer_name} 
                    onChange={e => setEditSaleData({...editSaleData, customer_name: e.target.value})}
                    placeholder="Walk-in customer"
                  />
                </div>
                <div>
                  <Label>Sale Date</Label>
                  <Input 
                    type="date"
                    value={editSaleData.sale_date} 
                    onChange={e => setEditSaleData({...editSaleData, sale_date: e.target.value})}
                  />
                </div>
              </div>
              
              <div>
                <Label>Source</Label>
                <Select value={editSaleData.source} onValueChange={(v) => setEditSaleData({...editSaleData, source: v})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">👤 Admin Sale</SelectItem>
                    <SelectItem value="shop">🛒 Shop Order</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Items ({editSaleData.items.length})</Label>
                <div className="border rounded-lg p-3 space-y-2 max-h-[200px] overflow-y-auto">
                  {editSaleData.items.map((item, idx) => (
                    <div key={idx} className="flex items-center gap-2 bg-gray-50 p-2 rounded">
                      <span className="flex-1 font-medium">{item.product_name}</span>
                      <Input 
                        type="number" 
                        className="w-20" 
                        value={item.quantity}
                        onChange={e => {
                          const newItems = [...editSaleData.items];
                          newItems[idx].quantity = parseFloat(e.target.value) || 0;
                          setEditSaleData({...editSaleData, items: newItems});
                        }}
                      />
                      <span className="text-gray-500">×</span>
                      <Input 
                        type="number" 
                        className="w-24" 
                        value={item.unit_price}
                        onChange={e => {
                          const newItems = [...editSaleData.items];
                          newItems[idx].unit_price = parseFloat(e.target.value) || 0;
                          setEditSaleData({...editSaleData, items: newItems});
                        }}
                      />
                      <span className="text-emerald-600 font-medium w-20 text-right">
                        ₹{((item.quantity || 0) * (item.unit_price || 0)).toFixed(0)}
                      </span>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => {
                          const newItems = editSaleData.items.filter((_, i) => i !== idx);
                          setEditSaleData({...editSaleData, items: newItems});
                        }}
                      >
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-emerald-50 p-3 rounded-lg">
                <div className="flex justify-between text-lg font-bold">
                  <span>New Total:</span>
                  <span className="text-emerald-700">
                    ₹{editSaleData.items.reduce((sum, item) => sum + ((item.quantity || 0) * (item.unit_price || 0)), 0).toFixed(2)}
                  </span>
                </div>
              </div>

              <div>
                <Label>Notes</Label>
                <Textarea 
                  value={editSaleData.notes} 
                  onChange={e => setEditSaleData({...editSaleData, notes: e.target.value})}
                  placeholder="Additional notes..."
                />
              </div>

              <div className="flex gap-2">
                <Button variant="outline" className="flex-1" onClick={() => setEditSaleDialog({ open: false, sale: null })}>
                  Cancel
                </Button>
                <Button className="flex-1 bg-blue-600 hover:bg-blue-700" onClick={saveEditedSale}>
                  💾 Save Changes
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* View Sale Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Sale Details</DialogTitle>
            <DialogDescription>
              Sale Date: {selectedSale?.sale_date || (selectedSale && new Date(selectedSale.created_at).toLocaleDateString('en-IN'))}
            </DialogDescription>
          </DialogHeader>
          {selectedSale && (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-gray-50 p-3 rounded-lg">
                  <p className="text-sm text-gray-500">Customer</p>
                  <p className="font-semibold">{selectedSale.customer_name || 'Walk-in'}</p>
                </div>
                <div className="bg-gray-50 p-3 rounded-lg">
                  <p className="text-sm text-gray-500">Source</p>
                  <p className="font-semibold">{selectedSale.source === 'shop' ? '🛒 Online Shop' : '👤 Admin Sale'}</p>
                </div>
                <div className="bg-blue-50 p-3 rounded-lg">
                  <p className="text-sm text-gray-500">Sale Date</p>
                  <p className="font-semibold">{selectedSale.sale_date || 'Not specified'}</p>
                </div>
              </div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Product</TableHead>
                    <TableHead className="text-right">Qty</TableHead>
                    <TableHead className="text-right">Price</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {selectedSale.items?.map((item, i) => (
                    <TableRow key={i}>
                      <TableCell>{item.product_name}</TableCell>
                      <TableCell className="text-right">{item.quantity}</TableCell>
                      <TableCell className="text-right">₹{item.unit_price}</TableCell>
                      <TableCell className="text-right">₹{item.total_price?.toFixed(2)}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow className="bg-gray-50 font-bold">
                    <TableCell colSpan={3}>Total Amount</TableCell>
                    <TableCell className="text-right">₹{selectedSale.total_amount?.toLocaleString()}</TableCell>
                  </TableRow>
                  <TableRow className="bg-emerald-50 font-bold">
                    <TableCell colSpan={3}>Profit</TableCell>
                    <TableCell className="text-right text-emerald-700">₹{selectedSale.total_profit?.toLocaleString()}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
              {selectedSale.notes && (
                <div className="bg-yellow-50 p-3 rounded-lg">
                  <p className="text-sm text-gray-500">Notes</p>
                  <p>{selectedSale.notes}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Bill with QR Code Dialog */}
      <Dialog open={billDialogOpen} onOpenChange={setBillDialogOpen}>
        <DialogContent className="max-w-md" data-testid="bill-qr-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-emerald-600" />
              Bill Generated
            </DialogTitle>
            <DialogDescription>Scan QR code to pay or share bill via WhatsApp</DialogDescription>
          </DialogHeader>
          {generatedBill && (
            <div className="space-y-4">
              {/* Bill Header */}
              <div className="bg-gradient-to-r from-emerald-500 to-green-600 text-white p-4 rounded-lg text-center">
                <img 
                  src="https://customer-assets.emergentagent.com/job_ecf1a18d-b8c4-4d51-9a48-b9c5614b6399/artifacts/50hdzzeb_freshwala%20logo.jpg" 
                  alt="Freshwala Logo" 
                  className="w-16 h-16 mx-auto rounded-full border-2 border-white mb-2"
                />
                <h3 className="font-bold text-lg">Freshwala Dairy</h3>
                <p className="text-sm text-emerald-100">Fresh Dairy Delivered Daily</p>
              </div>

              {/* Customer & Date Info */}
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="bg-gray-50 p-2 rounded">
                  <p className="text-gray-500">Customer</p>
                  <p className="font-semibold">{generatedBill.customer_name}</p>
                </div>
                <div className="bg-gray-50 p-2 rounded">
                  <p className="text-gray-500">Date</p>
                  <p className="font-semibold">{generatedBill.sale_date}</p>
                </div>
              </div>

              {/* Items List */}
              <div className="border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead className="text-xs">Item</TableHead>
                      <TableHead className="text-xs text-center">Qty</TableHead>
                      <TableHead className="text-xs text-right">Price</TableHead>
                      <TableHead className="text-xs text-right">Total</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {generatedBill.items.map((item, i) => (
                      <TableRow key={i} className="text-sm">
                        <TableCell className="py-1">{item.product_name}</TableCell>
                        <TableCell className="py-1 text-center">{item.quantity}</TableCell>
                        <TableCell className="py-1 text-right">₹{item.unit_price}</TableCell>
                        <TableCell className="py-1 text-right">₹{item.total_price.toFixed(2)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <div className="bg-emerald-50 p-3 flex justify-between items-center border-t">
                  <span className="font-bold text-lg">Total Amount</span>
                  <span className="font-bold text-2xl text-emerald-600">₹{generatedBill.total_amount.toFixed(2)}</span>
                </div>
              </div>

              {/* Loyalty Points Earned */}
              {generatedBill.loyalty && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-center" data-testid="bill-loyalty-points">
                  <p className="font-semibold text-amber-700">🌟 +{generatedBill.loyalty.points_earned} loyalty points earned!</p>
                  <p className="text-xs text-amber-600">New balance: {generatedBill.loyalty.new_balance} points • {generatedBill.loyalty.tier} tier</p>
                </div>
              )}

              {/* UPI QR Code */}
              <div className="bg-gradient-to-b from-gray-50 to-white p-4 rounded-lg border-2 border-dashed border-emerald-300 text-center">
                <p className="text-sm font-semibold text-gray-700 mb-3">📱 Scan to Pay ₹{generatedBill.total_amount.toFixed(2)} via UPI</p>
                <div className="bg-white p-2 inline-block rounded-lg shadow">
                  <img src={CONFIG.PAYMENT_QR} alt="Freshwala Payment QR" className="w-56 mx-auto" data-testid="bill-payment-qr" />
                </div>
                <p className="text-xs text-gray-500 mt-2">UPI ID: {CONFIG.UPI_ID} | Paytm: {CONFIG.GPAY_NUMBER}</p>
                <p className="text-xs text-gray-400">GPay / PhonePe / Paytm - any UPI app</p>
              </div>

              {/* Action Buttons */}
              <div className="grid grid-cols-2 gap-2">
                <Button 
                  className="bg-green-600 hover:bg-green-700"
                  onClick={() => {
                    const itemsList = generatedBill.items.map(item => `• ${item.product_name} x${item.quantity} = ₹${item.total_price.toFixed(2)}`).join('\n');
                    const waMsg = `🧾 *${CONFIG.BUSINESS_NAME} - Bill*\n\n👤 Customer: ${generatedBill.customer_name}\n📅 Date: ${generatedBill.sale_date}\n\n📦 Items:\n${itemsList}\n\n💰 *Total: ₹${generatedBill.total_amount.toFixed(2)}*\n\n🏦 *Pay via UPI:*\n${CONFIG.UPI_ID}\n\n📞 Contact: ${CONFIG.ADMIN_PHONE_1}\n🙏 Thank you for shopping with Freshwala!`;
                    window.open(`https://wa.me/${CONFIG.ADMIN_WHATSAPP}?text=${encodeURIComponent(waMsg)}`, '_blank');
                  }}
                  data-testid="share-bill-whatsapp-btn"
                >
                  <MessageCircle className="w-4 h-4 mr-2" /> Share Bill
                </Button>
                <Button 
                  variant="outline"
                  className="border-emerald-500 text-emerald-600"
                  onClick={() => {
                    // Print the bill - build escaped HTML and open via blob URL (no document.write)
                    const esc = (s) => String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
                    const billHtml = `
                      <html>
                        <head>
                          <title>Freshwala Bill</title>
                          <style>
                            body { font-family: Arial, sans-serif; padding: 20px; max-width: 300px; margin: 0 auto; }
                            .header { text-align: center; margin-bottom: 20px; }
                            .header img { width: 60px; height: 60px; border-radius: 50%; }
                            table { width: 100%; border-collapse: collapse; margin: 15px 0; }
                            th, td { padding: 8px; text-align: left; border-bottom: 1px solid #ddd; }
                            .total { font-size: 18px; font-weight: bold; text-align: right; margin-top: 15px; }
                            .footer { text-align: center; margin-top: 20px; font-size: 12px; color: #666; }
                          </style>
                        </head>
                        <body>
                          <div class="header">
                            <img src="https://customer-assets.emergentagent.com/job_ecf1a18d-b8c4-4d51-9a48-b9c5614b6399/artifacts/50hdzzeb_freshwala%20logo.jpg" alt="Logo" />
                            <h2>Freshwala Dairy</h2>
                            <p>Fresh Dairy Delivered Daily</p>
                          </div>
                          <p><strong>Customer:</strong> ${esc(generatedBill.customer_name)}</p>
                          <p><strong>Date:</strong> ${esc(generatedBill.sale_date)}</p>
                          <table>
                            <tr><th>Item</th><th>Qty</th><th>Price</th><th>Total</th></tr>
                            ${generatedBill.items.map(item => `<tr><td>${esc(item.product_name)}</td><td>${esc(item.quantity)}</td><td>₹${esc(item.unit_price)}</td><td>₹${item.total_price.toFixed(2)}</td></tr>`).join('')}
                          </table>
                          <div class="total">Total: ₹${generatedBill.total_amount.toFixed(2)}</div>
                          <div class="footer">
                            <img src="${window.location.origin}${CONFIG.PAYMENT_QR}" alt="Payment QR" style="width: 180px; margin: 10px auto; display: block;" />
                            <p><strong>Scan to Pay via UPI</strong></p>
                            <p>UPI: ${CONFIG.UPI_ID}</p>
                            <p>Contact: ${CONFIG.ADMIN_PHONE_1}, ${CONFIG.ADMIN_PHONE_2}</p>
                            <p>Thank you for shopping with Freshwala!</p>
                          </div>
                        </body>
                      </html>
                    `;
                    const blob = new Blob([billHtml], { type: 'text/html' });
                    const blobUrl = URL.createObjectURL(blob);
                    const printWindow = window.open(blobUrl, '_blank', 'width=400,height=600');
                    if (!printWindow) { toast.error('Please allow popups to print the bill'); URL.revokeObjectURL(blobUrl); return; }
                    printWindow.onload = () => {
                      printWindow.print();
                      setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
                    };
                  }}
                  data-testid="print-bill-btn"
                >
                  <Printer className="w-4 h-4 mr-2" /> Print Bill
                </Button>
              </div>

              <Button 
                variant="ghost" 
                className="w-full text-gray-500" 
                onClick={() => setBillDialogOpen(false)}
              >
                Close
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
const ReportsPage = () => {
  const [activeTab, setActiveTab] = useState('sales');
  const [salesReport, setSalesReport] = useState(null);
  const [purchasesReport, setPurchasesReport] = useState(null);
  const [stockReport, setStockReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState({ start: '', end: '' });

  useEffect(() => { fetchReports(); }, []);

  const fetchReports = async () => {
    setLoading(true);
    try {
      const params = {};
      if (dateRange.start) params.start_date = dateRange.start;
      if (dateRange.end) params.end_date = dateRange.end;
      
      const [salesRes, purchasesRes, stockRes] = await Promise.all([
        axios.get(`${API}/reports/sales`, { params }),
        axios.get(`${API}/reports/purchases`, { params }),
        axios.get(`${API}/reports/stock`)
      ]);
      setSalesReport(salesRes.data);
      setPurchasesReport(purchasesRes.data);
      setStockReport(stockRes.data);
    } catch (error) { toast.error('Failed to load reports'); }
    finally { setLoading(false); }
  };

  const COLORS = ['#10b981', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div></div>;

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Reports</h1>
          <p className="text-gray-500">Analyze sales, purchases, and inventory data</p>
        </div>
        <div className="flex items-center gap-2">
          <Input type="date" value={dateRange.start} onChange={e => setDateRange({...dateRange, start: e.target.value})} className="w-40" />
          <span className="text-gray-400">to</span>
          <Input type="date" value={dateRange.end} onChange={e => setDateRange({...dateRange, end: e.target.value})} className="w-40" />
          <Button onClick={fetchReports} variant="outline">Apply Filter</Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b">
        {[
          { id: 'sales', label: 'Sales Report', icon: TrendingUp },
          { id: 'purchases', label: 'Purchases Report', icon: Truck },
          { id: 'stock', label: 'Stock Report', icon: Package }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 border-b-2 transition-colors ${
              activeTab === tab.id 
                ? 'border-emerald-600 text-emerald-600' 
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Sales Report */}
      {activeTab === 'sales' && salesReport && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="p-4">
                <p className="text-sm text-gray-500">Total Sales</p>
                <p className="text-2xl font-bold">{salesReport.total_sales}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <p className="text-sm text-gray-500">Total Revenue</p>
                <p className="text-2xl font-bold text-blue-600">₹{salesReport.total_revenue?.toLocaleString()}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <p className="text-sm text-gray-500">Total Profit</p>
                <p className="text-2xl font-bold text-emerald-600">₹{salesReport.total_profit?.toLocaleString()}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <p className="text-sm text-gray-500">Profit Margin</p>
                <p className="text-2xl font-bold text-purple-600">
                  {salesReport.total_revenue > 0 ? ((salesReport.total_profit / salesReport.total_revenue) * 100).toFixed(1) : 0}%
                </p>
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader><CardTitle>Daily Sales Trend</CardTitle></CardHeader>
              <CardContent>
                {salesReport.daily?.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={salesReport.daily}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" tickFormatter={d => d.slice(5)} />
                      <YAxis />
                      <Tooltip formatter={(v) => `₹${v.toLocaleString()}`} />
                      <Legend />
                      <Bar dataKey="revenue" fill="#3b82f6" name="Revenue" />
                      <Bar dataKey="profit" fill="#10b981" name="Profit" />
                    </BarChart>
                  </ResponsiveContainer>
                ) : <p className="text-center text-gray-400 py-8">No sales data available</p>}
              </CardContent>
            </Card>

            <Card>
              <CardHeader><CardTitle>Sales by Category</CardTitle></CardHeader>
              <CardContent>
                {salesReport.by_category?.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={salesReport.by_category}
                        dataKey="value"
                        nameKey="category"
                        cx="50%"
                        cy="50%"
                        outerRadius={100}
                        label={({ category, percent }) => `${category} ${(percent * 100).toFixed(0)}%`}
                      >
                        {salesReport.by_category.map((_, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(v) => `₹${v.toLocaleString()}`} />
                    </PieChart>
                  </ResponsiveContainer>
                ) : <p className="text-center text-gray-400 py-8">No category data available</p>}
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* Purchases Report */}
      {activeTab === 'purchases' && purchasesReport && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card>
              <CardContent className="p-4">
                <p className="text-sm text-gray-500">Total Purchases</p>
                <p className="text-2xl font-bold">{purchasesReport.total_purchases}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <p className="text-sm text-gray-500">Total Amount</p>
                <p className="text-2xl font-bold text-blue-600">₹{purchasesReport.total_amount?.toLocaleString()}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <p className="text-sm text-gray-500">Suppliers</p>
                <p className="text-2xl font-bold text-purple-600">{purchasesReport.by_supplier?.length || 0}</p>
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader><CardTitle>Daily Purchases</CardTitle></CardHeader>
              <CardContent>
                {purchasesReport.daily?.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={purchasesReport.daily}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" tickFormatter={d => d.slice(5)} />
                      <YAxis />
                      <Tooltip formatter={(v) => `₹${v.toLocaleString()}`} />
                      <Bar dataKey="amount" fill="#8b5cf6" name="Amount" />
                    </BarChart>
                  </ResponsiveContainer>
                ) : <p className="text-center text-gray-400 py-8">No purchase data available</p>}
              </CardContent>
            </Card>

            <Card>
              <CardHeader><CardTitle>Purchases by Supplier</CardTitle></CardHeader>
              <CardContent>
                {purchasesReport.by_supplier?.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={purchasesReport.by_supplier}
                        dataKey="value"
                        nameKey="supplier"
                        cx="50%"
                        cy="50%"
                        outerRadius={100}
                        label={({ supplier, percent }) => `${supplier} ${(percent * 100).toFixed(0)}%`}
                      >
                        {purchasesReport.by_supplier.map((_, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(v) => `₹${v.toLocaleString()}`} />
                    </PieChart>
                  </ResponsiveContainer>
                ) : <p className="text-center text-gray-400 py-8">No supplier data available</p>}
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* Stock Report */}
      {activeTab === 'stock' && stockReport && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card>
              <CardContent className="p-4">
                <p className="text-sm text-gray-500">Total Products</p>
                <p className="text-2xl font-bold">{stockReport.products?.length || 0}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <p className="text-sm text-gray-500">Stock Value</p>
                <p className="text-2xl font-bold text-emerald-600">₹{stockReport.total_value?.toLocaleString()}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <p className="text-sm text-gray-500">Low Stock Items</p>
                <p className="text-2xl font-bold text-red-600">{stockReport.products?.filter(p => p.low_stock).length || 0}</p>
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader><CardTitle>Stock Value by Category</CardTitle></CardHeader>
              <CardContent>
                {stockReport.by_category?.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={stockReport.by_category} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" />
                      <YAxis dataKey="category" type="category" width={80} />
                      <Tooltip formatter={(v) => `₹${v.toLocaleString()}`} />
                      <Bar dataKey="value" fill="#10b981" name="Value" />
                    </BarChart>
                  </ResponsiveContainer>
                ) : <p className="text-center text-gray-400 py-8">No stock data available</p>}
              </CardContent>
            </Card>

            <Card>
              <CardHeader><CardTitle>Stock Distribution</CardTitle></CardHeader>
              <CardContent>
                {stockReport.by_category?.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={stockReport.by_category}
                        dataKey="value"
                        nameKey="category"
                        cx="50%"
                        cy="50%"
                        outerRadius={100}
                        label={({ category, percent }) => `${category} ${(percent * 100).toFixed(0)}%`}
                      >
                        {stockReport.by_category.map((_, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(v) => `₹${v.toLocaleString()}`} />
                    </PieChart>
                  </ResponsiveContainer>
                ) : <p className="text-center text-gray-400 py-8">No category data available</p>}
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader><CardTitle>Product Stock Details</CardTitle></CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Product</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead className="text-right">Stock</TableHead>
                    <TableHead className="text-right">Value</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {stockReport.products?.map((product, index) => (
                    <TableRow key={index} className={product.low_stock ? 'bg-red-50' : ''}>
                      <TableCell className="font-medium">{product.name}</TableCell>
                      <TableCell>{product.category}</TableCell>
                      <TableCell className="text-right">{product.stock}</TableCell>
                      <TableCell className="text-right">₹{product.value?.toLocaleString()}</TableCell>
                      <TableCell>
                        {product.low_stock ? (
                          <Badge variant="destructive">Low Stock</Badge>
                        ) : (
                          <Badge variant="outline" className="text-emerald-600 border-emerald-600">In Stock</Badge>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

// Customer Analytics Page
const CustomerAnalyticsPage = () => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerDetail, setCustomerDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [segmentFilter, setSegmentFilter] = useState('all');
  const [activeTab, setActiveTab] = useState('analytics');
  const [leaderboard, setLeaderboard] = useState([]);
  const [loyaltyConfig, setLoyaltyConfig] = useState(null);
  const [exporting, setExporting] = useState(false);

  useEffect(() => { fetchAnalytics(); fetchLoyaltyData(); }, []);

  const fetchAnalytics = async () => {
    try {
      const response = await axios.get(`${API}/analytics/customers`);
      setAnalytics(response.data);
    } catch (error) {
      toast.error('Failed to load customer analytics');
    } finally {
      setLoading(false);
    }
  };

  const fetchLoyaltyData = async () => {
    try {
      const [configRes, leaderboardRes] = await Promise.all([
        axios.get(`${API}/loyalty/config`),
        axios.get(`${API}/loyalty/leaderboard`)
      ]);
      setLoyaltyConfig(configRes.data);
      setLeaderboard(leaderboardRes.data);
    } catch (error) {
      console.error('Failed to load loyalty data');
    }
  };

  const fetchCustomerDetail = async (customerId) => {
    setDetailLoading(true);
    try {
      const [analyticsRes, loyaltyRes] = await Promise.all([
        axios.get(`${API}/analytics/customer/${customerId}`),
        axios.get(`${API}/loyalty/customer/${customerId}`)
      ]);
      setCustomerDetail({
        ...analyticsRes.data,
        loyalty: loyaltyRes.data
      });
    } catch (error) {
      toast.error('Failed to load customer details');
    } finally {
      setDetailLoading(false);
    }
  };

  const handleAwardPoints = async (customerId, amount) => {
    try {
      const response = await axios.post(`${API}/loyalty/award-points`, {
        customer_id: customerId,
        amount: amount,
        description: 'Manual points award'
      });
      toast.success(response.data.message);
      fetchCustomerDetail(customerId);
      fetchLoyaltyData();
    } catch (error) {
      toast.error('Failed to award points');
    }
  };

  const handleRedeemPoints = async (customerId, points) => {
    try {
      const response = await axios.post(`${API}/loyalty/redeem-points`, {
        customer_id: customerId,
        points_to_redeem: points
      });
      toast.success(response.data.message);
      fetchCustomerDetail(customerId);
      fetchLoyaltyData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to redeem points');
    }
  };

  const handleExport = async (format) => {
    setExporting(true);
    try {
      const response = await axios.get(`${API}/export/customer-analytics?format=${format}`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `customer_analytics_${new Date().toISOString().split('T')[0]}.${format === 'excel' ? 'xlsx' : format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(`Exported as ${format.toUpperCase()}`);
    } catch (error) {
      toast.error('Export failed');
    } finally {
      setExporting(false);
    }
  };

  const handleCustomerClick = (customer) => {
    setSelectedCustomer(customer);
    fetchCustomerDetail(customer.id);
  };

  const segmentColors = {
    'Champion': 'bg-emerald-100 text-emerald-800 border-emerald-300',
    'Loyal': 'bg-blue-100 text-blue-800 border-blue-300',
    'Potential': 'bg-yellow-100 text-yellow-800 border-yellow-300',
    'At Risk': 'bg-orange-100 text-orange-800 border-orange-300',
    'Inactive': 'bg-red-100 text-red-800 border-red-300'
  };

  const tierColors = {
    'Bronze': { bg: 'bg-amber-100', text: 'text-amber-800', icon: '🥉' },
    'Silver': { bg: 'bg-gray-100', text: 'text-gray-700', icon: '🥈' },
    'Gold': { bg: 'bg-yellow-100', text: 'text-yellow-700', icon: '🥇' },
    'Platinum': { bg: 'bg-purple-100', text: 'text-purple-700', icon: '💎' }
  };

  const filteredCustomers = analytics?.customers?.filter(c => {
    const matchesSearch = c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         c.phone?.includes(searchTerm);
    const matchesSegment = segmentFilter === 'all' || c.segment === segmentFilter;
    return matchesSearch && matchesSegment;
  }) || [];

  if (loading) {
    return (
      <div className="p-6 flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="customer-analytics-page">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Customer Analytics</h1>
          <p className="text-gray-500">Track purchase trends, loyalty rewards, and preferences</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => handleExport('csv')}
            disabled={exporting}
            data-testid="export-csv-btn"
          >
            <Download className="w-4 h-4 mr-2" /> Export CSV
          </Button>
          <Button
            variant="outline"
            onClick={() => handleExport('excel')}
            disabled={exporting}
            data-testid="export-excel-btn"
          >
            <Download className="w-4 h-4 mr-2" /> Export Excel
          </Button>
          <Button
            variant="outline"
            onClick={() => handleExport('pdf')}
            disabled={exporting}
            data-testid="export-pdf-btn"
          >
            <FileText className="w-4 h-4 mr-2" /> Export PDF
          </Button>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-2 border-b pb-2">
        <Button
          variant={activeTab === 'analytics' ? 'default' : 'ghost'}
          onClick={() => setActiveTab('analytics')}
          className={activeTab === 'analytics' ? 'bg-emerald-600' : ''}
          data-testid="analytics-tab"
        >
          <TrendingUp className="w-4 h-4 mr-2" /> Analytics
        </Button>
        <Button
          variant={activeTab === 'loyalty' ? 'default' : 'ghost'}
          onClick={() => setActiveTab('loyalty')}
          className={activeTab === 'loyalty' ? 'bg-emerald-600' : ''}
          data-testid="loyalty-rewards-tab"
        >
          <Star className="w-4 h-4 mr-2" /> Loyalty Rewards
        </Button>
      </div>

      {activeTab === 'analytics' && (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <Card className="bg-gradient-to-br from-emerald-500 to-emerald-600 text-white">
              <CardContent className="p-4">
                <p className="text-emerald-100 text-sm">Total Customers</p>
                <p className="text-3xl font-bold">{analytics?.summary?.total_customers || 0}</p>
                <p className="text-emerald-200 text-xs">{analytics?.summary?.active_customers || 0} active</p>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
              <CardContent className="p-4">
                <p className="text-blue-100 text-sm">Total Revenue</p>
                <p className="text-3xl font-bold">₹{(analytics?.summary?.total_revenue || 0).toLocaleString()}</p>
                <p className="text-blue-200 text-xs">Lifetime value</p>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white">
              <CardContent className="p-4">
                <p className="text-purple-100 text-sm">Avg. Customer Value</p>
                <p className="text-3xl font-bold">₹{(analytics?.summary?.avg_customer_value || 0).toLocaleString()}</p>
                <p className="text-purple-200 text-xs">Per customer</p>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-orange-500 to-orange-600 text-white">
              <CardContent className="p-4">
                <p className="text-orange-100 text-sm">Pending Amount</p>
                <p className="text-3xl font-bold">₹{(analytics?.summary?.total_pending || 0).toLocaleString()}</p>
                <p className="text-orange-200 text-xs">To collect</p>
              </CardContent>
            </Card>
        <Card className="bg-gradient-to-br from-pink-500 to-pink-600 text-white">
          <CardContent className="p-4">
            <p className="text-pink-100 text-sm">Champions</p>
            <p className="text-3xl font-bold">{analytics?.segments?.find(s => s.name === 'Champion')?.count || 0}</p>
            <p className="text-pink-200 text-xs">Top customers</p>
          </CardContent>
        </Card>
      </div>

      {/* Customer Segments Chart */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-1">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-emerald-600" />
              Customer Segments
            </CardTitle>
            <CardDescription>Distribution by engagement level</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {analytics?.segments?.map(segment => {
                const percentage = Math.round((segment.count / (analytics?.summary?.total_customers || 1)) * 100);
                return (
                  <div key={segment.name} className="space-y-1">
                    <div className="flex justify-between text-sm">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${segmentColors[segment.name] || 'bg-gray-100 text-gray-800'}`}>
                        {segment.name}
                      </span>
                      <span className="text-gray-600">{segment.count} ({percentage}%)</span>
                    </div>
                    <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div 
                        className={`h-full ${segment.name === 'Champion' ? 'bg-emerald-500' : 
                                             segment.name === 'Loyal' ? 'bg-blue-500' :
                                             segment.name === 'Potential' ? 'bg-yellow-500' :
                                             segment.name === 'At Risk' ? 'bg-orange-500' : 'bg-red-500'}`}
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
            
            <div className="mt-6 pt-4 border-t">
              <h4 className="font-medium text-gray-700 mb-2">Segment Guide</h4>
              <div className="space-y-1 text-xs text-gray-500">
                <p><span className="font-medium text-emerald-600">Champion:</span> High value, recent orders</p>
                <p><span className="font-medium text-blue-600">Loyal:</span> Regular customers</p>
                <p><span className="font-medium text-yellow-600">Potential:</span> Growing engagement</p>
                <p><span className="font-medium text-orange-600">At Risk:</span> Declining activity</p>
                <p><span className="font-medium text-red-600">Inactive:</span> No recent orders</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Customer List */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Customer Details</CardTitle>
            <div className="flex flex-col sm:flex-row gap-2 mt-2">
              <Input
                placeholder="Search by name or phone..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="max-w-xs"
                data-testid="customer-search-input"
              />
              <Select value={segmentFilter} onValueChange={setSegmentFilter}>
                <SelectTrigger className="w-40">
                  <SelectValue placeholder="All Segments" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Segments</SelectItem>
                  <SelectItem value="Champion">Champion</SelectItem>
                  <SelectItem value="Loyal">Loyal</SelectItem>
                  <SelectItem value="Potential">Potential</SelectItem>
                  <SelectItem value="At Risk">At Risk</SelectItem>
                  <SelectItem value="Inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardHeader>
          <CardContent>
            <div className="max-h-[500px] overflow-y-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Customer</TableHead>
                    <TableHead>Segment</TableHead>
                    <TableHead className="text-right">Total Spent</TableHead>
                    <TableHead className="text-right">Pending</TableHead>
                    <TableHead className="text-center">Orders</TableHead>
                    <TableHead>Favorite</TableHead>
                    <TableHead className="text-center">Score</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredCustomers.slice(0, 50).map(customer => (
                    <TableRow 
                      key={customer.id} 
                      className="cursor-pointer hover:bg-gray-50"
                      onClick={() => handleCustomerClick(customer)}
                      data-testid={`customer-row-${customer.id}`}
                    >
                      <TableCell>
                        <div>
                          <p className="font-medium">{customer.name}</p>
                          <p className="text-xs text-gray-500">{customer.phone}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={segmentColors[customer.segment]}>{customer.segment}</Badge>
                      </TableCell>
                      <TableCell className="text-right font-medium text-emerald-600">
                        ₹{customer.total_spent.toLocaleString()}
                      </TableCell>
                      <TableCell className="text-right">
                        {customer.pending_amount > 0 ? (
                          <span className="text-red-600 font-medium">₹{customer.pending_amount.toLocaleString()}</span>
                        ) : (
                          <span className="text-green-600">Paid</span>
                        )}
                      </TableCell>
                      <TableCell className="text-center">{customer.total_orders}</TableCell>
                      <TableCell>
                        <span className="text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded">
                          {customer.favorite_product}
                        </span>
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="flex items-center justify-center">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold
                            ${customer.retention_score >= 80 ? 'bg-emerald-100 text-emerald-700' :
                              customer.retention_score >= 60 ? 'bg-blue-100 text-blue-700' :
                              customer.retention_score >= 40 ? 'bg-yellow-100 text-yellow-700' :
                              customer.retention_score >= 20 ? 'bg-orange-100 text-orange-700' :
                              'bg-red-100 text-red-700'}`}>
                            {customer.retention_score}
                          </div>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {filteredCustomers.length > 50 && (
                <p className="text-center text-sm text-gray-500 py-2">
                  Showing 50 of {filteredCustomers.length} customers
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
        </>
      )}

      {/* Loyalty Rewards Tab */}
      {activeTab === 'loyalty' && (
        <div className="space-y-6">
          {/* Loyalty Program Info */}
          <Card className="bg-gradient-to-r from-amber-50 to-yellow-50 border-amber-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-amber-800">
                <Star className="w-6 h-6" /> Freshwala Loyalty Rewards Program
              </CardTitle>
              <CardDescription>Earn points on every purchase, unlock rewards and discounts!</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {loyaltyConfig && Object.entries(loyaltyConfig.tiers).map(([tier, config]) => (
                  <div key={tier} className={`p-4 rounded-lg text-center ${tierColors[tier]?.bg || 'bg-gray-100'}`}>
                    <span className="text-3xl">{tierColors[tier]?.icon}</span>
                    <h4 className={`font-bold ${tierColors[tier]?.text || 'text-gray-700'}`}>{tier}</h4>
                    <p className="text-xs text-gray-600">{config.min_points}+ points</p>
                    <p className="font-semibold text-green-600">{config.discount_percent}% off</p>
                  </div>
                ))}
              </div>
              <div className="mt-4 p-3 bg-white rounded-lg border border-amber-200">
                <h4 className="font-medium text-amber-800 mb-2">How it works:</h4>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>✨ Earn <strong>1 point for every ₹10</strong> spent</li>
                  <li>🎁 Get <strong>50 bonus points</strong> for ordering every month</li>
                  <li>💰 Redeem <strong>10 points = ₹1</strong> discount</li>
                  <li>🏆 Higher tiers unlock bigger discounts!</li>
                </ul>
              </div>
            </CardContent>
          </Card>

          {/* Leaderboard */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-emerald-600" /> Loyalty Leaderboard
              </CardTitle>
              <CardDescription>Top customers by lifetime points</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12">Rank</TableHead>
                    <TableHead>Customer</TableHead>
                    <TableHead className="text-center">Tier</TableHead>
                    <TableHead className="text-right">Current Points</TableHead>
                    <TableHead className="text-right">Lifetime Points</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {leaderboard.map((customer, i) => (
                    <TableRow key={customer.customer_id} className="cursor-pointer hover:bg-gray-50" onClick={() => handleCustomerClick({id: customer.customer_id, name: customer.name, phone: customer.phone})}>
                      <TableCell className="font-bold">
                        {i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : `#${i + 1}`}
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium">{customer.name}</p>
                          <p className="text-xs text-gray-500">{customer.phone}</p>
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge className={`${tierColors[customer.tier]?.bg} ${tierColors[customer.tier]?.text}`}>
                          {tierColors[customer.tier]?.icon} {customer.tier}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right font-medium text-amber-600">
                        {customer.current_points.toLocaleString()}
                      </TableCell>
                      <TableCell className="text-right font-bold text-emerald-600">
                        {customer.lifetime_points.toLocaleString()}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {leaderboard.length === 0 && (
                <p className="text-center text-gray-500 py-8">No loyalty data yet. Points will appear as customers make purchases.</p>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Customer Detail Dialog */}
      <Dialog open={!!selectedCustomer} onOpenChange={() => { setSelectedCustomer(null); setCustomerDetail(null); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <User className="w-5 h-5 text-emerald-600" />
              {selectedCustomer?.name}
            </DialogTitle>
            <DialogDescription>
              <Badge className={segmentColors[selectedCustomer?.segment]}>{selectedCustomer?.segment}</Badge>
              <span className="ml-2">{selectedCustomer?.phone}</span>
            </DialogDescription>
          </DialogHeader>
          
          {detailLoading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
            </div>
          ) : customerDetail && (
            <div className="space-y-6">
              {/* Metrics */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="bg-emerald-50 p-3 rounded-lg text-center">
                  <p className="text-xs text-emerald-600">Total Spent</p>
                  <p className="text-xl font-bold text-emerald-700">₹{customerDetail.metrics.total_spent.toLocaleString()}</p>
                </div>
                <div className="bg-blue-50 p-3 rounded-lg text-center">
                  <p className="text-xs text-blue-600">Total Paid</p>
                  <p className="text-xl font-bold text-blue-700">₹{customerDetail.metrics.total_paid.toLocaleString()}</p>
                </div>
                <div className="bg-orange-50 p-3 rounded-lg text-center">
                  <p className="text-xs text-orange-600">Pending</p>
                  <p className="text-xl font-bold text-orange-700">₹{customerDetail.metrics.pending_amount.toLocaleString()}</p>
                </div>
                <div className="bg-purple-50 p-3 rounded-lg text-center">
                  <p className="text-xs text-purple-600">Avg. Order</p>
                  <p className="text-xl font-bold text-purple-700">₹{customerDetail.metrics.avg_order_value.toLocaleString()}</p>
                </div>
              </div>

              {/* Consumption Stats */}
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-gray-50 p-3 rounded-lg text-center">
                  <p className="text-xs text-gray-600">Total Deliveries</p>
                  <p className="text-2xl font-bold text-gray-700">{customerDetail.metrics.total_deliveries}</p>
                </div>
                <div className="bg-amber-50 p-3 rounded-lg text-center">
                  <p className="text-xs text-amber-600">Buffalo Milk</p>
                  <p className="text-2xl font-bold text-amber-700">{customerDetail.metrics.buffalo_total}L</p>
                </div>
                <div className="bg-cyan-50 p-3 rounded-lg text-center">
                  <p className="text-xs text-cyan-600">Cow Milk</p>
                  <p className="text-2xl font-bold text-cyan-700">{customerDetail.metrics.cow_total}L</p>
                </div>
              </div>

              {/* Loyalty Points Section */}
              {customerDetail.loyalty && (
                <div className="bg-gradient-to-r from-amber-50 to-yellow-50 p-4 rounded-lg border border-amber-200">
                  <h4 className="font-medium text-amber-800 mb-3 flex items-center gap-2">
                    <Star className="w-5 h-5" /> Loyalty Rewards
                  </h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div className="text-center">
                      <p className="text-xs text-amber-600">Current Points</p>
                      <p className="text-2xl font-bold text-amber-700">{customerDetail.loyalty.current_points}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-xs text-amber-600">Lifetime Points</p>
                      <p className="text-2xl font-bold text-amber-700">{customerDetail.loyalty.lifetime_points}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-xs text-amber-600">Tier</p>
                      <p className="text-xl font-bold" style={{color: customerDetail.loyalty.tier_color}}>
                        {tierColors[customerDetail.loyalty.tier]?.icon} {customerDetail.loyalty.tier}
                      </p>
                    </div>
                    <div className="text-center">
                      <p className="text-xs text-amber-600">Redemption Value</p>
                      <p className="text-2xl font-bold text-green-600">₹{customerDetail.loyalty.redemption_value}</p>
                    </div>
                  </div>
                  {customerDetail.loyalty.next_tier && (
                    <div className="mt-3 text-center">
                      <p className="text-xs text-amber-600">
                        {customerDetail.loyalty.points_to_next_tier} more points to reach {customerDetail.loyalty.next_tier}!
                      </p>
                      <div className="w-full bg-amber-200 rounded-full h-2 mt-1">
                        <div 
                          className="bg-amber-500 h-2 rounded-full" 
                          style={{width: `${Math.min(100, (customerDetail.loyalty.lifetime_points / (customerDetail.loyalty.lifetime_points + customerDetail.loyalty.points_to_next_tier)) * 100)}%`}}
                        />
                      </div>
                    </div>
                  )}
                  <div className="flex gap-2 mt-3">
                    <Button
                      size="sm"
                      className="bg-amber-500 hover:bg-amber-600"
                      onClick={() => {
                        const amount = prompt('Enter purchase amount to award points:');
                        if (amount && !isNaN(amount)) {
                          handleAwardPoints(customerDetail.customer.id, parseFloat(amount));
                        }
                      }}
                    >
                      <Plus className="w-3 h-3 mr-1" /> Award Points
                    </Button>
                    {customerDetail.loyalty.current_points >= 10 && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-amber-500 text-amber-600"
                        onClick={() => {
                          const points = prompt(`Enter points to redeem (max ${customerDetail.loyalty.current_points}):`);
                          if (points && !isNaN(points)) {
                            handleRedeemPoints(customerDetail.customer.id, parseInt(points));
                          }
                        }}
                      >
                        Redeem Points
                      </Button>
                    )}
                  </div>
                </div>
              )}

              {/* Monthly History */}
              {customerDetail.monthly_history?.length > 0 && (
                <div>
                  <h4 className="font-medium text-gray-700 mb-2">Monthly History</h4>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {customerDetail.monthly_history.slice(0, 12).map(month => (
                      <div key={month.month} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                        <div>
                          <span className="font-medium">{month.month}</span>
                          <span className="text-xs text-gray-500 ml-2">({month.deliveries} deliveries)</span>
                        </div>
                        <div className="flex items-center gap-4">
                          <span className="text-sm">
                            🐃 {month.buffalo_total || 0}L | 🐄 {month.cow_total || 0}L
                          </span>
                          <span className="font-bold text-emerald-600">₹{month.amount.toLocaleString()}</span>
                          <Badge className={month.status === 'paid' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                            {month.status}
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex gap-2 pt-4 border-t">
                <Button
                  className="bg-green-600 hover:bg-green-700"
                  onClick={() => {
                    const phone = customerDetail.customer?.phone?.replace(/[^0-9]/g, '') || '';
                    const waPhone = phone.startsWith('91') ? phone : `91${phone}`;
                    const msg = `Hello ${customerDetail.customer?.name}! 👋\n\nThank you for being a valued Freshwala customer!\n\n📊 Your account summary:\n💰 Total purchases: ₹${customerDetail.metrics.total_spent.toLocaleString()}\n📦 Total deliveries: ${customerDetail.metrics.total_deliveries}\n\nWe appreciate your loyalty! 🙏\n- Freshwala Dairy`;
                    window.open(`https://wa.me/${waPhone}?text=${encodeURIComponent(msg)}`, '_blank');
                  }}
                >
                  <MessageCircle className="w-4 h-4 mr-2" /> Send Thank You
                </Button>
                {customerDetail.metrics.pending_amount > 0 && (
                  <Button
                    variant="outline"
                    className="border-orange-500 text-orange-600"
                    onClick={() => {
                      const phone = customerDetail.customer?.phone?.replace(/[^0-9]/g, '') || '';
                      const waPhone = phone.startsWith('91') ? phone : `91${phone}`;
                      const msg = `🔔 *Payment Reminder*\n\nNamaste ${customerDetail.customer?.name} ji,\n\nYour pending bill amount is *₹${customerDetail.metrics.pending_amount.toLocaleString()}*\n\n🏦 Pay via UPI: ${CONFIG.UPI_ID}\n📱 GPay: ${CONFIG.GPAY_NUMBER}\n📷 Scan QR: ${BACKEND_URL}${CONFIG.PAYMENT_QR}\n\nThank you! 🙏\n- Freshwala Dairy`;
                      window.open(`https://wa.me/${waPhone}?text=${encodeURIComponent(msg)}`, '_blank');
                    }}
                  >
                    <Bell className="w-4 h-4 mr-2" /> Send Reminder
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

const AlertsPage = () => {
  const [alerts, setAlerts] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showResolved, setShowResolved] = useState(false);

  useEffect(() => { fetchData(); }, [showResolved]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [alertsRes, productsRes] = await Promise.all([
        axios.get(`${API}/alerts`, { params: { resolved: showResolved ? undefined : false } }),
        axios.get(`${API}/products`)
      ]);
      setAlerts(alertsRes.data);
      setProducts(productsRes.data);
    } catch (error) { toast.error('Failed to load data'); }
    finally { setLoading(false); }
  };

  const resolveAlert = async (alertId) => {
    try {
      await axios.put(`${API}/alerts/${alertId}/resolve`);
      toast.success('Alert resolved');
      fetchData();
    } catch (error) { toast.error('Failed to resolve alert'); }
  };

  const lowStockProducts = products.filter(p => p.stock_quantity <= p.low_stock_threshold);
  const criticalProducts = lowStockProducts.filter(p => p.stock_quantity === 0);
  const warningProducts = lowStockProducts.filter(p => p.stock_quantity > 0);

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div></div>;

  return (
    <div className="space-y-6" data-testid="alerts-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Low Stock Alerts</h1>
          <p className="text-gray-500">Monitor and manage inventory levels</p>
        </div>
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2 text-sm text-gray-600">
            <input 
              type="checkbox" 
              checked={showResolved} 
              onChange={e => setShowResolved(e.target.checked)}
              className="rounded border-gray-300"
            />
            Show resolved alerts
          </label>
          <Button onClick={fetchData} variant="outline" size="sm">
            <Bell className="w-4 h-4 mr-2" /> Refresh
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className={criticalProducts.length > 0 ? 'border-red-300 bg-red-50' : ''}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Out of Stock</p>
                <p className="text-2xl font-bold text-red-600">{criticalProducts.length}</p>
              </div>
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className={warningProducts.length > 0 ? 'border-yellow-300 bg-yellow-50' : ''}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Low Stock</p>
                <p className="text-2xl font-bold text-yellow-600">{warningProducts.length}</p>
              </div>
              <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center">
                <Package className="w-6 h-6 text-yellow-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Active Alerts</p>
                <p className="text-2xl font-bold">{alerts.filter(a => !a.resolved).length}</p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                <Bell className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Products</p>
                <p className="text-2xl font-bold text-emerald-600">{products.length}</p>
              </div>
              <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center">
                <Package className="w-6 h-6 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Critical - Out of Stock */}
      {criticalProducts.length > 0 && (
        <Card className="border-red-300">
          <CardHeader className="bg-red-50 border-b border-red-200">
            <CardTitle className="text-red-700 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" />
              Out of Stock - Immediate Action Required
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Product</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead className="text-right">Current Stock</TableHead>
                  <TableHead className="text-right">Threshold</TableHead>
                  <TableHead>Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {criticalProducts.map(product => (
                  <TableRow key={product.id} className="bg-red-50">
                    <TableCell className="font-medium">{product.name}</TableCell>
                    <TableCell><Badge variant="outline">{product.category}</Badge></TableCell>
                    <TableCell className="text-right">
                      <Badge variant="destructive">0 {product.unit}</Badge>
                    </TableCell>
                    <TableCell className="text-right">{product.low_stock_threshold} {product.unit}</TableCell>
                    <TableCell>
                      <Link to="/purchases">
                        <Button size="sm" variant="destructive">
                          <Plus className="w-4 h-4 mr-1" /> Order Now
                        </Button>
                      </Link>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Warning - Low Stock */}
      {warningProducts.length > 0 && (
        <Card className="border-yellow-300">
          <CardHeader className="bg-yellow-50 border-b border-yellow-200">
            <CardTitle className="text-yellow-700 flex items-center gap-2">
              <Package className="w-5 h-5" />
              Low Stock Warning
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Product</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead className="text-right">Current Stock</TableHead>
                  <TableHead className="text-right">Threshold</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {warningProducts.map(product => {
                  const percentage = (product.stock_quantity / product.low_stock_threshold) * 100;
                  return (
                    <TableRow key={product.id} className="bg-yellow-50/50">
                      <TableCell className="font-medium">{product.name}</TableCell>
                      <TableCell><Badge variant="outline">{product.category}</Badge></TableCell>
                      <TableCell className="text-right">
                        <span className="text-yellow-700 font-semibold">{product.stock_quantity} {product.unit}</span>
                      </TableCell>
                      <TableCell className="text-right">{product.low_stock_threshold} {product.unit}</TableCell>
                      <TableCell>
                        <div className="w-24 bg-gray-200 rounded-full h-2">
                          <div 
                            className={`h-2 rounded-full ${percentage <= 50 ? 'bg-red-500' : 'bg-yellow-500'}`}
                            style={{ width: `${Math.min(percentage, 100)}%` }}
                          ></div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Link to="/purchases">
                          <Button size="sm" variant="outline" className="text-yellow-700 border-yellow-500 hover:bg-yellow-100">
                            <Plus className="w-4 h-4 mr-1" /> Restock
                          </Button>
                        </Link>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* All Good Message */}
      {lowStockProducts.length === 0 && (
        <Card className="border-emerald-300 bg-emerald-50">
          <CardContent className="p-8 text-center">
            <CheckCircle className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-emerald-700 mb-2">All Stock Levels OK!</h3>
            <p className="text-emerald-600">All products are above their minimum stock thresholds.</p>
          </CardContent>
        </Card>
      )}

      {/* Alert History */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bell className="w-5 h-5" />
            Alert History
          </CardTitle>
        </CardHeader>
        <CardContent>
          {alerts.length === 0 ? (
            <div className="text-center py-8">
              <Bell className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">No alerts to display</p>
              <p className="text-sm text-gray-400">Alerts are automatically created when stock falls below threshold</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Product</TableHead>
                  <TableHead className="text-right">Stock Level</TableHead>
                  <TableHead className="text-right">Threshold</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {alerts.map(alert => (
                  <TableRow key={alert.id} className={alert.resolved ? 'bg-gray-50 opacity-60' : ''}>
                    <TableCell>{new Date(alert.created_at).toLocaleDateString('en-IN')}</TableCell>
                    <TableCell className="font-medium">{alert.product_name}</TableCell>
                    <TableCell className="text-right">
                      <Badge variant={alert.current_stock === 0 ? 'destructive' : 'secondary'}>
                        {alert.current_stock}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">{alert.threshold}</TableCell>
                    <TableCell>
                      {alert.resolved ? (
                        <Badge variant="outline" className="text-emerald-600 border-emerald-600">
                          <CheckCircle className="w-3 h-3 mr-1" /> Resolved
                        </Badge>
                      ) : (
                        <Badge variant="destructive">Active</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      {!alert.resolved && (
                        <Button size="sm" variant="ghost" onClick={() => resolveAlert(alert.id)}>
                          <CheckCircle className="w-4 h-4 mr-1" /> Mark Resolved
                        </Button>
                      )}
                      {alert.resolved && alert.resolved_at && (
                        <span className="text-xs text-gray-400">
                          {new Date(alert.resolved_at).toLocaleDateString('en-IN')}
                        </span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Quick Tips */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="p-4">
          <h4 className="font-semibold text-blue-800 mb-2">💡 Tips for Managing Stock</h4>
          <ul className="text-sm text-blue-700 space-y-1">
            <li>• Set appropriate thresholds in <Link to="/products" className="underline">Products</Link> based on your sales patterns</li>
            <li>• Record purchases in <Link to="/purchases" className="underline">Purchases</Link> to automatically update stock levels</li>
            <li>• Check <Link to="/reports" className="underline">Reports</Link> to analyze your inventory trends</li>
            <li>• Alerts are automatically created when stock falls below threshold</li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
};

// Customer Orders Page (Admin)
const CustomerOrdersPage = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editDialog, setEditDialog] = useState({ open: false, order: null });
  const [editFormData, setEditFormData] = useState({ items: [], total_amount: 0 });

  useEffect(() => { fetchOrders(); }, []);
  const fetchOrders = async () => { try { const res = await axios.get(`${API}/customer-orders`); setOrders(res.data); } catch(e) { toast.error('Failed'); } finally { setLoading(false); } };
  const updateStatus = async (id, status) => { try { await axios.put(`${API}/customer-orders/${id}/status?status=${status}`); toast.success('Updated!'); fetchOrders(); } catch(e) { toast.error('Failed'); } };
  const markPaid = async (id) => { try { await axios.put(`${API}/customer-orders/${id}/mark-paid`); toast.success('Payment marked as received!'); fetchOrders(); } catch(e) { toast.error('Failed to mark paid'); } };
  
  const deleteOrder = async (orderId) => {
    if (!window.confirm('⚠️ Delete this order?\n\nThis action cannot be undone.')) return;
    try {
      await axios.delete(`${API}/customer-orders/${orderId}`);
      toast.success('Order deleted!');
      fetchOrders();
    } catch(e) { toast.error('Failed to delete order'); }
  };

  const openEditDialog = (order) => {
    setEditFormData({
      items: order.items || [],
      total_amount: order.total_amount || 0,
      customer_name: order.customer_name,
      customer_phone: order.customer_phone
    });
    setEditDialog({ open: true, order });
  };

  const updateEditItem = (index, field, value) => {
    const newItems = [...editFormData.items];
    newItems[index] = { ...newItems[index], [field]: parseFloat(value) || value };
    // Recalculate total
    const newTotal = newItems.reduce((sum, item) => sum + ((item.quantity || 1) * (item.price || 0)), 0);
    setEditFormData({ ...editFormData, items: newItems, total_amount: newTotal });
  };

  const saveOrderEdit = async () => {
    try {
      await axios.put(`${API}/customer-orders/${editDialog.order.id}`, {
        items: editFormData.items,
        total_amount: editFormData.total_amount
      });
      toast.success('Order updated!');
      setEditDialog({ open: false, order: null });
      fetchOrders();
    } catch(e) { toast.error('Failed to update order'); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div></div>;

  return (
    <div className="space-y-6" data-testid="customer-orders-page">
      <h1 className="text-2xl font-bold text-gray-800">Customer Orders</h1>
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Order #</TableHead>
                <TableHead>Customer</TableHead>
                <TableHead>Products</TableHead>
                <TableHead className="text-right">Total</TableHead>
                <TableHead>Payment</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Update Status</TableHead>
                <TableHead className="text-center">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {orders.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8 text-gray-500">No orders yet</TableCell>
                </TableRow>
              ) : orders.map(order => (
                <TableRow key={order.id} data-testid={`order-row-${order.id}`}>
                  <TableCell className="font-mono text-sm">{order.order_number}</TableCell>
                  <TableCell>
                    <div>
                      <p className="font-medium">{order.customer_name}</p>
                      <p className="text-xs text-gray-500">{order.customer_phone}</p>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="space-y-1">
                      {order.items?.map((item, idx) => (
                        <div key={idx} className="flex items-center gap-2">
                          <Badge variant="outline" className="text-xs">
                            {item.product_name}
                          </Badge>
                          <span className="text-sm text-gray-600">
                            × {item.quantity} = ₹{((item.quantity || 1) * (item.price || 0)).toLocaleString()}
                          </span>
                        </div>
                      ))}
                    </div>
                  </TableCell>
                  <TableCell className="text-right font-semibold">₹{order.total_amount?.toLocaleString()}</TableCell>
                  <TableCell>
                    <Badge variant={order.payment_status === 'paid' ? 'default' : 'outline'} 
                           className={order.payment_status === 'paid' ? 'bg-emerald-600' : ''}>
                      {order.payment_status}
                    </Badge>
                    <p className="text-xs text-gray-400 mt-1">{order.payment_method === 'upi_qr' ? 'UPI QR' : order.payment_method}</p>
                    {order.payment_status !== 'paid' && (
                      <Button 
                        size="sm" 
                        className="mt-1 h-7 text-xs bg-emerald-600 hover:bg-emerald-700"
                        onClick={() => markPaid(order.id)}
                        data-testid={`mark-paid-${order.id}`}
                      >
                        ✓ Payment Received
                      </Button>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge className={
                      order.order_status === 'delivered' ? 'bg-emerald-600' :
                      order.order_status === 'cancelled' ? 'bg-red-500' :
                      order.order_status === 'out_for_delivery' ? 'bg-blue-500' :
                      'bg-yellow-500'
                    }>
                      {order.order_status}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Select value={order.order_status} onValueChange={(v) => updateStatus(order.id, v)}>
                      <SelectTrigger className="w-36"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {['placed','confirmed','preparing','out_for_delivery','delivered','cancelled'].map(s => (
                          <SelectItem key={s} value={s}>{s.replace('_', ' ')}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell className="text-center">
                    <div className="flex justify-center gap-1">
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => openEditDialog(order)}
                        title="Edit Order"
                        data-testid={`edit-order-${order.id}`}
                      >
                        <Edit className="w-4 h-4 text-blue-500" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => deleteOrder(order.id)}
                        title="Delete Order"
                        data-testid={`delete-order-${order.id}`}
                      >
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Edit Order Dialog */}
      <Dialog open={editDialog.open} onOpenChange={(open) => !open && setEditDialog({ open: false, order: null })}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="w-5 h-5 text-blue-500" />
              Edit Order #{editDialog.order?.order_number}
            </DialogTitle>
            <DialogDescription>
              Customer: {editFormData.customer_name} ({editFormData.customer_phone})
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <Label className="font-semibold">Order Items</Label>
            {editFormData.items?.map((item, idx) => (
              <div key={idx} className="grid grid-cols-3 gap-2 p-3 bg-gray-50 rounded-lg">
                <div>
                  <Label className="text-xs text-gray-500">Product</Label>
                  <p className="font-medium">{item.product_name}</p>
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Quantity</Label>
                  <Input 
                    type="number" 
                    value={item.quantity} 
                    onChange={(e) => updateEditItem(idx, 'quantity', e.target.value)}
                    className="h-8"
                  />
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Price (₹)</Label>
                  <Input 
                    type="number" 
                    value={item.price} 
                    onChange={(e) => updateEditItem(idx, 'price', e.target.value)}
                    className="h-8"
                  />
                </div>
              </div>
            ))}
            <div className="flex justify-between items-center pt-4 border-t">
              <span className="font-semibold">Total Amount:</span>
              <span className="text-xl font-bold text-emerald-600">₹{editFormData.total_amount?.toLocaleString()}</span>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialog({ open: false, order: null })}>Cancel</Button>
            <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={saveOrderEdit}>
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ==========================================
// SUBSCRIPTION MANAGEMENT PAGE (Admin)
// ==========================================
const SubscriptionManagePage = () => {
  const [activeTab, setActiveTab] = useState('customers');
  const [customers, setCustomers] = useState([]);
  const [deliveries, setDeliveries] = useState([]);
  const [bills, setBills] = useState([]);
  const [billFilter, setBillFilter] = useState('all'); // 'all', 'paid', 'pending'
  const [selectedBills, setSelectedBills] = useState([]); // For bulk selection
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7));
  const [paymentDialog, setPaymentDialog] = useState({ open: false, customer: null });
  const [invoiceDialog, setInvoiceDialog] = useState({ open: false, bill: null });
  const [billPaymentDialog, setBillPaymentDialog] = useState({ open: false, bill: null });
  const [billLoyalty, setBillLoyalty] = useState(null);
  const [editBillDialog, setEditBillDialog] = useState({ open: false, bill: null });
  const [editBillData, setEditBillData] = useState({ 
    total_deliveries: 0, 
    buffalo_total: 0,
    cow_total: 0,
    curd_total: 0,
    vijaya_toned_total: 0,
    vijaya_standard_total: 0,
    vijaya_gold_total: 0,
    final_amount: 0 
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterArea, setFilterArea] = useState('all');
  const [filterTime, setFilterTime] = useState('all');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [selectedCustomers, setSelectedCustomers] = useState([]);
  const [bulkDeliveryCount, setBulkDeliveryCount] = useState(null);
  const [bulkDeliveryDialog, setBulkDeliveryDialog] = useState(false);
  const [dateRangeFrom, setDateRangeFrom] = useState(new Date().toISOString().split('T')[0]);
  const [dateRangeTo, setDateRangeTo] = useState(new Date().toISOString().split('T')[0]);
  const [bulkProgress, setBulkProgress] = useState({ show: false, current: 0, total: 0, message: '' });
  const [bulkProductMode, setBulkProductMode] = useState('customer'); // 'customer' or 'custom'
  const [schedulerStatus, setSchedulerStatus] = useState({ enabled: false, next_run: null, recent_logs: [] });
  const [bulkProducts, setBulkProducts] = useState({
    buffalo: { enabled: true, qty: '0.5' },
    cow: { enabled: false, qty: '0.5' },
    curd: { enabled: false, qty: '0.5' },
    vijaya_toned: { enabled: false, qty: '0.5' },
    vijaya_standard: { enabled: false, qty: '0.5' },
    vijaya_gold: { enabled: false, qty: '0.5' }
  });
  const [formData, setFormData] = useState({
    name: '', phone: '', email: '', address: '', area: '',
    buffalo_qty: '0', cow_qty: '0', curd_qty: '0',
    vijaya_toned_qty: '0', vijaya_standard_qty: '0', vijaya_gold_qty: '0',
    buffalo_price: '100', cow_price: '100', curd_price: '120',
    vijaya_toned_price: '60', vijaya_standard_price: '64', vijaya_gold_price: '78',
    delivery_time: 'morning', delivery_frequency: 'daily', preferred_day: 6,
    start_date: new Date().toISOString().split('T')[0], notes: ''
  });
  const [editDeliveryDialog, setEditDeliveryDialog] = useState({ open: false, delivery: null });
  const [monthlyCustomerTotals, setMonthlyCustomerTotals] = useState([]);
  const [customerHistoryDialog, setCustomerHistoryDialog] = useState({ open: false, customerId: null, customerName: '', deliveries: [] });
  const [remindAllDialog, setRemindAllDialog] = useState(false);
  const [remindedBills, setRemindedBills] = useState([]);
  // Import Bills from Excel state
  const [importBillsDialog, setImportBillsDialog] = useState(false);
  const [importBillsFile, setImportBillsFile] = useState(null);
  const [importBillsResult, setImportBillsResult] = useState(null);
  const [importingBills, setImportingBills] = useState(false);

  // Import Bills from Excel function
  const importBillsFromExcel = async () => {
    if (!importBillsFile) {
      toast.error('Please select an Excel file');
      return;
    }
    setImportingBills(true);
    setImportBillsResult(null);
    
    const formData = new FormData();
    formData.append('file', importBillsFile);
    formData.append('month', selectedMonth);
    
    try {
      const response = await axios.post(`${API}/monthly-bills/import-excel`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setImportBillsResult({ success: true, message: `✅ ${response.data.message}` });
      toast.success('Bills imported successfully!');
      fetchData();
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to import bills';
      setImportBillsResult({ success: false, message: `❌ ${msg}` });
      toast.error(msg);
    } finally {
      setImportingBills(false);
    }
  };

  // Download editable bills sheet for the selected month
  const downloadBillsSheet = async (fmt) => {
    try {
      const res = await axios.get(`${API}/monthly-bills-export`, { params: { month: selectedMonth, format: fmt }, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `monthly_bills_${selectedMonth}.${fmt === 'csv' ? 'csv' : 'xlsx'}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Bills sheet downloaded - edit and import it back!');
    } catch (e) { toast.error('Download failed'); }
  };

  // Customer sheet import/export
  const [importCustomersOpen, setImportCustomersOpen] = useState(false);
  const [importCustomersFile, setImportCustomersFile] = useState(null);
  const [importingCustomers, setImportingCustomers] = useState(false);
  const [importCustomersResult, setImportCustomersResult] = useState(null);

  const downloadCustomersSheet = async (fmt) => {
    try {
      const res = await axios.get(`${API}/regular-customers-export`, { params: { format: fmt }, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `subscription_customers.${fmt === 'csv' ? 'csv' : 'xlsx'}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Customers sheet downloaded - edit and import it back!');
    } catch (e) { toast.error('Download failed'); }
  };

  const importCustomersFromSheet = async () => {
    if (!importCustomersFile) { toast.error('Please select a file'); return; }
    setImportingCustomers(true);
    setImportCustomersResult(null);
    try {
      const fd = new FormData();
      fd.append('file', importCustomersFile);
      const res = await axios.post(`${API}/regular-customers/import-excel`, fd);
      setImportCustomersResult(res.data);
      toast.success(res.data.message);
      setImportCustomersFile(null);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Import failed'); }
    setImportingCustomers(false);
  };

  const bulkDeleteSelected = async () => {
    if (selectedCustomers.length === 0) { toast.error('Select customers first'); return; }
    if (!window.confirm(`⚠️ Delete ${selectedCustomers.length} selected customer(s) permanently?\n\nThis cannot be undone.`)) return;
    let ok = 0, fail = 0;
    for (const id of selectedCustomers) {
      try { await axios.delete(`${API}/regular-customers/${id}`); ok++; } catch (e) { fail++; }
    }
    toast.success(`Deleted ${ok} customer(s)${fail ? `, ${fail} failed` : ''}`);
    setSelectedCustomers([]);
    fetchData();
  };

  useEffect(() => { fetchData(); }, [activeTab, selectedDate, selectedMonth]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [custRes, summaryRes] = await Promise.all([
        axios.get(`${API}/regular-customers`),
        axios.get(`${API}/regular-customers/summary`)
      ]);
      setCustomers(custRes.data);
      setSummary(summaryRes.data);
      
      if (activeTab === 'deliveries') {
        const delRes = await axios.get(`${API}/daily-deliveries?date=${selectedDate}`);
        setDeliveries(delRes.data);
        // Also fetch monthly totals for context
        const monthFromDate = selectedDate.slice(0, 7);
        const allMonthDeliveries = await axios.get(`${API}/daily-deliveries?month=${monthFromDate}`);
        // Calculate per-customer monthly totals
        const customerTotals = {};
        allMonthDeliveries.data.forEach(d => {
          if (!customerTotals[d.customer_id]) {
            customerTotals[d.customer_id] = { 
              name: d.customer_name, 
              id: d.customer_id,
              days: 0, 
              buffalo: 0, 
              cow: 0, 
              curd: 0,
              vijaya_toned: 0,
              vijaya_standard: 0,
              vijaya_gold: 0,
              amount: 0 
            };
          }
          customerTotals[d.customer_id].days++;
          customerTotals[d.customer_id].buffalo += d.buffalo_qty || 0;
          customerTotals[d.customer_id].cow += d.cow_qty || 0;
          customerTotals[d.customer_id].curd += d.curd_qty || 0;
          customerTotals[d.customer_id].vijaya_toned += d.vijaya_toned_qty || 0;
          customerTotals[d.customer_id].vijaya_standard += d.vijaya_standard_qty || 0;
          customerTotals[d.customer_id].vijaya_gold += d.vijaya_gold_qty || 0;
          customerTotals[d.customer_id].amount += d.total_amount || 0;
        });
        setMonthlyCustomerTotals(Object.values(customerTotals).sort((a, b) => b.amount - a.amount));
      }
      if (activeTab === 'bills') {
        const billRes = await axios.get(`${API}/monthly-bills?month=${selectedMonth}`);
        setBills(billRes.data);
      }
      // Fetch scheduler status
      try {
        const schedRes = await axios.get(`${API}/scheduler/status`);
        setSchedulerStatus(schedRes.data);
      } catch (e) { /* scheduler status is optional */ }
    } catch (error) { toast.error('Failed to load data'); }
    finally { setLoading(false); }
  };

  // Toggle scheduler on/off
  const toggleScheduler = async () => {
    try {
      const res = await axios.post(`${API}/scheduler/toggle`);
      toast.success(res.data.message);
      setSchedulerStatus(prev => ({ ...prev, enabled: res.data.enabled }));
    } catch (error) { toast.error('Failed to toggle scheduler'); }
  };

  // Run scheduler manually
  const runSchedulerNow = async () => {
    try {
      const res = await axios.post(`${API}/scheduler/run-now`);
      toast.success(res.data.message);
      fetchData();
    } catch (error) { toast.error('Failed to run scheduler'); }
  };

  // View all deliveries for a specific customer in the month
  const viewCustomerHistory = async (customerId, customerName) => {
    try {
      const monthFromDate = selectedDate.slice(0, 7);
      const res = await axios.get(`${API}/daily-deliveries?customer_id=${customerId}&month=${monthFromDate}`);
      const sortedDeliveries = res.data.sort((a, b) => a.date.localeCompare(b.date));
      setCustomerHistoryDialog({ open: true, customerId, customerName, deliveries: sortedDeliveries });
    } catch (error) {
      toast.error('Failed to load customer history');
    }
  };

  // Update a delivery from customer history view
  const updateDeliveryInHistory = async (deliveryId, newQty, field) => {
    const delivery = customerHistoryDialog.deliveries.find(d => d.id === deliveryId);
    if (!delivery) return;
    
    try {
      const params = new URLSearchParams({
        buffalo_qty: field === 'buffalo' ? newQty : (delivery.buffalo_qty || 0),
        cow_qty: field === 'cow' ? newQty : (delivery.cow_qty || 0),
        curd_qty: field === 'curd' ? newQty : (delivery.curd_qty || 0),
        vijaya_toned_qty: field === 'vijaya_toned' ? newQty : (delivery.vijaya_toned_qty || 0),
        vijaya_standard_qty: field === 'vijaya_standard' ? newQty : (delivery.vijaya_standard_qty || 0),
        vijaya_gold_qty: field === 'vijaya_gold' ? newQty : (delivery.vijaya_gold_qty || 0),
      });
      
      await axios.put(`${API}/daily-deliveries/${deliveryId}?${params.toString()}`);
      
      // Refresh the history
      viewCustomerHistory(customerHistoryDialog.customerId, customerHistoryDialog.customerName);
      fetchData();
      toast.success('Updated!');
    } catch (error) {
      toast.error('Failed to update');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = { 
        ...formData, 
        buffalo_qty: parseFloat(formData.buffalo_qty) || 0, 
        cow_qty: parseFloat(formData.cow_qty) || 0, 
        curd_qty: parseFloat(formData.curd_qty) || 0,
        vijaya_toned_qty: parseFloat(formData.vijaya_toned_qty) || 0,
        vijaya_standard_qty: parseFloat(formData.vijaya_standard_qty) || 0,
        vijaya_gold_qty: parseFloat(formData.vijaya_gold_qty) || 0,
        buffalo_price: parseFloat(formData.buffalo_price) || 100,
        cow_price: parseFloat(formData.cow_price) || 100,
        curd_price: parseFloat(formData.curd_price) || 120,
        vijaya_toned_price: parseFloat(formData.vijaya_toned_price) || 60,
        vijaya_standard_price: parseFloat(formData.vijaya_standard_price) || 64,
        vijaya_gold_price: parseFloat(formData.vijaya_gold_price) || 78
      };
      if (editingCustomer) {
        await axios.put(`${API}/regular-customers/${editingCustomer.id}`, data);
        toast.success('Customer updated!');
      } else {
        await axios.post(`${API}/regular-customers`, data);
        toast.success('Customer added!');
      }
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to save'); }
  };

  const resetForm = () => {
    setFormData({ name: '', phone: '', email: '', address: '', area: '', buffalo_qty: '0', cow_qty: '0', curd_qty: '0', vijaya_toned_qty: '0', vijaya_standard_qty: '0', vijaya_gold_qty: '0', buffalo_price: '100', cow_price: '100', curd_price: '120', vijaya_toned_price: '60', vijaya_standard_price: '64', vijaya_gold_price: '78', delivery_time: 'morning', delivery_frequency: 'daily', preferred_day: 6, start_date: new Date().toISOString().split('T')[0], notes: '' });
    setEditingCustomer(null);
  };

  const editCustomer = (customer) => {
    setEditingCustomer(customer);
    setFormData({ 
      name: customer.name, 
      phone: customer.phone,
      email: customer.email || '',
      address: customer.address || '', 
      area: customer.area || '', 
      buffalo_qty: (customer.buffalo_qty || 0).toString(), 
      cow_qty: (customer.cow_qty || 0).toString(), 
      curd_qty: (customer.curd_qty || 0).toString(),
      vijaya_toned_qty: (customer.vijaya_toned_qty || 0).toString(),
      vijaya_standard_qty: (customer.vijaya_standard_qty || 0).toString(),
      vijaya_gold_qty: (customer.vijaya_gold_qty || 0).toString(),
      buffalo_price: (customer.buffalo_price || 100).toString(),
      cow_price: (customer.cow_price || 100).toString(),
      curd_price: (customer.curd_price || 120).toString(),
      vijaya_toned_price: (customer.vijaya_toned_price || 60).toString(),
      vijaya_standard_price: (customer.vijaya_standard_price || 64).toString(),
      vijaya_gold_price: (customer.vijaya_gold_price || 78).toString(),
      delivery_time: customer.delivery_time || 'morning',
      delivery_frequency: customer.delivery_frequency || 'daily',
      preferred_day: customer.preferred_day ?? 6,
      start_date: customer.start_date, 
      notes: customer.notes || '' 
    });
    setDialogOpen(true);
  };

  const deleteCustomer = async (id) => {
    if (!window.confirm('Are you sure you want to delete this customer?')) return;
    try { await axios.delete(`${API}/regular-customers/${id}`); toast.success('Deleted!'); fetchData(); }
    catch (error) { toast.error('Failed to delete'); }
  };

  const toggleStatus = async (customer) => {
    const newStatus = customer.status === 'active' ? 'paused' : 'active';
    try { await axios.put(`${API}/regular-customers/${customer.id}`, { status: newStatus }); toast.success(`Customer ${newStatus}`); fetchData(); }
    catch (error) { toast.error('Failed to update status'); }
  };

  const markAllDeliveries = async () => {
    try {
      const res = await axios.post(`${API}/daily-deliveries/mark-all?date=${selectedDate}`);
      toast.success(res.data.message);
      fetchData();
    } catch (error) { toast.error('Failed to mark deliveries'); }
  };

  const autoGenerateMonthDeliveries = async () => {
    const month = selectedDate.slice(0, 7);
    if (!window.confirm(`🗓️ Auto-generate deliveries for ALL customers for the entire month of ${month}?\n\nThis will create delivery records for:\n• Daily customers: every day\n• Alternate customers: every other day\n• Weekly customers: on their preferred day\n\nExisting records won't be duplicated.`)) {
      return;
    }
    try {
      const res = await axios.post(`${API}/daily-deliveries/auto-generate-month?month=${month}`);
      toast.success(res.data.message);
      fetchData();
    } catch (error) { toast.error('Failed to auto-generate deliveries'); }
  };

  const generateBills = async () => {
    try {
      const res = await axios.post(`${API}/monthly-bills/generate?month=${selectedMonth}`);
      toast.success(res.data.message);
      fetchData();
    } catch (error) { toast.error('Failed to generate bills'); }
  };

  const generateBillsFromSubscription = async () => {
    if (!window.confirm(`Generate ${selectedMonth} bills for ALL active customers from their subscription (quantity × price × days in month)?\n\nCustomers who already have a bill for ${selectedMonth} are skipped.`)) return;
    try {
      const res = await axios.post(`${API}/monthly-bills/generate-from-subscription?month=${selectedMonth}`);
      toast.success(res.data.message);
      fetchData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to generate bills'); }
  };

  const recordPayment = async (customerId, amount, type, month, paymentDate = null, replacePayment = false, redeemPoints = 0) => {
    try {
      const res = await axios.post(`${API}/payments`, { 
        customer_id: customerId, 
        amount: parseFloat(amount), 
        payment_type: type, 
        month: month,
        payment_date: paymentDate,
        replace_payment: replacePayment,
        redeem_points: redeemPoints
      });
      toast.success(res.data?.message || (replacePayment ? 'Payment updated!' : 'Payment recorded!'));
      setPaymentDialog({ open: false, customer: null });
      setBillPaymentDialog({ open: false, bill: null });
      fetchData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to record payment'); }
  };

  // Open Edit Bill Dialog
  const openEditBillDialog = (bill) => {
    setEditBillData({
      total_deliveries: bill.total_deliveries || 0,
      buffalo_total: bill.buffalo_total || 0,
      cow_total: bill.cow_total || 0,
      curd_total: bill.curd_total || 0,
      vijaya_toned_total: bill.vijaya_toned_total || 0,
      vijaya_standard_total: bill.vijaya_standard_total || 0,
      vijaya_gold_total: bill.vijaya_gold_total || 0,
      final_amount: bill.final_amount || 0
    });
    setEditBillDialog({ open: true, bill });
  };

  // Save Bill Edits
  const saveBillEdits = async () => {
    try {
      // Calculate total amount from all products
      const totalAmount = 
        parseFloat(editBillData.buffalo_total || 0) +
        parseFloat(editBillData.cow_total || 0) +
        parseFloat(editBillData.curd_total || 0) +
        parseFloat(editBillData.vijaya_toned_total || 0) +
        parseFloat(editBillData.vijaya_standard_total || 0) +
        parseFloat(editBillData.vijaya_gold_total || 0);
      
      await axios.put(`${API}/monthly-bills/${editBillDialog.bill.id}`, {
        total_deliveries: parseInt(editBillData.total_deliveries) || 0,
        buffalo_total: parseFloat(editBillData.buffalo_total) || 0,
        cow_total: parseFloat(editBillData.cow_total) || 0,
        curd_total: parseFloat(editBillData.curd_total) || 0,
        vijaya_toned_total: parseFloat(editBillData.vijaya_toned_total) || 0,
        vijaya_standard_total: parseFloat(editBillData.vijaya_standard_total) || 0,
        vijaya_gold_total: parseFloat(editBillData.vijaya_gold_total) || 0,
        final_amount: parseFloat(editBillData.final_amount) || totalAmount,
        total_amount: parseFloat(editBillData.final_amount) || totalAmount
      });
      toast.success('Bill updated successfully!');
      setEditBillDialog({ open: false, bill: null });
      fetchData();
    } catch (error) { 
      toast.error('Failed to update bill'); 
    }
  };

  // Share bill via WhatsApp
  const shareBillToCustomer = (bill) => {
    const customer = customers.find(c => c.id === bill.customer_id);
    if (!customer?.phone) {
      toast.error('Customer phone number not found');
      return;
    }
    
    const days = bill.total_deliveries || bill.delivery_days || bill.days || 'N/A';
    const status = bill.payment_status === 'paid' ? '✅ PAID' : '⏳ PENDING';
    const balance = bill.final_amount - (bill.amount_paid || 0);
    
    // Build product lines - only show products with quantity > 0
    const productLines = [];
    if (bill.buffalo_total > 0) productLines.push(`🐃 Buffalo Milk: ₹${bill.buffalo_total}`);
    if (bill.cow_total > 0) productLines.push(`🐄 Cow Milk: ₹${bill.cow_total}`);
    if (bill.curd_total > 0) productLines.push(`🥣 Curd: ₹${bill.curd_total}`);
    if (bill.vijaya_toned_total > 0) productLines.push(`🥛 V.Toned: ₹${bill.vijaya_toned_total}`);
    if (bill.vijaya_standard_total > 0) productLines.push(`🥛 V.Standard: ₹${bill.vijaya_standard_total}`);
    if (bill.vijaya_gold_total > 0) productLines.push(`🥛 V.Gold: ₹${bill.vijaya_gold_total}`);
    
    const productsText = productLines.length > 0 
      ? productLines.join('\n') 
      : '(See delivery details)';
    
    const logoUrl = 'https://customer-assets.emergentagent.com/job_ecf1a18d-b8c4-4d51-9a48-b9c5614b6399/artifacts/50hdzzeb_freshwala%20logo.jpg';
    
    const message = `🥛 *FRESHWALA*
_Freshness at your doorstep_
━━━━━━━━━━━━━━━━━━

📋 *Monthly Bill - ${bill.month}*

👤 *Customer:* ${bill.customer_name}
📅 *Period:* ${bill.month}
📦 *Deliveries:* ${days} days

📊 *Product Details:*
${productsText}

━━━━━━━━━━━━━━━━━━
💰 *Total Bill:* ₹${bill.final_amount?.toLocaleString()}
💵 *Paid:* ₹${(bill.amount_paid || 0).toLocaleString()}
📍 *Balance:* ₹${balance.toLocaleString()}
📊 *Status:* ${status}
${bill.payment_date ? `📅 *Payment Date:* ${bill.payment_date}` : ''}
━━━━━━━━━━━━━━━━━━

💳 *Payment Options:*
📱 GPay/Paytm: *9248444687*
🔗 UPI: *paytm.s274py4@pty*

🖼️ *View Our Logo:* ${logoUrl}

Thank you! 🙏
📞 Contact: 8125995777
🌐 freshwala.store`;

    const encodedMessage = encodeURIComponent(message);
    window.open(`https://wa.me/91${customer.phone}?text=${encodedMessage}`, '_blank');
  };

  const shareBillToAdmin = (bill) => {
    const days = bill.total_deliveries || bill.delivery_days || bill.days || 'N/A';
    const status = bill.payment_status === 'paid' ? '✅ PAID' : '⏳ PENDING';
    const balance = bill.final_amount - (bill.amount_paid || 0);
    
    // Build product lines - only show products with quantity > 0
    const productLines = [];
    if (bill.buffalo_total > 0) productLines.push(`🐃 Buffalo: ₹${bill.buffalo_total}`);
    if (bill.cow_total > 0) productLines.push(`🐄 Cow: ₹${bill.cow_total}`);
    if (bill.curd_total > 0) productLines.push(`🥣 Curd: ₹${bill.curd_total}`);
    if (bill.vijaya_toned_total > 0) productLines.push(`🥛 V.T: ₹${bill.vijaya_toned_total}`);
    if (bill.vijaya_standard_total > 0) productLines.push(`🥛 V.S: ₹${bill.vijaya_standard_total}`);
    if (bill.vijaya_gold_total > 0) productLines.push(`🥛 V.G: ₹${bill.vijaya_gold_total}`);
    
    // If no products, show a fallback message
    const productsText = productLines.length > 0 
      ? productLines.join('\n') 
      : '(No product breakdown)';
    
    const logoUrl = 'https://customer-assets.emergentagent.com/job_ecf1a18d-b8c4-4d51-9a48-b9c5614b6399/artifacts/50hdzzeb_freshwala%20logo.jpg';
    
    const message = `📊 *FRESHWALA ADMIN*
━━━━━━━━━━━━━━━━━━

👤 *${bill.customer_name}*
📅 *${bill.month}* | 📦 ${days} days

${productsText}

💰 *Total:* ₹${bill.final_amount?.toLocaleString()}
💵 *Paid:* ₹${(bill.amount_paid || 0).toLocaleString()}
📍 *Balance:* ₹${balance.toLocaleString()}
📊 ${status}
${bill.payment_date ? `📅 ${bill.payment_date}` : ''}
━━━━━━━━━━━━━━━━━━

🖼️ Logo: ${logoUrl}`;

    const encodedMessage = encodeURIComponent(message);
    // Open WhatsApp without specific number - let user choose
    window.open(`https://wa.me/?text=${encodedMessage}`, '_blank');
  };

  const exportToCSV = () => {
    const headers = ['Name', 'Phone', 'Area', 'Buffalo (L)', 'Cow (L)', 'Curd (L)', 'Buffalo ₹', 'Cow ₹', 'Curd ₹', 'Time', 'Status', 'Advance'];
    const rows = customers.map(c => [c.name, c.phone, c.area || '', c.buffalo_qty || 0, c.cow_qty || 0, c.curd_qty || 0, c.buffalo_price || 100, c.cow_price || 80, c.curd_price || 80, c.delivery_time, c.status, c.advance_balance || 0]);
    const csv = [headers.join(','), ...rows.map(r => r.map(v => `"${v}"`).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = `subscription_customers_${new Date().toISOString().split('T')[0]}.csv`; a.click();
    toast.success('Exported to CSV!');
  };

  // Bulk selection functions
  const toggleSelectCustomer = (customerId) => {
    setSelectedCustomers(prev => 
      prev.includes(customerId) 
        ? prev.filter(id => id !== customerId)
        : [...prev, customerId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedCustomers.length === filteredCustomers.length) {
      setSelectedCustomers([]);
    } else {
      setSelectedCustomers(filteredCustomers.map(c => c.id));
    }
  };

  const countDeliveriesForSelected = async () => {
    if (selectedCustomers.length === 0) {
      toast.error('Please select at least one customer');
      return;
    }
    try {
      const counts = await Promise.all(
        selectedCustomers.map(async (customerId) => {
          const res = await axios.get(`${API}/daily-deliveries?customer_id=${customerId}`);
          const deliveredCount = res.data.filter(d => d.status === 'delivered' && d.date.startsWith(selectedMonth)).length;
          const customer = customers.find(c => c.id === customerId);
          return { name: customer?.name, count: deliveredCount };
        })
      );
      setBulkDeliveryCount(counts);
      toast.success(`Counted deliveries for ${selectedCustomers.length} customers`);
    } catch (error) {
      toast.error('Failed to count deliveries');
    }
  };

  const generateBillsForSelected = async () => {
    if (selectedCustomers.length === 0) {
      toast.error('Please select at least one customer');
      return;
    }
    if (!window.confirm(`Generate bills for ${selectedCustomers.length} selected customers for ${selectedMonth}?`)) return;
    try {
      await axios.post(`${API}/monthly-bills/generate?month=${selectedMonth}`);
      toast.success(`Bills generated for ${selectedMonth}`);
      fetchData();
      setSelectedCustomers([]);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate bills');
    }
  };

  const markDeliveriesForSelected = async () => {
    if (selectedCustomers.length === 0) {
      toast.error('Please select at least one customer');
      return;
    }
    if (!window.confirm(`Mark delivery for ${selectedCustomers.length} selected customers for ${selectedDate}?`)) return;
    try {
      // Mark deliveries for each selected customer
      let markedCount = 0;
      for (const customerId of selectedCustomers) {
        const customer = customers.find(c => c.id === customerId);
        if (!customer || customer.status !== 'active') continue;
        
        const totalAmt = (customer.buffalo_qty || 0) * (customer.buffalo_price || 100) +
                        (customer.cow_qty || 0) * (customer.cow_price || 100) +
                        (customer.curd_qty || 0) * (customer.curd_price || 120) +
                        (customer.vijaya_toned_qty || 0) * (customer.vijaya_toned_price || 60) +
                        (customer.vijaya_standard_qty || 0) * (customer.vijaya_standard_price || 64) +
                        (customer.vijaya_gold_qty || 0) * (customer.vijaya_gold_price || 78);
        
        if (totalAmt > 0) {
          try {
            await axios.post(`${API}/daily-deliveries`, {
              customer_id: customerId,
              buffalo_qty: customer.buffalo_qty || 0,
              cow_qty: customer.cow_qty || 0,
              curd_qty: customer.curd_qty || 0,
              vijaya_toned_qty: customer.vijaya_toned_qty || 0,
              vijaya_standard_qty: customer.vijaya_standard_qty || 0,
              vijaya_gold_qty: customer.vijaya_gold_qty || 0,
              date: selectedDate,
              delivery_time: customer.delivery_time || 'morning'
            });
            markedCount++;
          } catch (e) { console.log('Delivery already exists or skipped'); }
        }
      }
      toast.success(`Marked ${markedCount} deliveries for ${selectedDate}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to mark deliveries');
    }
  };

  // Helper to get dates in range
  const getDatesInRange = (startDate, endDate) => {
    const dates = [];
    const current = new Date(startDate);
    const end = new Date(endDate);
    while (current <= end) {
      dates.push(current.toISOString().split('T')[0]);
      current.setDate(current.getDate() + 1);
    }
    return dates;
  };

  // Check if delivery should happen based on frequency
  const shouldDeliverOnDate = (customer, dateStr) => {
    const freq = customer.delivery_frequency || 'daily';
    const deliveryDate = new Date(dateStr);
    
    if (freq === 'daily') return true;
    
    if (freq === 'alternate') {
      const startDate = new Date(customer.start_date || dateStr);
      const daysDiff = Math.floor((deliveryDate - startDate) / (1000 * 60 * 60 * 24));
      return daysDiff >= 0 && daysDiff % 2 === 0;
    }
    
    if (freq === 'weekly') {
      const preferredDay = customer.preferred_day ?? 6; // Default Sunday
      return deliveryDate.getDay() === preferredDay;
    }
    
    return true;
  };

  // Bulk mark deliveries for date range
  const markBulkDeliveriesForRange = async () => {
    if (selectedCustomers.length === 0) {
      toast.error('Please select at least one customer');
      return;
    }
    
    const dates = getDatesInRange(dateRangeFrom, dateRangeTo);
    if (dates.length === 0) {
      toast.error('Invalid date range');
      return;
    }
    
    // Check if custom mode has at least one product selected
    if (bulkProductMode === 'custom') {
      const hasProduct = Object.values(bulkProducts).some(p => p.enabled && parseFloat(p.qty) > 0);
      if (!hasProduct) {
        toast.error('Please select at least one product with quantity > 0');
        return;
      }
    }
    
    const totalOperations = selectedCustomers.length * dates.length;
    const modeText = bulkProductMode === 'custom' ? ' with CUSTOM quantities' : ' using customer default quantities';
    if (!window.confirm(`Mark deliveries for ${selectedCustomers.length} customers across ${dates.length} days${modeText}?\n\nThis may create up to ${totalOperations} delivery records.`)) return;
    
    setBulkProgress({ show: true, current: 0, total: totalOperations, message: 'Starting...' });
    
    let markedCount = 0;
    let skippedCount = 0;
    let currentOp = 0;
    
    for (const dateStr of dates) {
      for (const customerId of selectedCustomers) {
        currentOp++;
        const customer = customers.find(c => c.id === customerId);
        
        if (!customer) {
          skippedCount++;
          continue;
        }
        
        // Check frequency - skip if not a delivery day for this customer
        if (!shouldDeliverOnDate(customer, dateStr)) {
          skippedCount++;
          setBulkProgress(prev => ({ ...prev, current: currentOp, message: `Skipped ${customer.name} - ${dateStr} (frequency)` }));
          continue;
        }
        
        // Determine quantities based on mode
        let buffalo_qty, cow_qty, curd_qty, vijaya_toned_qty, vijaya_standard_qty, vijaya_gold_qty;
        
        if (bulkProductMode === 'custom') {
          buffalo_qty = bulkProducts.buffalo.enabled ? parseFloat(bulkProducts.buffalo.qty) || 0 : 0;
          cow_qty = bulkProducts.cow.enabled ? parseFloat(bulkProducts.cow.qty) || 0 : 0;
          curd_qty = bulkProducts.curd.enabled ? parseFloat(bulkProducts.curd.qty) || 0 : 0;
          vijaya_toned_qty = bulkProducts.vijaya_toned.enabled ? parseFloat(bulkProducts.vijaya_toned.qty) || 0 : 0;
          vijaya_standard_qty = bulkProducts.vijaya_standard.enabled ? parseFloat(bulkProducts.vijaya_standard.qty) || 0 : 0;
          vijaya_gold_qty = bulkProducts.vijaya_gold.enabled ? parseFloat(bulkProducts.vijaya_gold.qty) || 0 : 0;
        } else {
          buffalo_qty = customer.buffalo_qty || 0;
          cow_qty = customer.cow_qty || 0;
          curd_qty = customer.curd_qty || 0;
          vijaya_toned_qty = customer.vijaya_toned_qty || 0;
          vijaya_standard_qty = customer.vijaya_standard_qty || 0;
          vijaya_gold_qty = customer.vijaya_gold_qty || 0;
        }
        
        const totalAmt = buffalo_qty * (customer.buffalo_price || 100) +
                        cow_qty * (customer.cow_price || 100) +
                        curd_qty * (customer.curd_price || 120) +
                        vijaya_toned_qty * (customer.vijaya_toned_price || 60) +
                        vijaya_standard_qty * (customer.vijaya_standard_price || 64) +
                        vijaya_gold_qty * (customer.vijaya_gold_price || 78);
        
        if (totalAmt > 0) {
          try {
            await axios.post(`${API}/daily-deliveries`, {
              customer_id: customerId,
              buffalo_qty,
              cow_qty,
              curd_qty,
              vijaya_toned_qty,
              vijaya_standard_qty,
              vijaya_gold_qty,
              date: dateStr,
              delivery_time: customer.delivery_time || 'morning'
            });
            markedCount++;
            setBulkProgress(prev => ({ ...prev, current: currentOp, message: `✓ ${customer.name} - ${dateStr}` }));
          } catch (e) {
            skippedCount++;
            setBulkProgress(prev => ({ ...prev, current: currentOp, message: `Exists: ${customer.name} - ${dateStr}` }));
          }
        }
      }
    }
    
    setBulkProgress({ show: false, current: 0, total: 0, message: '' });
    setBulkDeliveryDialog(false);
    toast.success(`Done! Marked: ${markedCount}, Skipped: ${skippedCount} (already exist or frequency mismatch)`);
    fetchData();
  };

  // Quick date range setters
  const setQuickDateRange = (type) => {
    const today = new Date();
    let from, to;
    
    switch(type) {
      case 'today':
        from = to = today.toISOString().split('T')[0];
        break;
      case 'yesterday':
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        from = to = yesterday.toISOString().split('T')[0];
        break;
      case 'last7':
        to = today.toISOString().split('T')[0];
        const last7 = new Date(today);
        last7.setDate(last7.getDate() - 6);
        from = last7.toISOString().split('T')[0];
        break;
      case 'last15':
        to = today.toISOString().split('T')[0];
        const last15 = new Date(today);
        last15.setDate(last15.getDate() - 14);
        from = last15.toISOString().split('T')[0];
        break;
      case 'thisMonth':
        from = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-01`;
        to = today.toISOString().split('T')[0];
        break;
      case 'lastMonth':
        const lastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        const lastMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0);
        from = lastMonth.toISOString().split('T')[0];
        to = lastMonthEnd.toISOString().split('T')[0];
        break;
      default:
        from = to = today.toISOString().split('T')[0];
    }
    
    setDateRangeFrom(from);
    setDateRangeTo(to);
  };

  // Filter and sort customers
  const filteredCustomers = customers.filter(c => {
    const matchesSearch = c.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
                          c.phone.includes(searchTerm) ||
                          (c.area && c.area.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesStatus = filterStatus === 'all' || c.status === filterStatus;
    const matchesArea = filterArea === 'all' || c.area === filterArea;
    const matchesTime = filterTime === 'all' || c.delivery_time === filterTime;
    
    // Date range filter - filter by start_date
    let matchesDateRange = true;
    if (filterDateFrom && c.start_date) {
      matchesDateRange = matchesDateRange && c.start_date >= filterDateFrom;
    }
    if (filterDateTo && c.start_date) {
      matchesDateRange = matchesDateRange && c.start_date <= filterDateTo;
    }
    
    return matchesSearch && matchesStatus && matchesArea && matchesTime && matchesDateRange;
  }).sort((a, b) => {
    if (sortBy === 'name') return a.name.localeCompare(b.name);
    if (sortBy === 'area') return (a.area || '').localeCompare(b.area || '');
    if (sortBy === 'quantity') return b.daily_quantity - a.daily_quantity;
    if (sortBy === 'amount') return (b.daily_quantity * b.price_per_liter) - (a.daily_quantity * a.price_per_liter);
    return 0;
  });

  // Get unique areas for filter
  const uniqueAreas = [...new Set(customers.map(c => c.area).filter(Boolean))];

  // Calculate analytics
  const getDailyAmount = (c) => {
    return (c.buffalo_qty || 0) * (c.buffalo_price || 100) + 
           (c.cow_qty || 0) * (c.cow_price || 100) + 
           (c.curd_qty || 0) * (c.curd_price || 120) +
           (c.vijaya_toned_qty || 0) * (c.vijaya_toned_price || 60) +
           (c.vijaya_standard_qty || 0) * (c.vijaya_standard_price || 64) +
           (c.vijaya_gold_qty || 0) * (c.vijaya_gold_price || 78);
  };
  const getTotalQty = (c) => (c.buffalo_qty || 0) + (c.cow_qty || 0) + (c.curd_qty || 0) + (c.vijaya_toned_qty || 0) + (c.vijaya_standard_qty || 0) + (c.vijaya_gold_qty || 0);
  
  const analytics = {
    totalBuffaloQty: customers.filter(c => c.status === 'active').reduce((sum, c) => sum + (c.buffalo_qty || 0), 0),
    totalCowQty: customers.filter(c => c.status === 'active').reduce((sum, c) => sum + (c.cow_qty || 0), 0),
    totalCurdQty: customers.filter(c => c.status === 'active').reduce((sum, c) => sum + (c.curd_qty || 0), 0),
    totalVijayaTonedQty: customers.filter(c => c.status === 'active').reduce((sum, c) => sum + (c.vijaya_toned_qty || 0), 0),
    totalVijayaStdQty: customers.filter(c => c.status === 'active').reduce((sum, c) => sum + (c.vijaya_standard_qty || 0), 0),
    totalVijayaGoldQty: customers.filter(c => c.status === 'active').reduce((sum, c) => sum + (c.vijaya_gold_qty || 0), 0),
    totalDailyRevenue: customers.filter(c => c.status === 'active').reduce((sum, c) => sum + getDailyAmount(c), 0),
    dailyCustomers: customers.filter(c => c.delivery_frequency === 'daily').length,
    alternateCustomers: customers.filter(c => c.delivery_frequency === 'alternate').length,
    weeklyCustomers: customers.filter(c => c.delivery_frequency === 'weekly').length,
    morningCustomers: customers.filter(c => c.delivery_time === 'morning').length,
    eveningCustomers: customers.filter(c => c.delivery_time === 'evening').length,
    totalAdvanceBalance: customers.reduce((sum, c) => sum + (c.advance_balance || 0), 0),
    byArea: uniqueAreas.map(area => ({
      area,
      count: customers.filter(c => c.area === area).length,
      amount: customers.filter(c => c.area === area).reduce((sum, c) => sum + getDailyAmount(c), 0)
    })).sort((a, b) => b.count - a.count)
  };

  // Send WhatsApp reminder
  const sendWhatsAppReminder = (customer, bill) => {
    const message = encodeURIComponent(
      `🥛 *Freshwala Monthly Bill*\n\n` +
      `Dear ${customer?.name || bill?.customer_name},\n\n` +
      `Month: ${bill?.month || selectedMonth}\n` +
      `Total Deliveries: ${bill?.total_deliveries || '-'}\n` +
      `Total Quantity: ${bill?.total_quantity || '-'}L\n` +
      `Amount Due: ₹${bill?.final_amount - (bill?.amount_paid || 0)}\n\n` +
      `Please clear your dues at the earliest.\n\n` +
      `Thank you!\n- Freshwala 🥛`
    );
    const phone = customer?.phone || '';
    window.open(`https://wa.me/91${phone}?text=${message}`, '_blank');
  };

  // Print Invoice - Using safe DOM manipulation
  const printInvoice = (bill) => {
    const customer = customers.find(c => c.id === bill.customer_id);
    const logoUrl = 'https://customer-assets.emergentagent.com/job_ecf1a18d-b8c4-4d51-9a48-b9c5614b6399/artifacts/50hdzzeb_freshwala%20logo.jpg';
    
    // Sanitize data to prevent XSS
    const sanitize = (str) => String(str || '').replace(/[<>&"']/g, c => ({'<':'&lt;','>':'&gt;','&':'&amp;','"':'&quot;',"'":'&#39;'}[c]));
    
    const invoiceWindow = window.open('', '_blank');
    if (!invoiceWindow) {
      toast.error('Please allow popups to print invoice');
      return;
    }
    
    // Create document structure using DOM methods
    const doc = invoiceWindow.document;
    doc.open();
    
    // Build HTML content with sanitized values
    const customerName = sanitize(bill.customer_name);
    const customerPhone = sanitize(customer?.phone || 'N/A');
    const customerAddress = sanitize(customer?.address || customer?.area || 'N/A');
    const billMonth = sanitize(bill.month);
    const invoiceNo = `FW-${(bill.month || '').replace('-', '')}-${(bill.id || '').slice(-4) || '0001'}`;
    
    const htmlContent = `<!DOCTYPE html>
<html>
<head>
  <title>Invoice - ${customerName}</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { font-family: 'Segoe UI', Arial, sans-serif; padding: 30px; max-width: 800px; margin: 0 auto; background: #fff; }
    .invoice-container { border: 2px solid #10b981; border-radius: 10px; padding: 30px; }
    .header { display: flex; justify-content: space-between; align-items: center; border-bottom: 3px solid #10b981; padding-bottom: 20px; margin-bottom: 25px; }
    .logo-section { display: flex; align-items: center; gap: 15px; }
    .logo-section img { height: 60px; width: auto; }
    .company-info { text-align: right; }
    .company-info h2 { color: #10b981; font-size: 24px; margin-bottom: 5px; }
    .company-info p { color: #666; font-size: 12px; line-height: 1.5; }
    .invoice-title { text-align: center; background: linear-gradient(135deg, #10b981, #059669); color: white; padding: 15px; border-radius: 8px; margin-bottom: 25px; }
    .invoice-title h1 { font-size: 28px; letter-spacing: 2px; }
    .details-row { display: flex; justify-content: space-between; margin-bottom: 25px; gap: 20px; }
    .details-box { flex: 1; background: #f8fafb; padding: 15px; border-radius: 8px; border-left: 4px solid #10b981; }
    .details-box h3 { color: #10b981; font-size: 14px; margin-bottom: 10px; text-transform: uppercase; }
    .details-box p { color: #333; font-size: 14px; line-height: 1.6; }
    table { width: 100%; border-collapse: collapse; margin: 20px 0; }
    th { background: #10b981; color: white; padding: 12px 15px; text-align: left; font-size: 14px; }
    td { border: 1px solid #e5e7eb; padding: 12px 15px; font-size: 14px; }
    tr:nth-child(even) { background: #f9fafb; }
    .summary-section { background: #f0fdf4; padding: 20px; border-radius: 8px; margin-top: 20px; }
    .summary-row { display: flex; justify-content: space-between; padding: 8px 0; border-bottom: 1px dashed #d1d5db; }
    .summary-row:last-child { border-bottom: none; }
    .summary-row.total { font-size: 18px; font-weight: bold; color: #10b981; padding-top: 15px; border-top: 2px solid #10b981; margin-top: 10px; }
    .status-badge { display: inline-block; padding: 5px 15px; border-radius: 20px; font-size: 12px; font-weight: bold; }
    .status-paid { background: #d1fae5; color: #059669; }
    .status-pending { background: #fee2e2; color: #dc2626; }
    .footer { text-align: center; margin-top: 30px; padding-top: 20px; border-top: 2px dashed #e5e7eb; }
    .footer p { color: #666; font-size: 12px; margin: 5px 0; }
    .footer .tagline { color: #10b981; font-weight: bold; font-size: 14px; }
    @media print { body { padding: 10px; print-color-adjust: exact; -webkit-print-color-adjust: exact; } .invoice-container { border: 1px solid #10b981; } }
  </style>
</head>
<body>
  <div class="invoice-container">
    <div class="header">
      <div class="logo-section"><img src="${logoUrl}" alt="Freshwala Logo" /></div>
      <div class="company-info">
        <h2>FRESHWALA</h2>
        <p>Fresh Dairy Delivery Service</p>
        <p>📞 8125995777 | 📱 WhatsApp Available</p>
        <p>📧 info@freshwala.online</p>
        <p>🌐 freshwala.store</p>
      </div>
    </div>
    <div class="invoice-title"><h1>TAX INVOICE</h1></div>
    <div class="details-row">
      <div class="details-box">
        <h3>Bill To</h3>
        <p><strong>${customerName}</strong></p>
        <p>📞 ${customerPhone}</p>
        <p>📍 ${customerAddress}</p>
      </div>
      <div class="details-box">
        <h3>Invoice Details</h3>
        <p><strong>Invoice No:</strong> ${invoiceNo}</p>
        <p><strong>Bill Month:</strong> ${billMonth}</p>
        <p><strong>Issue Date:</strong> ${new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</p>
        <p><strong>Status:</strong> <span class="status-badge ${bill.payment_status === 'paid' ? 'status-paid' : 'status-pending'}">${(bill.payment_status || '').toUpperCase()}</span></p>
      </div>
    </div>
    <table>
      <thead><tr><th>Description</th><th>Days</th><th>Qty (L)</th><th>Rate</th><th>Amount</th></tr></thead>
      <tbody>
        ${bill.buffalo_total > 0 ? `<tr><td>🐃 Buffalo Milk Delivery</td><td>${bill.total_deliveries || 0}</td><td>${(bill.buffalo_total / (customer?.buffalo_price || 100)).toFixed(1)}L</td><td>₹${customer?.buffalo_price || 100}/L</td><td>₹${(bill.buffalo_total || 0).toLocaleString()}</td></tr>` : ''}
        ${bill.cow_total > 0 ? `<tr><td>🐄 Cow Milk Delivery</td><td>${bill.total_deliveries || 0}</td><td>${(bill.cow_total / (customer?.cow_price || 80)).toFixed(1)}L</td><td>₹${customer?.cow_price || 80}/L</td><td>₹${(bill.cow_total || 0).toLocaleString()}</td></tr>` : ''}
        ${bill.curd_total > 0 ? `<tr><td>🥣 Curd Delivery</td><td>${bill.total_deliveries || 0}</td><td>${(bill.curd_total / (customer?.curd_price || 120)).toFixed(1)}L</td><td>₹${customer?.curd_price || 120}/L</td><td>₹${(bill.curd_total || 0).toLocaleString()}</td></tr>` : ''}
        ${(bill.buffalo_total === 0 && bill.cow_total === 0 && bill.curd_total === 0) ? `<tr><td>Milk Delivery (${bill.total_deliveries || 0} days)</td><td>${bill.total_deliveries || 0}</td><td>${bill.total_quantity || '-'}</td><td>₹${customer?.price_per_liter || 100}/L</td><td>₹${(bill.total_amount || 0).toLocaleString()}</td></tr>` : ''}
      </tbody>
    </table>
    <div class="summary-section">
      <div class="summary-row"><span>Subtotal</span><span>₹${(bill.total_amount || 0).toLocaleString()}</span></div>
      ${bill.advance_used > 0 ? `<div class="summary-row"><span>Advance Adjusted</span><span style="color: #059669;">-₹${(bill.advance_used || 0).toLocaleString()}</span></div>` : ''}
      ${bill.amount_paid > 0 ? `<div class="summary-row"><span>Amount Paid</span><span style="color: #059669;">-₹${(bill.amount_paid || 0).toLocaleString()}</span></div>` : ''}
      <div class="summary-row total"><span>${bill.payment_status === 'paid' ? '✅ PAID' : '💰 BALANCE DUE'}</span><span>₹${((bill.final_amount || 0) - (bill.amount_paid || 0)).toLocaleString()}</span></div>
    </div>
    <div class="footer">
      <p class="tagline">🥛 Thank you for choosing Freshwala!</p>
      <p>For queries: Call 8125995777 or WhatsApp 7075769452</p>
      <p>Payment: GPay/PhonePe: 9248444687 | UPI: paytm.s274py4@pty</p>
      <p style="margin-top: 10px; font-size: 10px; color: #999;">This is a computer generated invoice</p>
    </div>
  </div>
</body>
</html>`;
    
    doc.write(htmlContent);
    doc.close();
    
    // Trigger print after content loads
    invoiceWindow.onload = function() { invoiceWindow.print(); };
  };

  // Bulk actions
  const bulkPauseAll = async () => {
    if (!window.confirm('Pause all active customers?')) return;
    for (const c of customers.filter(c => c.status === 'active')) {
      await axios.put(`${API}/regular-customers/${c.id}`, { status: 'paused' });
    }
    toast.success('All customers paused');
    fetchData();
  };

  const bulkResumeAll = async () => {
    if (!window.confirm('Resume all paused customers?')) return;
    for (const c of customers.filter(c => c.status === 'paused')) {
      await axios.put(`${API}/regular-customers/${c.id}`, { status: 'active' });
    }
    toast.success('All customers resumed');
    fetchData();
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div></div>;

  return (
    <div className="space-y-6" data-testid="subscription-manage-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Subscription Customers</h1>
          <p className="text-gray-500">Manage your {summary.total_customers || 0} regular milk subscription customers</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={exportToCSV}><FileText className="w-4 h-4 mr-2" />Export CSV</Button>
          <Dialog open={importCustomersOpen} onOpenChange={(open) => { setImportCustomersOpen(open); if (!open) { setImportCustomersFile(null); setImportCustomersResult(null); } }}>
            <DialogTrigger asChild>
              <Button variant="outline" className="border-blue-500 text-blue-600 hover:bg-blue-50" data-testid="import-customers-btn">
                <Upload className="w-4 h-4 mr-2" />Import Customers
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>📥 Import Customers (Excel / CSV)</DialogTitle>
                <DialogDescription>
                  Download the sheet, add or edit customers in Excel, then upload it back. Phone number is the unique ID — existing customers get updated, new phone numbers get added.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-2">
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" className="flex-1 border-emerald-500 text-emerald-600 hover:bg-emerald-50" onClick={() => downloadCustomersSheet('excel')} data-testid="download-customers-excel">
                    <Download className="w-4 h-4 mr-1" /> Download Sheet (Excel)
                  </Button>
                  <Button variant="outline" size="sm" className="flex-1" onClick={() => downloadCustomersSheet('csv')} data-testid="download-customers-csv">
                    <Download className="w-4 h-4 mr-1" /> CSV
                  </Button>
                </div>
                <div className="bg-blue-50 p-3 rounded-lg text-xs text-gray-700 space-y-1">
                  <p className="font-semibold">Required columns: <span className="font-mono">Phone, Name</span></p>
                  <p>Optional: Area, Address, Buffalo Qty, Buffalo Price, Cow Qty, Cow Price, Curd Qty, Curd Price, Vijaya Toned/Standard/Gold Qty, Delivery Time (morning/evening), Status (active/paused/stopped)</p>
                  <p>✏️ New customers are created with sensible defaults — only filled columns are applied.</p>
                </div>
                <div>
                  <Label>Select Excel or CSV File</Label>
                  <Input 
                    type="file" 
                    accept=".xlsx,.xls,.csv" 
                    onChange={(e) => setImportCustomersFile(e.target.files[0])} 
                    className="mt-1"
                    data-testid="import-customers-file"
                  />
                </div>
                {importCustomersResult && (
                  <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-sm" data-testid="import-customers-result">
                    <p className="font-semibold text-emerald-700">✅ {importCustomersResult.message}</p>
                    {importCustomersResult.errors?.length > 0 && (
                      <ul className="mt-1 text-red-600 text-xs list-disc pl-4">
                        {importCustomersResult.errors.slice(0, 5).map((er, i) => <li key={er}>{er}</li>)}
                      </ul>
                    )}
                  </div>
                )}
                <Button 
                  className="w-full bg-blue-600 hover:bg-blue-700" 
                  onClick={importCustomersFromSheet} 
                  disabled={importingCustomers || !importCustomersFile}
                  data-testid="import-customers-submit"
                >
                  {importingCustomers ? 'Importing...' : 'Import Customers'}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild><Button className="bg-emerald-600 hover:bg-emerald-700"><Plus className="w-4 h-4 mr-2" />Add Customer</Button></DialogTrigger>
            <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle>{editingCustomer ? 'Edit Customer' : 'Add New Customer'}</DialogTitle></DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2"><Label>Full Name *</Label><Input value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} required /></div>
                  <div><Label>Phone *</Label><Input value={formData.phone} onChange={e => setFormData({...formData, phone: e.target.value})} required /></div>
                  <div><Label>Email (for bills)</Label><Input type="email" value={formData.email || ''} onChange={e => setFormData({...formData, email: e.target.value})} placeholder="customer@email.com" /></div>
                  <div><Label>Area *</Label><Input value={formData.area} onChange={e => setFormData({...formData, area: e.target.value})} placeholder="e.g., Madhapur" required /></div>
                  <div><Label>Address (Optional)</Label><Textarea value={formData.address} onChange={e => setFormData({...formData, address: e.target.value})} placeholder="Full address" /></div>
                  
                  {/* Product Quantities */}
                  <div className="col-span-2 p-3 bg-blue-50 rounded-lg">
                    <Label className="text-blue-800 font-semibold mb-2 block">🥛 Daily Products (Liters)</Label>
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <Label className="text-xs">🐃 Buffalo @₹{formData.buffalo_price}</Label>
                        <Input type="number" step="0.5" min="0" value={formData.buffalo_qty} onChange={e => setFormData({...formData, buffalo_qty: e.target.value})} />
                      </div>
                      <div>
                        <Label className="text-xs">🐄 Cow @₹{formData.cow_price}</Label>
                        <Input type="number" step="0.5" min="0" value={formData.cow_qty} onChange={e => setFormData({...formData, cow_qty: e.target.value})} />
                      </div>
                      <div>
                        <Label className="text-xs">🥣 Curd @₹{formData.curd_price}</Label>
                        <Input type="number" step="0.5" min="0" value={formData.curd_qty} onChange={e => setFormData({...formData, curd_qty: e.target.value})} />
                      </div>
                      <div>
                        <Label className="text-xs">📦 Vijaya Toned @₹{formData.vijaya_toned_price}</Label>
                        <Input type="number" step="0.5" min="0" value={formData.vijaya_toned_qty} onChange={e => setFormData({...formData, vijaya_toned_qty: e.target.value})} />
                      </div>
                      <div>
                        <Label className="text-xs">📦 Vijaya Std @₹{formData.vijaya_standard_price}</Label>
                        <Input type="number" step="0.5" min="0" value={formData.vijaya_standard_qty} onChange={e => setFormData({...formData, vijaya_standard_qty: e.target.value})} />
                      </div>
                      <div>
                        <Label className="text-xs">📦 Vijaya Gold @₹{formData.vijaya_gold_price}</Label>
                        <Input type="number" step="0.5" min="0" value={formData.vijaya_gold_qty} onChange={e => setFormData({...formData, vijaya_gold_qty: e.target.value})} />
                      </div>
                    </div>
                  </div>
                  
                  <div><Label>Delivery Time</Label>
                    <Select value={formData.delivery_time} onValueChange={v => setFormData({...formData, delivery_time: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="morning">🌅 Morning (7-9 AM)</SelectItem><SelectItem value="evening">🌆 Evening (7-8 PM)</SelectItem></SelectContent>
                    </Select>
                  </div>
                  <div><Label>Delivery Frequency</Label>
                    <Select value={formData.delivery_frequency} onValueChange={v => setFormData({...formData, delivery_frequency: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="daily">📅 Daily</SelectItem>
                        <SelectItem value="alternate">🔄 Alternate Days</SelectItem>
                        <SelectItem value="weekly">📆 Weekly</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {formData.delivery_frequency === 'weekly' && (
                    <div><Label>Preferred Day</Label>
                      <Select value={formData.preferred_day?.toString()} onValueChange={v => setFormData({...formData, preferred_day: parseInt(v)})}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="0">Monday</SelectItem>
                          <SelectItem value="1">Tuesday</SelectItem>
                          <SelectItem value="2">Wednesday</SelectItem>
                          <SelectItem value="3">Thursday</SelectItem>
                          <SelectItem value="4">Friday</SelectItem>
                          <SelectItem value="5">Saturday</SelectItem>
                          <SelectItem value="6">Sunday</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  <div><Label>Start Date</Label><Input type="date" value={formData.start_date} onChange={e => setFormData({...formData, start_date: e.target.value})} required /></div>
                  <div className="col-span-2"><Label>Notes</Label><Textarea value={formData.notes} onChange={e => setFormData({...formData, notes: e.target.value})} placeholder="Any special instructions..." /></div>
                </div>
                <Button type="submit" className="w-full bg-emerald-600 hover:bg-emerald-700">{editingCustomer ? 'Update Customer' : 'Add Customer'}</Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Enhanced Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
        <Card><CardContent className="p-4"><p className="text-sm text-gray-500">Customers</p><p className="text-2xl font-bold">{summary.total_customers || 0}</p><p className="text-xs text-emerald-600">{summary.active_customers || 0} active</p></CardContent></Card>
        <Card><CardContent className="p-4"><p className="text-sm text-gray-500">🐃 Buffalo</p><p className="text-2xl font-bold text-blue-600">{analytics.totalBuffaloQty}L</p><p className="text-xs text-gray-500">/day</p></CardContent></Card>
        <Card><CardContent className="p-4"><p className="text-sm text-gray-500">🐄 Cow</p><p className="text-2xl font-bold text-emerald-600">{analytics.totalCowQty}L</p><p className="text-xs text-gray-500">/day</p></CardContent></Card>
        <Card><CardContent className="p-4"><p className="text-sm text-gray-500">🥣 Curd</p><p className="text-2xl font-bold text-orange-600">{analytics.totalCurdQty}L</p><p className="text-xs text-gray-500">/day</p></CardContent></Card>
        <Card><CardContent className="p-4"><p className="text-sm text-gray-500">Daily Revenue</p><p className="text-2xl font-bold text-emerald-600">₹{analytics.totalDailyRevenue.toLocaleString()}</p></CardContent></Card>
        <Card><CardContent className="p-4"><p className="text-sm text-gray-500">Advance</p><p className="text-2xl font-bold text-purple-600">₹{analytics.totalAdvanceBalance.toLocaleString()}</p></CardContent></Card>
        <Card className="border-red-300 bg-red-50"><CardContent className="p-4"><p className="text-sm text-red-600">Pending ({summary.current_month || new Date().toISOString().slice(0, 7)})</p><p className="text-2xl font-bold text-red-600">₹{summary.pending_amount?.toLocaleString() || 0}</p><p className="text-xs text-red-500">Current month only</p></CardContent></Card>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b overflow-x-auto">
        {[
          { id: 'customers', label: 'Customers', icon: Users }, 
          { id: 'deliveries', label: 'Daily Deliveries', icon: Truck }, 
          { id: 'bills', label: 'Monthly Bills', icon: FileText },
          { id: 'analytics', label: 'Analytics', icon: BarChart3 }
        ].map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`flex items-center gap-2 px-4 py-2 border-b-2 transition-colors whitespace-nowrap ${activeTab === tab.id ? 'border-emerald-600 text-emerald-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
            <tab.icon className="w-4 h-4" />{tab.label}
          </button>
        ))}
      </div>

      {/* Customers Tab */}
      {activeTab === 'customers' && (
        <div className="space-y-4">
          {/* Search and Filters */}
          <div className="flex flex-wrap gap-3 items-center bg-gray-50 p-4 rounded-lg">
            <div className="flex-1 min-w-[200px]">
              <Input placeholder="🔍 Search by name, phone, area..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
            </div>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-32"><SelectValue placeholder="Status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="paused">Paused</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterArea} onValueChange={setFilterArea}>
              <SelectTrigger className="w-36"><SelectValue placeholder="Area" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Areas</SelectItem>
                {uniqueAreas.map(area => <SelectItem key={area} value={area}>{area}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filterTime} onValueChange={setFilterTime}>
              <SelectTrigger className="w-32"><SelectValue placeholder="Time" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Times</SelectItem>
                <SelectItem value="morning">🌅 Morning</SelectItem>
                <SelectItem value="evening">🌆 Evening</SelectItem>
              </SelectContent>
            </Select>
            <div className="flex items-center gap-2 bg-white border rounded-lg px-2 py-1">
              <span className="text-sm text-gray-500">From:</span>
              <Input 
                type="date" 
                value={filterDateFrom} 
                onChange={e => setFilterDateFrom(e.target.value)} 
                className="w-36 h-8 border-0 p-0"
                data-testid="filter-date-from"
              />
              <span className="text-sm text-gray-500">To:</span>
              <Input 
                type="date" 
                value={filterDateTo} 
                onChange={e => setFilterDateTo(e.target.value)} 
                className="w-36 h-8 border-0 p-0"
                data-testid="filter-date-to"
              />
              <Button 
                size="sm" 
                className="bg-emerald-600 hover:bg-emerald-700 h-7 px-3"
                onClick={() => {
                  if (filterDateFrom || filterDateTo) {
                    toast.success(`Filter applied: ${filterDateFrom || 'Start'} to ${filterDateTo || 'End'}`);
                  }
                }}
                data-testid="apply-date-filter"
              >
                Apply
              </Button>
              {(filterDateFrom || filterDateTo) && (
                <Button variant="ghost" size="sm" className="h-6 px-2" onClick={() => { setFilterDateFrom(''); setFilterDateTo(''); }}>
                  ✕
                </Button>
              )}
            </div>
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-32"><SelectValue placeholder="Sort" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="name">Name</SelectItem>
                <SelectItem value="area">Area</SelectItem>
                <SelectItem value="quantity">Quantity</SelectItem>
                <SelectItem value="amount">Amount</SelectItem>
              </SelectContent>
            </Select>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={bulkPauseAll} title="Pause All">⏸️</Button>
              <Button variant="outline" size="sm" onClick={bulkResumeAll} title="Resume All">▶️</Button>
            </div>
          </div>
          
          {/* Bulk Action Bar */}
          {selectedCustomers.length > 0 && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-3">
                <span className="font-medium text-emerald-700">✓ {selectedCustomers.length} selected</span>
                <Button size="sm" variant="outline" onClick={() => setSelectedCustomers([])}>Clear</Button>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Input type="date" value={selectedDate} onChange={e => setSelectedDate(e.target.value)} className="w-40 h-8" />
                <Button size="sm" className="bg-blue-600 hover:bg-blue-700" onClick={markDeliveriesForSelected}>
                  📦 Single Day
                </Button>
                <Button size="sm" className="bg-purple-600 hover:bg-purple-700" onClick={() => setBulkDeliveryDialog(true)}>
                  📅 Date Range
                </Button>
                <Button size="sm" variant="outline" onClick={countDeliveriesForSelected}>
                  📊 Count Days
                </Button>
                <Input type="month" value={selectedMonth} onChange={e => setSelectedMonth(e.target.value)} className="w-36 h-8" />
                <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={generateBillsForSelected}>
                  📄 Generate Bills
                </Button>
                <Button size="sm" variant="destructive" className="bg-red-600 hover:bg-red-700" onClick={bulkDeleteSelected} data-testid="bulk-delete-btn">
                  🗑 Delete Selected
                </Button>
              </div>
            </div>
          )}
          
          {/* Bulk Delivery Dialog for Date Range */}
          <Dialog open={bulkDeliveryDialog} onOpenChange={setBulkDeliveryDialog}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>📅 Bulk Mark Deliveries (Date Range)</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="bg-blue-50 p-3 rounded-lg text-sm">
                  <p className="font-medium text-blue-700">Selected: {selectedCustomers.length} customers</p>
                  <p className="text-blue-600">Deliveries will be marked based on each customer's frequency (Daily/Alternate/Weekly)</p>
                </div>
                
                {/* Quick Options */}
                <div>
                  <Label className="mb-2 block">Quick Select Date Range</Label>
                  <div className="flex flex-wrap gap-2">
                    <Button size="sm" variant="outline" onClick={() => setQuickDateRange('today')}>Today</Button>
                    <Button size="sm" variant="outline" onClick={() => setQuickDateRange('yesterday')}>Yesterday</Button>
                    <Button size="sm" variant="outline" onClick={() => setQuickDateRange('last7')}>Last 7 Days</Button>
                    <Button size="sm" variant="outline" onClick={() => setQuickDateRange('last15')}>Last 15 Days</Button>
                    <Button size="sm" variant="outline" onClick={() => setQuickDateRange('thisMonth')}>This Month</Button>
                    <Button size="sm" variant="outline" onClick={() => setQuickDateRange('lastMonth')}>Last Month</Button>
                  </div>
                </div>
                
                {/* Custom Date Range */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>From Date</Label>
                    <Input type="date" value={dateRangeFrom} onChange={e => setDateRangeFrom(e.target.value)} />
                  </div>
                  <div>
                    <Label>To Date</Label>
                    <Input type="date" value={dateRangeTo} onChange={e => setDateRangeTo(e.target.value)} />
                  </div>
                </div>
                
                {/* Product Mode Selection */}
                <div className="border-t pt-4">
                  <Label className="mb-2 block font-medium">Product Quantities</Label>
                  <div className="flex gap-4 mb-3">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input 
                        type="radio" 
                        name="productMode" 
                        checked={bulkProductMode === 'customer'} 
                        onChange={() => setBulkProductMode('customer')}
                        className="w-4 h-4"
                      />
                      <span className="text-sm">Use Customer's Default Quantities</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input 
                        type="radio" 
                        name="productMode" 
                        checked={bulkProductMode === 'custom'} 
                        onChange={() => setBulkProductMode('custom')}
                        className="w-4 h-4"
                      />
                      <span className="text-sm">Custom Quantities (Select Products)</span>
                    </label>
                  </div>
                  
                  {/* Custom Product Selection */}
                  {bulkProductMode === 'custom' && (
                    <div className="bg-orange-50 p-3 rounded-lg space-y-3">
                      <p className="text-xs text-orange-600 mb-2">Select products and set quantities for this bulk delivery:</p>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                        {/* Buffalo Milk */}
                        <div className={`p-2 rounded border ${bulkProducts.buffalo.enabled ? 'bg-blue-100 border-blue-400' : 'bg-white'}`}>
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input 
                              type="checkbox" 
                              checked={bulkProducts.buffalo.enabled}
                              onChange={e => setBulkProducts(prev => ({...prev, buffalo: {...prev.buffalo, enabled: e.target.checked}}))}
                              className="w-4 h-4"
                            />
                            <span className="text-sm font-medium">🐃 Buffalo Milk</span>
                          </label>
                          {bulkProducts.buffalo.enabled && (
                            <div className="mt-2 flex items-center gap-1">
                              <Input 
                                type="number" 
                                step="0.5" 
                                min="0" 
                                value={bulkProducts.buffalo.qty}
                                onChange={e => setBulkProducts(prev => ({...prev, buffalo: {...prev.buffalo, qty: e.target.value}}))}
                                className="w-20 h-8 text-sm"
                              />
                              <span className="text-xs">Liters</span>
                            </div>
                          )}
                        </div>
                        
                        {/* Cow Milk */}
                        <div className={`p-2 rounded border ${bulkProducts.cow.enabled ? 'bg-green-100 border-green-400' : 'bg-white'}`}>
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input 
                              type="checkbox" 
                              checked={bulkProducts.cow.enabled}
                              onChange={e => setBulkProducts(prev => ({...prev, cow: {...prev.cow, enabled: e.target.checked}}))}
                              className="w-4 h-4"
                            />
                            <span className="text-sm font-medium">🐄 Cow Milk</span>
                          </label>
                          {bulkProducts.cow.enabled && (
                            <div className="mt-2 flex items-center gap-1">
                              <Input 
                                type="number" 
                                step="0.5" 
                                min="0" 
                                value={bulkProducts.cow.qty}
                                onChange={e => setBulkProducts(prev => ({...prev, cow: {...prev.cow, qty: e.target.value}}))}
                                className="w-20 h-8 text-sm"
                              />
                              <span className="text-xs">Liters</span>
                            </div>
                          )}
                        </div>
                        
                        {/* Curd */}
                        <div className={`p-2 rounded border ${bulkProducts.curd.enabled ? 'bg-yellow-100 border-yellow-400' : 'bg-white'}`}>
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input 
                              type="checkbox" 
                              checked={bulkProducts.curd.enabled}
                              onChange={e => setBulkProducts(prev => ({...prev, curd: {...prev.curd, enabled: e.target.checked}}))}
                              className="w-4 h-4"
                            />
                            <span className="text-sm font-medium">🥣 Curd</span>
                          </label>
                          {bulkProducts.curd.enabled && (
                            <div className="mt-2 flex items-center gap-1">
                              <Input 
                                type="number" 
                                step="0.5" 
                                min="0" 
                                value={bulkProducts.curd.qty}
                                onChange={e => setBulkProducts(prev => ({...prev, curd: {...prev.curd, qty: e.target.value}}))}
                                className="w-20 h-8 text-sm"
                              />
                              <span className="text-xs">Liters</span>
                            </div>
                          )}
                        </div>
                        
                        {/* Vijaya Toned */}
                        <div className={`p-2 rounded border ${bulkProducts.vijaya_toned.enabled ? 'bg-purple-100 border-purple-400' : 'bg-white'}`}>
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input 
                              type="checkbox" 
                              checked={bulkProducts.vijaya_toned.enabled}
                              onChange={e => setBulkProducts(prev => ({...prev, vijaya_toned: {...prev.vijaya_toned, enabled: e.target.checked}}))}
                              className="w-4 h-4"
                            />
                            <span className="text-sm font-medium">🥛 Vijaya Toned</span>
                          </label>
                          {bulkProducts.vijaya_toned.enabled && (
                            <div className="mt-2 flex items-center gap-1">
                              <Input 
                                type="number" 
                                step="0.5" 
                                min="0" 
                                value={bulkProducts.vijaya_toned.qty}
                                onChange={e => setBulkProducts(prev => ({...prev, vijaya_toned: {...prev.vijaya_toned, qty: e.target.value}}))}
                                className="w-20 h-8 text-sm"
                              />
                              <span className="text-xs">Liters</span>
                            </div>
                          )}
                        </div>
                        
                        {/* Vijaya Standard */}
                        <div className={`p-2 rounded border ${bulkProducts.vijaya_standard.enabled ? 'bg-pink-100 border-pink-400' : 'bg-white'}`}>
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input 
                              type="checkbox" 
                              checked={bulkProducts.vijaya_standard.enabled}
                              onChange={e => setBulkProducts(prev => ({...prev, vijaya_standard: {...prev.vijaya_standard, enabled: e.target.checked}}))}
                              className="w-4 h-4"
                            />
                            <span className="text-sm font-medium">🥛 Vijaya Standard</span>
                          </label>
                          {bulkProducts.vijaya_standard.enabled && (
                            <div className="mt-2 flex items-center gap-1">
                              <Input 
                                type="number" 
                                step="0.5" 
                                min="0" 
                                value={bulkProducts.vijaya_standard.qty}
                                onChange={e => setBulkProducts(prev => ({...prev, vijaya_standard: {...prev.vijaya_standard, qty: e.target.value}}))}
                                className="w-20 h-8 text-sm"
                              />
                              <span className="text-xs">Liters</span>
                            </div>
                          )}
                        </div>
                        
                        {/* Vijaya Gold */}
                        <div className={`p-2 rounded border ${bulkProducts.vijaya_gold.enabled ? 'bg-amber-100 border-amber-400' : 'bg-white'}`}>
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input 
                              type="checkbox" 
                              checked={bulkProducts.vijaya_gold.enabled}
                              onChange={e => setBulkProducts(prev => ({...prev, vijaya_gold: {...prev.vijaya_gold, enabled: e.target.checked}}))}
                              className="w-4 h-4"
                            />
                            <span className="text-sm font-medium">🥛 Vijaya Gold</span>
                          </label>
                          {bulkProducts.vijaya_gold.enabled && (
                            <div className="mt-2 flex items-center gap-1">
                              <Input 
                                type="number" 
                                step="0.5" 
                                min="0" 
                                value={bulkProducts.vijaya_gold.qty}
                                onChange={e => setBulkProducts(prev => ({...prev, vijaya_gold: {...prev.vijaya_gold, qty: e.target.value}}))}
                                className="w-20 h-8 text-sm"
                              />
                              <span className="text-xs">Liters</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Summary */}
                <div className="bg-gray-50 p-3 rounded-lg">
                  <p className="text-sm">
                    <strong>Date Range:</strong> {dateRangeFrom} to {dateRangeTo}
                    <br />
                    <strong>Days:</strong> {getDatesInRange(dateRangeFrom, dateRangeTo).length} days
                    <br />
                    <strong>Mode:</strong> {bulkProductMode === 'customer' ? 'Customer Default Quantities' : 'Custom Quantities'}
                    {bulkProductMode === 'custom' && (
                      <>
                        <br />
                        <strong>Products:</strong> {Object.entries(bulkProducts).filter(([k, v]) => v.enabled).map(([k, v]) => `${k.replace('_', ' ')} (${v.qty}L)`).join(', ') || 'None selected'}
                      </>
                    )}
                  </p>
                </div>
                
                {/* Progress Bar */}
                {bulkProgress.show && (
                  <div className="space-y-2">
                    <div className="w-full bg-gray-200 rounded-full h-2.5">
                      <div className="bg-emerald-600 h-2.5 rounded-full transition-all" style={{ width: `${(bulkProgress.current / bulkProgress.total) * 100}%` }}></div>
                    </div>
                    <p className="text-xs text-gray-500">{bulkProgress.message}</p>
                    <p className="text-sm font-medium">{bulkProgress.current} / {bulkProgress.total}</p>
                  </div>
                )}
                
                <Button 
                  className="w-full bg-purple-600 hover:bg-purple-700" 
                  onClick={markBulkDeliveriesForRange}
                  disabled={bulkProgress.show}
                >
                  {bulkProgress.show ? 'Processing...' : `📦 Mark Deliveries for ${getDatesInRange(dateRangeFrom, dateRangeTo).length} Days`}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
          
          {/* Delivery Count Results */}
          {bulkDeliveryCount && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <div className="flex justify-between items-center mb-2">
                <span className="font-medium text-blue-700">📊 Delivery Count for {selectedMonth}</span>
                <Button size="sm" variant="ghost" onClick={() => setBulkDeliveryCount(null)}>✕</Button>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2 text-sm">
                {bulkDeliveryCount.map((item, idx) => (
                  <div key={idx} className="bg-white p-2 rounded border">
                    <div className="font-medium truncate">{item.name}</div>
                    <div className="text-lg font-bold text-blue-600">{item.count} days</div>
                  </div>
                ))}
              </div>
              <div className="mt-2 text-right font-medium">
                Total: {bulkDeliveryCount.reduce((sum, i) => sum + i.count, 0)} deliveries
              </div>
            </div>
          )}
          
          <p className="text-sm text-gray-500">Showing {filteredCustomers.length} of {customers.length} customers</p>
          
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-10">
                      <input 
                        type="checkbox" 
                        checked={selectedCustomers.length === filteredCustomers.length && filteredCustomers.length > 0}
                        onChange={toggleSelectAll}
                        className="w-4 h-4 rounded border-gray-300"
                      />
                    </TableHead>
                    <TableHead>Customer</TableHead><TableHead>Contact</TableHead><TableHead>Subscription</TableHead>
                    <TableHead className="text-right">Daily Rate</TableHead><TableHead className="text-right">Advance</TableHead>
                    <TableHead>Status</TableHead><TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredCustomers.length === 0 ? <TableRow><TableCell colSpan={8} className="text-center py-8 text-gray-500">No customers found</TableCell></TableRow> : 
                  filteredCustomers.map(customer => (
                    <TableRow key={customer.id} className={`${customer.status === 'paused' ? 'bg-yellow-50' : ''} ${selectedCustomers.includes(customer.id) ? 'bg-emerald-50' : ''}`}>
                      <TableCell>
                        <input 
                          type="checkbox" 
                          checked={selectedCustomers.includes(customer.id)}
                          onChange={() => toggleSelectCustomer(customer.id)}
                          className="w-4 h-4 rounded border-gray-300"
                        />
                      </TableCell>
                      <TableCell>
                        <div className="font-medium">{customer.name}</div>
                        <div className="text-xs text-gray-500">{customer.area}</div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">{customer.phone}</div>
                        <a href={`https://wa.me/91${customer.phone}`} target="_blank" rel="noreferrer" className="text-xs text-green-600 hover:underline">WhatsApp</a>
                      </TableCell>
                      <TableCell>
                        <div className="text-xs space-y-0.5">
                          {(customer.buffalo_qty || 0) > 0 && <span className="inline-block bg-blue-100 px-1 rounded mr-1">🐃{customer.buffalo_qty}L</span>}
                          {(customer.cow_qty || 0) > 0 && <span className="inline-block bg-green-100 px-1 rounded mr-1">🐄{customer.cow_qty}L</span>}
                          {(customer.curd_qty || 0) > 0 && <span className="inline-block bg-yellow-100 px-1 rounded mr-1">🥣{customer.curd_qty}L</span>}
                          {(customer.vijaya_toned_qty || 0) > 0 && <span className="inline-block bg-purple-100 px-1 rounded mr-1">VT{customer.vijaya_toned_qty}L</span>}
                          {(customer.vijaya_standard_qty || 0) > 0 && <span className="inline-block bg-pink-100 px-1 rounded mr-1">VS{customer.vijaya_standard_qty}L</span>}
                          {(customer.vijaya_gold_qty || 0) > 0 && <span className="inline-block bg-amber-100 px-1 rounded mr-1">VG{customer.vijaya_gold_qty}L</span>}
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                          {customer.delivery_time === 'morning' ? '🌅' : '🌆'} 
                          {customer.delivery_frequency === 'daily' ? ' Daily' : customer.delivery_frequency === 'alternate' ? ' Alt' : ' Weekly'}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="font-medium">₹{getDailyAmount(customer)}</div>
                        <div className="text-xs text-gray-500">/day</div>
                      </TableCell>
                      <TableCell className="text-right">
                        <span className={customer.advance_balance > 0 ? 'text-emerald-600 font-medium' : 'text-gray-400'}>
                          ₹{customer.advance_balance || 0}
                        </span>
                      </TableCell>
                      <TableCell>
                        <Badge variant={customer.status === 'active' ? 'default' : 'secondary'} className={customer.status === 'active' ? 'bg-emerald-600' : ''}>
                          {customer.status}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="sm" onClick={() => editCustomer(customer)}><Edit className="w-4 h-4" /></Button>
                        <Button variant="ghost" size="sm" onClick={() => toggleStatus(customer)} title={customer.status === 'active' ? 'Pause' : 'Activate'}>
                          {customer.status === 'active' ? '⏸️' : '▶️'}
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => setPaymentDialog({ open: true, customer })}><DollarSign className="w-4 h-4 text-emerald-600" /></Button>
                        <Button variant="ghost" size="sm" onClick={() => deleteCustomer(customer.id)}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
        </div>
      )}

      {/* Daily Deliveries Tab */}
      {activeTab === 'deliveries' && (
        <div className="space-y-4">
          {/* Scheduler Status Card */}
          <Card className={`border-2 ${schedulerStatus.enabled ? 'border-emerald-300 bg-emerald-50' : 'border-gray-300 bg-gray-50'}`}>
            <CardContent className="p-3 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className={`w-3 h-3 rounded-full ${schedulerStatus.enabled ? 'bg-emerald-500 animate-pulse' : 'bg-gray-400'}`} />
                <div>
                  <p className="font-medium text-sm">
                    ⏰ Auto-Generation Scheduler: 
                    <span className={schedulerStatus.enabled ? 'text-emerald-600' : 'text-gray-500'}>
                      {schedulerStatus.enabled ? ' ACTIVE' : ' DISABLED'}
                    </span>
                  </p>
                  {schedulerStatus.next_run && schedulerStatus.enabled && (
                    <p className="text-xs text-gray-500">
                      Next run: {new Date(schedulerStatus.next_run).toLocaleString()}
                    </p>
                  )}
                  {schedulerStatus.recent_logs?.[0] && (
                    <p className="text-xs text-gray-500">
                      Last run: {schedulerStatus.recent_logs[0].date} - Created {schedulerStatus.recent_logs[0].deliveries_created} deliveries
                    </p>
                  )}
                </div>
              </div>
              <div className="flex gap-2">
                <Button 
                  size="sm" 
                  variant={schedulerStatus.enabled ? 'destructive' : 'default'}
                  className={!schedulerStatus.enabled ? 'bg-emerald-600 hover:bg-emerald-700' : ''}
                  onClick={toggleScheduler}
                >
                  {schedulerStatus.enabled ? '⏸️ Pause' : '▶️ Enable'}
                </Button>
                <Button 
                  size="sm" 
                  variant="outline"
                  onClick={runSchedulerNow}
                  title="Generate today's deliveries now"
                >
                  🚀 Run Now
                </Button>
              </div>
            </CardContent>
          </Card>
          
          <div className="flex items-center gap-4 flex-wrap">
            <Input type="date" value={selectedDate} onChange={e => setSelectedDate(e.target.value)} className="w-48" />
            <Button onClick={markAllDeliveries} className="bg-emerald-600 hover:bg-emerald-700">
              <CheckCircle className="w-4 h-4 mr-2" />Mark All for {selectedDate}
            </Button>
            <Button onClick={autoGenerateMonthDeliveries} variant="outline" className="border-purple-500 text-purple-700 hover:bg-purple-50">
              <CalendarIcon className="w-4 h-4 mr-2" />Auto-Generate Month ({selectedDate.slice(0, 7)})
            </Button>
            <span className="text-gray-500">{deliveries.length} deliveries marked</span>
            <div className="ml-auto flex items-center gap-2 text-sm flex-wrap">
              {deliveries.reduce((sum, d) => sum + (d.buffalo_qty || 0), 0) > 0 && (
                <span className="bg-blue-100 px-2 py-1 rounded-lg">
                  🐃 Buffalo: <strong>{deliveries.reduce((sum, d) => sum + (d.buffalo_qty || 0), 0)}L</strong>
                </span>
              )}
              {deliveries.reduce((sum, d) => sum + (d.cow_qty || 0), 0) > 0 && (
                <span className="bg-green-100 px-2 py-1 rounded-lg">
                  🐄 Cow: <strong>{deliveries.reduce((sum, d) => sum + (d.cow_qty || 0), 0)}L</strong>
                </span>
              )}
              {deliveries.reduce((sum, d) => sum + (d.curd_qty || 0), 0) > 0 && (
                <span className="bg-yellow-100 px-2 py-1 rounded-lg">
                  🥣 Curd: <strong>{deliveries.reduce((sum, d) => sum + (d.curd_qty || 0), 0)}L</strong>
                </span>
              )}
              {deliveries.reduce((sum, d) => sum + (d.vijaya_toned_qty || 0), 0) > 0 && (
                <span className="bg-purple-100 px-2 py-1 rounded-lg">
                  🥛 V.Toned: <strong>{deliveries.reduce((sum, d) => sum + (d.vijaya_toned_qty || 0), 0)}L</strong>
                </span>
              )}
              {deliveries.reduce((sum, d) => sum + (d.vijaya_standard_qty || 0), 0) > 0 && (
                <span className="bg-indigo-100 px-2 py-1 rounded-lg">
                  🥛 V.Std: <strong>{deliveries.reduce((sum, d) => sum + (d.vijaya_standard_qty || 0), 0)}L</strong>
                </span>
              )}
              {deliveries.reduce((sum, d) => sum + (d.vijaya_gold_qty || 0), 0) > 0 && (
                <span className="bg-amber-100 px-2 py-1 rounded-lg">
                  🥛 V.Gold: <strong>{deliveries.reduce((sum, d) => sum + (d.vijaya_gold_qty || 0), 0)}L</strong>
                </span>
              )}
              <span className="bg-emerald-100 px-3 py-2 rounded-lg font-bold text-emerald-700">
                💰 Total: ₹{deliveries.reduce((sum, d) => sum + (d.total_amount || 0), 0).toLocaleString()}
              </span>
            </div>
          </div>
          
          {/* Monthly Customer Totals */}
          {monthlyCustomerTotals.length > 0 && (
            <Card className="bg-purple-50 border-purple-200">
              <CardContent className="p-4">
                <h3 className="font-bold text-purple-800 mb-3">📊 Monthly Totals ({selectedDate.slice(0, 7)})</h3>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-purple-100">
                        <TableHead>Customer</TableHead>
                        <TableHead className="text-center">Days</TableHead>
                        <TableHead className="text-right">🐃 Buffalo</TableHead>
                        <TableHead className="text-right">🐄 Cow</TableHead>
                        <TableHead className="text-right">🥣 Curd</TableHead>
                        <TableHead className="text-right">🥛 V.Toned</TableHead>
                        <TableHead className="text-right">🥛 V.Std</TableHead>
                        <TableHead className="text-right">🥛 V.Gold</TableHead>
                        <TableHead className="text-right font-bold">Total Amount</TableHead>
                        <TableHead>Action</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {monthlyCustomerTotals.map((ct, idx) => (
                        <TableRow key={idx} className="hover:bg-purple-100 cursor-pointer">
                          <TableCell className="font-medium text-purple-700">{ct.name}</TableCell>
                          <TableCell className="text-center">{ct.days}</TableCell>
                          <TableCell className="text-right">{ct.buffalo || 0}L</TableCell>
                          <TableCell className="text-right">{ct.cow || 0}L</TableCell>
                          <TableCell className="text-right">{ct.curd || 0}L</TableCell>
                          <TableCell className="text-right">{ct.vijaya_toned || 0}L</TableCell>
                          <TableCell className="text-right">{ct.vijaya_standard || 0}L</TableCell>
                          <TableCell className="text-right">{ct.vijaya_gold || 0}L</TableCell>
                          <TableCell className="text-right font-bold text-emerald-600">₹{ct.amount.toLocaleString()}</TableCell>
                          <TableCell>
                            <div className="flex gap-1">
                              <Button size="sm" variant="outline" onClick={() => viewCustomerHistory(ct.id, ct.name)} className="text-xs">
                                📋 View
                              </Button>
                              <Button size="sm" variant="outline" className="text-xs text-red-600 border-red-300 hover:bg-red-50" onClick={async () => {
                                if (window.confirm(`🗑️ Delete ALL ${ct.days} deliveries for ${ct.name} in ${selectedDate.slice(0, 7)}?\n\nThis cannot be undone!`)) {
                                  try {
                                    await axios.delete(`${API}/daily-deliveries/customer/${ct.id}/month/${selectedDate.slice(0, 7)}`);
                                    toast.success(`Deleted all deliveries for ${ct.name}`);
                                    fetchData();
                                  } catch (e) { toast.error('Failed to delete'); }
                                }
                              }}>
                                🗑️ Delete All
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                      <TableRow className="bg-purple-200 font-bold">
                        <TableCell>MONTHLY TOTAL</TableCell>
                        <TableCell className="text-center">{monthlyCustomerTotals.reduce((s, c) => s + c.days, 0)}</TableCell>
                        <TableCell className="text-right">{monthlyCustomerTotals.reduce((s, c) => s + (c.buffalo || 0), 0)}L</TableCell>
                        <TableCell className="text-right">{monthlyCustomerTotals.reduce((s, c) => s + (c.cow || 0), 0)}L</TableCell>
                        <TableCell className="text-right">{monthlyCustomerTotals.reduce((s, c) => s + (c.curd || 0), 0)}L</TableCell>
                        <TableCell className="text-right">{monthlyCustomerTotals.reduce((s, c) => s + (c.vijaya_toned || 0), 0)}L</TableCell>
                        <TableCell className="text-right">{monthlyCustomerTotals.reduce((s, c) => s + (c.vijaya_standard || 0), 0)}L</TableCell>
                        <TableCell className="text-right">{monthlyCustomerTotals.reduce((s, c) => s + (c.vijaya_gold || 0), 0)}L</TableCell>
                        <TableCell className="text-right text-lg text-emerald-700">₹{monthlyCustomerTotals.reduce((s, c) => s + c.amount, 0).toLocaleString()}</TableCell>
                        <TableCell></TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          )}
          
          {/* Customer History Dialog - View ALL days for one customer */}
          <Dialog open={customerHistoryDialog.open} onOpenChange={(open) => !open && setCustomerHistoryDialog({ open: false, customerId: null, customerName: '', deliveries: [] })}>
            <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>📋 {customerHistoryDialog.customerName} - All Deliveries ({selectedDate.slice(0, 7)})</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="bg-blue-50 p-3 rounded-lg text-sm">
                  <p><strong>Total Days:</strong> {customerHistoryDialog.deliveries.length}</p>
                  <p><strong>Total Amount:</strong> ₹{customerHistoryDialog.deliveries.reduce((s, d) => s + (d.total_amount || 0), 0).toLocaleString()}</p>
                </div>
                
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-100">
                      <TableHead>Date</TableHead>
                      <TableHead className="text-center">🐃 Buffalo (L)</TableHead>
                      <TableHead className="text-center">🐄 Cow (L)</TableHead>
                      <TableHead className="text-center">🥣 Curd (L)</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {customerHistoryDialog.deliveries.map(d => (
                      <TableRow key={d.id}>
                        <TableCell className="font-medium">{d.date}</TableCell>
                        <TableCell>
                          <Input 
                            type="number" 
                            step="0.5" 
                            min="0" 
                            defaultValue={d.buffalo_qty || 0}
                            className="w-20 h-8 text-center"
                            onBlur={(e) => {
                              const newVal = parseFloat(e.target.value) || 0;
                              if (newVal !== (d.buffalo_qty || 0)) {
                                updateDeliveryInHistory(d.id, newVal, 'buffalo');
                              }
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Input 
                            type="number" 
                            step="0.5" 
                            min="0" 
                            defaultValue={d.cow_qty || 0}
                            className="w-20 h-8 text-center"
                            onBlur={(e) => {
                              const newVal = parseFloat(e.target.value) || 0;
                              if (newVal !== (d.cow_qty || 0)) {
                                updateDeliveryInHistory(d.id, newVal, 'cow');
                              }
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Input 
                            type="number" 
                            step="0.5" 
                            min="0" 
                            defaultValue={d.curd_qty || 0}
                            className="w-20 h-8 text-center"
                            onBlur={(e) => {
                              const newVal = parseFloat(e.target.value) || 0;
                              if (newVal !== (d.curd_qty || 0)) {
                                updateDeliveryInHistory(d.id, newVal, 'curd');
                              }
                            }}
                          />
                        </TableCell>
                        <TableCell className="text-right font-medium text-emerald-600">₹{(d.total_amount || 0).toLocaleString()}</TableCell>
                        <TableCell>
                          <Button variant="ghost" size="sm" onClick={async () => {
                            if (window.confirm(`Delete delivery for ${d.date}?`)) {
                              try {
                                await axios.delete(`${API}/daily-deliveries/${d.id}`);
                                toast.success('Deleted');
                                viewCustomerHistory(customerHistoryDialog.customerId, customerHistoryDialog.customerName);
                                fetchData();
                              } catch (e) { toast.error('Failed'); }
                            }
                          }}>
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                
                <div className="bg-gray-100 p-3 rounded-lg text-sm">
                  <p className="text-gray-600">💡 <strong>Tip:</strong> Change any quantity directly in the table. It will auto-save when you click outside the field.</p>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      )}

      {/* Monthly Bills Tab */}
      {activeTab === 'bills' && (
        <div className="space-y-4">
          {/* Payment Status Summary - Enhanced */}
          <Card className="bg-gradient-to-r from-emerald-50 to-red-50 border-2">
            <CardContent className="p-4">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center gap-8">
                  <div className="text-center">
                    <p className="text-3xl font-bold text-emerald-600">{bills.filter(b => b.payment_status === 'paid').length}</p>
                    <p className="text-sm text-emerald-700">✅ Paid</p>
                  </div>
                  <div className="text-4xl text-gray-300">/</div>
                  <div className="text-center">
                    <p className="text-3xl font-bold text-red-600">{bills.filter(b => b.payment_status !== 'paid').length}</p>
                    <p className="text-sm text-red-700">⏳ Pending</p>
                  </div>
                  <div className="text-4xl text-gray-300">=</div>
                  <div className="text-center">
                    <p className="text-3xl font-bold text-blue-600">{bills.length}</p>
                    <p className="text-sm text-blue-700">📊 Total</p>
                  </div>
                </div>
                <div className="flex gap-2 flex-wrap">
                  <Button 
                    variant={billFilter === 'all' ? 'default' : 'outline'} 
                    size="sm" 
                    onClick={() => setBillFilter('all')}
                    className={billFilter === 'all' ? 'bg-blue-600' : ''}
                  >
                    All ({bills.length})
                  </Button>
                  <Button 
                    variant={billFilter === 'paid' ? 'default' : 'outline'} 
                    size="sm" 
                    onClick={() => setBillFilter('paid')}
                    className={billFilter === 'paid' ? 'bg-emerald-600' : 'text-emerald-600 border-emerald-300'}
                  >
                    ✅ Paid ({bills.filter(b => b.payment_status === 'paid').length})
                  </Button>
                  <Button 
                    variant={billFilter === 'pending' ? 'default' : 'outline'} 
                    size="sm" 
                    onClick={() => setBillFilter('pending')}
                    className={billFilter === 'pending' ? 'bg-red-600' : 'text-red-600 border-red-300'}
                  >
                    ⏳ Pending ({bills.filter(b => b.payment_status !== 'paid').length})
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => {
                      const paidCount = bills.filter(b => b.payment_status === 'paid').length;
                      const pendingCount = bills.filter(b => b.payment_status !== 'paid').length;
                      const totalBills = bills.length;
                      const paidAmount = bills.filter(b => b.payment_status === 'paid').reduce((sum, b) => sum + (b.amount_paid || b.final_amount || 0), 0);
                      const pendingAmount = bills.filter(b => b.payment_status !== 'paid').reduce((sum, b) => sum + b.final_amount - (b.amount_paid || 0), 0);
                      const totalAmount = bills.reduce((sum, b) => sum + (b.final_amount || 0), 0);
                      
                      const pendingCustomers = bills
                        .filter(b => b.payment_status !== 'paid')
                        .map(b => `• ${b.customer_name}: ₹${(b.final_amount - (b.amount_paid || 0)).toLocaleString()}`)
                        .join('\n');
                      
                      const message = `📊 *FRESHWALA - Payment Report*
━━━━━━━━━━━━━━━━━━
📅 *Month:* ${selectedMonth}

📈 *Summary:*
✅ Paid: *${paidCount}* customers
⏳ Pending: *${pendingCount}* customers
📊 Total: *${totalBills}* customers

💰 *Amount Details:*
✅ Collected: *₹${paidAmount.toLocaleString()}*
⏳ Pending: *₹${pendingAmount.toLocaleString()}*
📊 Total Bills: *₹${totalAmount.toLocaleString()}*

${pendingCount > 0 ? `\n⏳ *Pending Customers:*\n${pendingCustomers}` : ''}
━━━━━━━━━━━━━━━━━━
🥛 Freshwala Admin Report`;
                      
                      window.open(`https://wa.me/?text=${encodeURIComponent(message)}`, '_blank');
                    }}
                    className="bg-green-50 text-green-700 border-green-300 hover:bg-green-100"
                  >
                    <Share2 className="w-4 h-4 mr-1" /> Share Report
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="bg-emerald-50 border-emerald-200">
              <CardContent className="p-4">
                <p className="text-emerald-600 text-sm">✅ Total Paid</p>
                <p className="text-2xl font-bold text-emerald-700">₹{bills.filter(b => b.payment_status === 'paid').reduce((sum, b) => sum + (b.amount_paid || b.final_amount || 0), 0).toLocaleString()}</p>
                <p className="text-xs text-emerald-500">{bills.filter(b => b.payment_status === 'paid').length} customers paid</p>
              </CardContent>
            </Card>
            <Card className="bg-red-50 border-red-200">
              <CardContent className="p-4">
                <p className="text-red-600 text-sm">⏳ Total Pending</p>
                <p className="text-2xl font-bold text-red-700">₹{bills.filter(b => b.payment_status !== 'paid').reduce((sum, b) => sum + b.final_amount - (b.amount_paid || 0), 0).toLocaleString()}</p>
                <p className="text-xs text-red-500">{bills.filter(b => b.payment_status === 'pending').length} customers pending</p>
              </CardContent>
            </Card>
            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="p-4">
                <p className="text-blue-600 text-sm">📊 Total Bills</p>
                <p className="text-2xl font-bold text-blue-700">₹{bills.reduce((sum, b) => sum + (b.final_amount || 0), 0).toLocaleString()}</p>
                <p className="text-xs text-blue-500">{bills.length} bills generated</p>
              </CardContent>
            </Card>
            <Card className="bg-yellow-50 border-yellow-200">
              <CardContent className="p-4">
                <p className="text-yellow-600 text-sm">📦 Total Deliveries</p>
                <p className="text-2xl font-bold text-yellow-700">{bills.reduce((sum, b) => sum + (b.total_deliveries || 0), 0)}</p>
                <p className="text-xs text-yellow-500">{bills.reduce((sum, b) => sum + (b.buffalo_total || 0), 0)}L Buffalo delivered</p>
              </CardContent>
            </Card>
          </div>

          {/* Overdue Bills Reminders */}
          {bills.filter(b => b.payment_status !== 'paid').length > 0 && (
            <Card className="border-orange-300 bg-orange-50">
              <CardHeader className="pb-2">
                <CardTitle className="text-orange-700 flex items-center gap-2">
                  <Bell className="w-5 h-5" /> Payment Reminders
                </CardTitle>
                <CardDescription>Send WhatsApp reminders to customers with pending bills</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2 mb-4">
                  <Button
                    variant="outline"
                    size="sm"
                    className="bg-green-50 text-green-700 border-green-300 hover:bg-green-100"
                    onClick={() => {
                      const pendingBills = bills.filter(b => b.payment_status !== 'paid');
                      const pendingList = pendingBills.map(b => `• ${b.customer_name}: ₹${(b.final_amount - (b.amount_paid || 0)).toLocaleString()}`).join('\n');
                      const totalPending = pendingBills.reduce((sum, b) => sum + b.final_amount - (b.amount_paid || 0), 0);
                      const msg = `📋 *Pending Bills - ${selectedMonth}*\n\n${pendingList}\n\n💰 *Total: ₹${totalPending.toLocaleString()}*\n\n🏦 UPI: ${CONFIG.UPI_ID}\n📞 Contact: ${CONFIG.ADMIN_PHONE_1}`;
                      window.open(`https://wa.me/${CONFIG.ADMIN_WHATSAPP}?text=${encodeURIComponent(msg)}`, '_blank');
                    }}
                  >
                    <MessageCircle className="w-4 h-4 mr-1" /> Share Pending List
                  </Button>
                </div>
                <div className="max-h-48 overflow-y-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Customer</TableHead>
                        <TableHead className="text-right">Due</TableHead>
                        <TableHead className="text-center">Action</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {bills.filter(b => b.payment_status !== 'paid').slice(0, 10).map(bill => {
                        const dueAmount = bill.final_amount - (bill.amount_paid || 0);
                        const customer = customers.find(c => c.id === bill.customer_id);
                        const phone = customer?.phone || '';
                        return (
                          <TableRow key={bill.id}>
                            <TableCell className="font-medium">{bill.customer_name}</TableCell>
                            <TableCell className="text-right text-red-600 font-bold">₹{dueAmount.toLocaleString()}</TableCell>
                            <TableCell className="text-center">
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-green-600 hover:text-green-700 hover:bg-green-50"
                                onClick={() => {
                                  const customerPhone = phone.replace(/[^0-9]/g, '');
                                  const waPhone = customerPhone.startsWith('91') ? customerPhone : `91${customerPhone}`;
                                  const reminderMsg = `🔔 *Freshwala - Payment Reminder*\n\nNamaste ${bill.customer_name} ji,\n\nYour milk bill for *${selectedMonth}* is pending.\n\n💰 *Amount Due: ₹${dueAmount.toLocaleString()}*\n\n🏦 *Pay via UPI:*\n• UPI ID: ${CONFIG.UPI_ID}\n• GPay: ${CONFIG.GPAY_NUMBER}\n📷 Scan QR: ${BACKEND_URL}${CONFIG.PAYMENT_QR}\n\nThank you! 🙏\n- Freshwala Dairy`;
                                  window.open(`https://wa.me/${waPhone}?text=${encodeURIComponent(reminderMsg)}`, '_blank');
                                }}
                                data-testid={`send-reminder-${bill.id}`}
                              >
                                <MessageCircle className="w-4 h-4" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                  {bills.filter(b => b.payment_status !== 'paid').length > 10 && (
                    <p className="text-center text-sm text-gray-500 mt-2">
                      +{bills.filter(b => b.payment_status !== 'paid').length - 10} more pending...
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          )}
          
          <div className="flex items-center gap-4 flex-wrap">
            <Input type="month" value={selectedMonth} onChange={e => setSelectedMonth(e.target.value)} className="w-48" />
            <Button onClick={generateBills} className="bg-emerald-600 hover:bg-emerald-700">
              <FileText className="w-4 h-4 mr-2" />Generate Bills for {selectedMonth}
            </Button>
            <Button onClick={generateBillsFromSubscription} variant="outline" className="border-emerald-600 text-emerald-700 hover:bg-emerald-50" data-testid="generate-from-subscription-btn">
              📋 Generate from Subscription
            </Button>
            <Button onClick={() => { setRemindedBills([]); setRemindAllDialog(true); }} variant="outline" className="border-green-600 text-green-700 hover:bg-green-50" data-testid="remind-all-pending-btn">
              <MessageCircle className="w-4 h-4 mr-2" />Remind All Pending
            </Button>
            {/* Import Bills from Excel */}
            <Dialog open={importBillsDialog} onOpenChange={setImportBillsDialog}>
              <DialogTrigger asChild>
                <Button variant="outline" className="border-blue-500 text-blue-600 hover:bg-blue-50">
                  <Upload className="w-4 h-4 mr-2" />Import Bills (Excel/CSV)
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle>📊 Import Monthly Bills (Excel / CSV)</DialogTitle>
                  <DialogDescription>
                    Download the current bills sheet, correct names & amounts in Excel, then upload it back. Names are auto-corrected when the phone number matches.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" className="flex-1 border-emerald-500 text-emerald-600 hover:bg-emerald-50" onClick={() => downloadBillsSheet('excel')} data-testid="download-bills-excel">
                      <Download className="w-4 h-4 mr-1" /> Download Sheet (Excel)
                    </Button>
                    <Button variant="outline" size="sm" className="flex-1" onClick={() => downloadBillsSheet('csv')} data-testid="download-bills-csv">
                      <Download className="w-4 h-4 mr-1" /> CSV
                    </Button>
                  </div>
                  <div className="bg-blue-50 p-4 rounded-lg text-sm">
                    <p className="font-semibold mb-2">Sheet columns:</p>
                    <table className="w-full text-xs border-collapse">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left p-1">Phone</th>
                          <th className="text-left p-1">Customer Name</th>
                          <th className="text-left p-1">Bill Amount</th>
                          <th className="text-left p-1">Amount Paid</th>
                          <th className="text-left p-1">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td className="p-1">9908878000</td>
                          <td className="p-1">ABDUL NAYEEM</td>
                          <td className="p-1">1500</td>
                          <td className="p-1">500</td>
                          <td className="p-1">partial</td>
                        </tr>
                      </tbody>
                    </table>
                    <p className="mt-2 text-gray-600">✏️ Fix a wrong name? Just type the correct name — it updates the customer record (phone must match). Amount Paid & Status are optional.</p>
                  </div>
                  <div>
                    <Label>Month for Bills</Label>
                    <Input type="month" value={selectedMonth} onChange={e => setSelectedMonth(e.target.value)} className="mt-1" />
                  </div>
                  <div>
                    <Label>Select Excel or CSV File</Label>
                    <Input 
                      type="file" 
                      accept=".xlsx,.xls,.csv" 
                      onChange={(e) => setImportBillsFile(e.target.files[0])} 
                      className="mt-1"
                      data-testid="import-bills-file"
                    />
                  </div>
                  {importBillsResult && (
                    <div className={`p-3 rounded-lg ${importBillsResult.success ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}>
                      {importBillsResult.message}
                    </div>
                  )}
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => { setImportBillsDialog(false); setImportBillsFile(null); setImportBillsResult(null); }}>
                    Cancel
                  </Button>
                  <Button 
                    className="bg-emerald-600 hover:bg-emerald-700" 
                    onClick={importBillsFromExcel}
                    disabled={!importBillsFile || importingBills}
                    data-testid="import-bills-submit"
                  >
                    {importingBills ? 'Importing...' : 'Import Bills'}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
            {/* Remind All Pending Dialog */}
            <Dialog open={remindAllDialog} onOpenChange={setRemindAllDialog}>
              <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <MessageCircle className="w-5 h-5 text-green-600" /> Send Bill Reminders — {selectedMonth}
                  </DialogTitle>
                  <DialogDescription>
                    Tap <strong>Send</strong> on each customer — WhatsApp opens with the bill message ready, just hit send and come back. Sent ones turn green.
                  </DialogDescription>
                </DialogHeader>
                {(() => {
                  const pendingBills = bills
                    .filter(b => b.payment_status !== 'paid' && (b.final_amount - (b.amount_paid || 0)) > 0)
                    .sort((a, b) => (a.customer_name || '').localeCompare(b.customer_name || ''));
                  const withPhone = pendingBills.filter(b => customers.find(c => c.id === b.customer_id)?.phone);
                  const totalDue = pendingBills.reduce((s, b) => s + (b.final_amount - (b.amount_paid || 0)), 0);
                  return (
                    <div className="space-y-3">
                      <div className="flex items-center justify-between bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm">
                        <span data-testid="remind-all-summary"><strong>{pendingBills.length}</strong> pending bills · ₹{totalDue.toLocaleString()} due</span>
                        <span className="text-emerald-700">{remindedBills.length} reminded ✓</span>
                      </div>
                      {pendingBills.length === 0 && <p className="text-center text-gray-500 py-6">🎉 No pending bills for {selectedMonth}!</p>}
                      <div className="divide-y border rounded-lg">
                        {pendingBills.map(bill => {
                          const cust = customers.find(c => c.id === bill.customer_id);
                          const due = bill.final_amount - (bill.amount_paid || 0);
                          const sent = remindedBills.includes(bill.id);
                          return (
                            <div key={bill.id} className={`flex items-center justify-between gap-2 px-3 py-2 ${sent ? 'bg-emerald-50' : ''}`}>
                              <div className="min-w-0">
                                <p className="font-medium text-sm truncate">{sent ? '✅ ' : ''}{bill.customer_name}</p>
                                <p className="text-xs text-gray-500">{cust?.phone ? `📱 ${cust.phone}` : '⚠️ No phone number'} · Due ₹{due.toLocaleString()}</p>
                              </div>
                              <Button
                                size="sm"
                                disabled={!cust?.phone}
                                variant={sent ? 'outline' : 'default'}
                                className={sent ? 'border-emerald-400 text-emerald-700' : 'bg-green-600 hover:bg-green-700'}
                                data-testid={`remind-bill-${bill.id}`}
                                onClick={() => {
                                  sendWhatsAppReminder(cust, bill);
                                  setRemindedBills(prev => prev.includes(bill.id) ? prev : [...prev, bill.id]);
                                }}
                              >
                                {sent ? 'Sent ✓' : 'Send'}
                              </Button>
                            </div>
                          );
                        })}
                      </div>
                      {pendingBills.length > withPhone.length && (
                        <p className="text-xs text-amber-700">⚠️ {pendingBills.length - withPhone.length} customer(s) have no phone number — add it in the Customers tab to remind them.</p>
                      )}
                    </div>
                  );
                })()}
                <DialogFooter>
                  <Button variant="outline" onClick={() => setRemindAllDialog(false)}>Close</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
            <Select 
              value={searchTerm || "all"} 
              onValueChange={(val) => setSearchTerm(val === "all" ? "" : val)}
            >
              <SelectTrigger className="w-72">
                <SelectValue placeholder="🔍 Select Customer..." />
              </SelectTrigger>
              <SelectContent className="max-h-80">
                <SelectItem value="all">📋 All Customers ({bills.length})</SelectItem>
                {bills
                  .sort((a, b) => (a.customer_name || '').localeCompare(b.customer_name || ''))
                  .map(bill => (
                    <SelectItem key={bill.id} value={bill.customer_name || ''}>
                      {bill.customer_name} - ₹{bill.final_amount} {bill.payment_status === 'paid' ? '✅' : '⏳'}
                    </SelectItem>
                  ))
                }
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={() => {
              const billCustomerIds = bills.map(b => b.customer_id);
              const missingCustomers = customers.filter(c => !billCustomerIds.includes(c.id));
              if (missingCustomers.length === 0) {
                toast.success('All customers have bills!');
              } else {
                const names = missingCustomers.map(c => c.name).join('\n- ');
                alert(`⚠️ ${missingCustomers.length} customers missing bills:\n\n- ${names}\n\nThese customers may not have any deliveries for ${selectedMonth}.`);
              }
            }} className="text-orange-600 border-orange-300">
              ⚠️ Check Missing ({customers.length - bills.length})
            </Button>
          </div>
          
          {/* Bulk Actions Toolbar */}
          {selectedBills.length > 0 && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-center justify-between">
              <span className="text-blue-700 font-medium">
                ✓ {selectedBills.length} customer(s) selected
              </span>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" className="bg-white text-green-600 border-green-300" onClick={() => {
                  const selectedBillsList = bills.filter(b => selectedBills.includes(b.id));
                  selectedBillsList.forEach(bill => shareBillToCustomer(bill));
                  toast.success(`Sending bills to ${selectedBills.length} customers...`);
                }}>
                  📤 Send All Bills
                </Button>
                <Button size="sm" variant="outline" className="bg-white text-emerald-600 border-emerald-300" onClick={async () => {
                  if (!window.confirm(`Mark ${selectedBills.length} bills as PAID?`)) return;
                  for (const billId of selectedBills) {
                    const bill = bills.find(b => b.id === billId);
                    if (bill && bill.payment_status !== 'paid') {
                      try {
                        await axios.put(`${API}/monthly-bills/${billId}/payment`, {
                          amount: bill.final_amount,
                          payment_date: new Date().toISOString().split('T')[0]
                        });
                      } catch (e) { console.error('Failed to mark paid:', e); }
                    }
                  }
                  toast.success(`Marked ${selectedBills.length} bills as paid`);
                  setSelectedBills([]);
                  fetchData();
                }}>
                  ✅ Mark All Paid
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setSelectedBills([])}>
                  ✕ Clear
                </Button>
              </div>
            </div>
          )}
          
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader><TableRow>
                  <TableHead className="w-10">
                    <input 
                      type="checkbox" 
                      className="w-4 h-4 rounded border-gray-300"
                      checked={selectedBills.length === bills.filter(b => billFilter === 'all' ? true : billFilter === 'paid' ? b.payment_status === 'paid' : b.payment_status !== 'paid').filter(b => b.customer_name?.toLowerCase().includes(searchTerm.toLowerCase())).length && selectedBills.length > 0}
                      onChange={(e) => {
                        if (e.target.checked) {
                          const filteredBills = bills.filter(b => billFilter === 'all' ? true : billFilter === 'paid' ? b.payment_status === 'paid' : b.payment_status !== 'paid').filter(b => b.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()));
                          setSelectedBills(filteredBills.map(b => b.id));
                        } else {
                          setSelectedBills([]);
                        }
                      }}
                    />
                  </TableHead>
                  <TableHead>Customer</TableHead><TableHead className="text-center">Days</TableHead><TableHead className="text-right">🐃</TableHead><TableHead className="text-right">🐄</TableHead><TableHead className="text-right">🥣</TableHead><TableHead className="text-right">V.T</TableHead><TableHead className="text-right">V.S</TableHead><TableHead className="text-right">V.G</TableHead><TableHead className="text-right">Amount</TableHead><TableHead className="text-right">Final</TableHead><TableHead>Status</TableHead><TableHead>Actions</TableHead></TableRow></TableHeader>
                <TableBody>
                  {bills
                    .filter(b => billFilter === 'all' ? true : billFilter === 'paid' ? b.payment_status === 'paid' : b.payment_status !== 'paid')
                    .filter(b => b.customer_name?.toLowerCase().includes(searchTerm.toLowerCase())).length === 0 ? <TableRow><TableCell colSpan={13} className="text-center py-8 text-gray-500">{bills.length === 0 ? 'No bills for this month. Click "Generate Bills" to create.' : 'No customer found matching search or filter.'}</TableCell></TableRow> :
                  bills
                    .filter(b => billFilter === 'all' ? true : billFilter === 'paid' ? b.payment_status === 'paid' : b.payment_status !== 'paid')
                    .filter(b => b.customer_name?.toLowerCase().includes(searchTerm.toLowerCase())).map(bill => (
                    <TableRow key={bill.id} className={`${bill.payment_status === 'pending' ? 'bg-red-50' : bill.payment_status === 'partial' ? 'bg-yellow-50' : ''} ${selectedBills.includes(bill.id) ? 'bg-blue-100' : ''}`}>
                      <TableCell>
                        <input 
                          type="checkbox" 
                          className="w-4 h-4 rounded border-gray-300"
                          checked={selectedBills.includes(bill.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedBills([...selectedBills, bill.id]);
                            } else {
                              setSelectedBills(selectedBills.filter(id => id !== bill.id));
                            }
                          }}
                        />
                      </TableCell>
                      <TableCell className="font-medium">{bill.customer_name}</TableCell>
                      <TableCell className="text-center">{bill.total_deliveries}</TableCell>
                      <TableCell className="text-right">{bill.buffalo_total || 0}L</TableCell>
                      <TableCell className="text-right">{bill.cow_total || 0}L</TableCell>
                      <TableCell className="text-right">{bill.curd_total || 0}L</TableCell>
                      <TableCell className="text-right">{bill.vijaya_toned_total || 0}L</TableCell>
                      <TableCell className="text-right">{bill.vijaya_standard_total || 0}L</TableCell>
                      <TableCell className="text-right">{bill.vijaya_gold_total || 0}L</TableCell>
                      <TableCell className="text-right">₹{bill.total_amount}</TableCell>
                      <TableCell className="text-right font-bold">₹{bill.final_amount}</TableCell>
                      <TableCell>
                        <Badge variant={bill.payment_status === 'paid' ? 'default' : bill.payment_status === 'partial' ? 'secondary' : 'destructive'} 
                               className={bill.payment_status === 'paid' ? 'bg-emerald-600' : ''}>
                          {bill.payment_status}
                        </Badge>
                        {bill.payment_date && <span className="block text-xs text-gray-500 mt-1">Paid: {bill.payment_date}</span>}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1 flex-wrap">
                          <Button size="sm" variant="default" className="bg-blue-600 hover:bg-blue-700 text-white" onClick={() => openEditBillDialog(bill)} title="Edit Bill">
                            ✏️ Edit
                          </Button>
                          <Button size="sm" variant={bill.payment_status === 'paid' ? 'outline' : 'default'} className={bill.payment_status === 'paid' ? 'text-emerald-600 border-emerald-300' : 'bg-emerald-600 hover:bg-emerald-700 text-white'} onClick={async () => {
                            setBillLoyalty(null);
                            setBillPaymentDialog({ open: true, bill: bill });
                            try { const r = await axios.get(`${API}/loyalty/customer/${bill.customer_id}`); setBillLoyalty(r.data); } catch(e) {}
                          }} title={bill.payment_status === 'paid' ? 'Edit Payment' : 'Record Payment'}>
                            {bill.payment_status === 'paid' ? '✓ Paid' : '💰 Pay'}
                          </Button>
                          <Button size="sm" variant="outline" className="text-blue-600" onClick={async () => {
                            try {
                              const res = await axios.put(`${API}/monthly-bills/${bill.id}/recalculate`);
                              toast.success(res.data.message);
                              fetchData();
                            } catch (e) { toast.error('Failed to recalculate'); }
                          }} title="Recalculate Bill">
                            🔄
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => shareBillToCustomer(bill)} title="Share to Customer" className="text-blue-600">
                            📤
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => shareBillToAdmin(bill)} title="Share to Admin" className="text-purple-600">
                            👤
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => sendWhatsAppReminder(customers.find(c => c.id === bill.customer_id), bill)} title="WhatsApp Reminder" className="text-green-600">
                            📱
                          </Button>
                          <Button size="sm" variant="outline" onClick={async () => {
                            const customer = customers.find(c => c.id === bill.customer_id);
                            if (!customer?.email) {
                              toast.error(`${bill.customer_name} has no email address. Add email in customer details.`);
                              return;
                            }
                            if (!window.confirm(`📧 Send bill to ${customer.email}?`)) return;
                            try {
                              const res = await axios.post(`${API}/email/send-bill?customer_id=${bill.customer_id}&month=${bill.month}`);
                              if (res.data.success) {
                                toast.success(`📧 Bill sent to ${customer.email}`);
                              } else {
                                toast.error(res.data.error || 'Failed to send email');
                              }
                            } catch (e) { toast.error(e.response?.data?.detail || 'Failed to send email'); }
                          }} title="Email Bill" className="text-orange-600">
                            📧
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => printInvoice(bill)} title="Print Invoice">
                            🖨️
                          </Button>
                          <Button size="sm" variant="outline" className="text-red-600 hover:bg-red-50" onClick={async () => {
                            if (window.confirm(`🗑️ Delete bill for ${bill.customer_name} (${bill.month})?\n\nThis cannot be undone!`)) {
                              try {
                                await axios.delete(`${API}/monthly-bills/${bill.id}`);
                                toast.success('Bill deleted');
                                fetchData();
                              } catch (e) { toast.error('Failed to delete bill'); }
                            }
                          }} title="Delete Bill">
                            🗑️
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
          
          {/* Bill Payment Dialog */}
          <Dialog open={billPaymentDialog.open} onOpenChange={(open) => !open && setBillPaymentDialog({ open: false, bill: null })}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{billPaymentDialog.bill?.payment_status === 'paid' ? '✏️ Edit Payment' : '💰 Record Payment'}</DialogTitle>
              </DialogHeader>
              {billPaymentDialog.bill && (
                <div className="space-y-4">
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <p className="font-medium">{billPaymentDialog.bill.customer_name}</p>
                    <p className="text-sm text-gray-500">Bill for: {billPaymentDialog.bill.month}</p>
                    {billPaymentDialog.bill.payment_status === 'paid' && (
                      <Badge className="bg-emerald-600 mt-2">Currently Paid</Badge>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div className="bg-blue-50 p-2 rounded">
                      <Label className="text-gray-500 text-xs">Bill Amount (₹) - Editable</Label>
                      <Input 
                        type="number" 
                        id="bill-amount"
                        defaultValue={billPaymentDialog.bill.final_amount}
                        min="0"
                        className="font-bold text-lg h-10 mt-1"
                        onChange={async (e) => {
                          const newAmount = parseFloat(e.target.value) || 0;
                          try {
                            await axios.put(`${API}/monthly-bills/${billPaymentDialog.bill.id}/update-amount?amount=${newAmount}`);
                          } catch (err) { console.error('Failed to update bill amount:', err); }
                        }}
                      />
                    </div>
                    <div className="bg-yellow-50 p-2 rounded">
                      <p className="text-gray-500 text-xs">{billPaymentDialog.bill.payment_status === 'paid' ? 'Previously Paid' : 'Already Paid'}</p>
                      <p className="font-bold text-lg mt-2">₹{billPaymentDialog.bill.amount_paid || 0}</p>
                    </div>
                  </div>
                  <div>
                    <Label>{billPaymentDialog.bill.payment_status === 'paid' ? 'New Payment Amount (₹)' : 'Payment Amount (₹)'}</Label>
                    <Input 
                      type="number" 
                      id="payment-amount"
                      defaultValue={billPaymentDialog.bill.payment_status === 'paid' 
                        ? billPaymentDialog.bill.amount_paid 
                        : (billPaymentDialog.bill.final_amount - (billPaymentDialog.bill.amount_paid || 0))}
                      min="0"
                    />
                    {billPaymentDialog.bill.payment_status === 'paid' && (
                      <p className="text-xs text-amber-600 mt-1">⚠️ This will replace the existing payment amount</p>
                    )}
                  </div>
                  <div>
                    <Label>Payment Date</Label>
                    <Input 
                      type="date" 
                      id="payment-date"
                      defaultValue={billPaymentDialog.bill.payment_date || new Date().toISOString().split('T')[0]}
                    />
                  </div>
                  {billPaymentDialog.bill.payment_status !== 'paid' && billLoyalty && billLoyalty.current_points >= 10 && (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                      <Label className="text-amber-700 font-semibold">🌟 Redeem Loyalty Points — Available: {billLoyalty.current_points} pts (worth ₹{Math.floor(billLoyalty.current_points / 10)})</Label>
                      <Input 
                        type="number" 
                        id="redeem-points" 
                        placeholder="Points to redeem (10 pts = ₹1 discount)" 
                        min="0" 
                        max={billLoyalty.current_points}
                        className="mt-2"
                        data-testid="redeem-points-input"
                      />
                      <p className="text-xs text-amber-600 mt-1">Discount is added to the payment automatically</p>
                    </div>
                  )}
                  <Button 
                    className="w-full bg-emerald-600 hover:bg-emerald-700"
                    onClick={async () => {
                      const billAmount = parseFloat(document.getElementById('bill-amount').value) || 0;
                      const paymentAmount = document.getElementById('payment-amount').value;
                      const paymentDate = document.getElementById('payment-date').value;
                      const isPaidBill = billPaymentDialog.bill.payment_status === 'paid';
                      
                      // First update bill amount if changed
                      if (billAmount !== billPaymentDialog.bill.final_amount) {
                        try {
                          await axios.put(`${API}/monthly-bills/${billPaymentDialog.bill.id}/update-amount?amount=${billAmount}`);
                        } catch (err) { console.error('Failed to update bill amount:', err); }
                      }
                      
                      // Record or update payment
                      if (paymentAmount && parseFloat(paymentAmount) >= 0) {
                        const redeemEl = document.getElementById('redeem-points');
                        const redeemPoints = redeemEl ? (parseInt(redeemEl.value) || 0) : 0;
                        recordPayment(
                          billPaymentDialog.bill.customer_id, 
                          paymentAmount, 
                          'monthly', 
                          billPaymentDialog.bill.month, 
                          paymentDate,
                          isPaidBill,  // replace_payment = true for paid bills
                          redeemPoints
                        );
                      } else {
                        toast.error('Please enter a valid payment amount');
                      }
                    }}
                  >
                    {billPaymentDialog.bill.payment_status === 'paid' ? '✏️ Update Payment' : '💰 Record Payment'}
                  </Button>
                </div>
              )}
            </DialogContent>
          </Dialog>

          {/* Edit Bill Dialog (Days & Quantities) */}
          <Dialog open={editBillDialog.open} onOpenChange={(open) => !open && setEditBillDialog({ open: false, bill: null })}>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Edit className="w-5 h-5 text-blue-500" />
                  Edit Bill - {editBillDialog.bill?.customer_name}
                </DialogTitle>
                <DialogDescription>
                  Update days, quantities and amount for {editBillDialog.bill?.month}
                </DialogDescription>
              </DialogHeader>
              {editBillDialog.bill && (
                <div className="space-y-4 py-4">
                  {/* Days and Main Products */}
                  <div className="grid grid-cols-4 gap-3">
                    <div>
                      <Label className="text-xs">Days</Label>
                      <Input 
                        type="number" 
                        value={editBillData.total_deliveries}
                        onChange={(e) => setEditBillData({...editBillData, total_deliveries: e.target.value})}
                        min="0" max="31"
                        className="mt-1 h-9"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">🐃 Buffalo (₹)</Label>
                      <Input 
                        type="number" 
                        value={editBillData.buffalo_total}
                        onChange={(e) => setEditBillData({...editBillData, buffalo_total: e.target.value})}
                        min="0"
                        className="mt-1 h-9"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">🐄 Cow (₹)</Label>
                      <Input 
                        type="number" 
                        value={editBillData.cow_total}
                        onChange={(e) => setEditBillData({...editBillData, cow_total: e.target.value})}
                        min="0"
                        className="mt-1 h-9"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">🥣 Curd (₹)</Label>
                      <Input 
                        type="number" 
                        value={editBillData.curd_total}
                        onChange={(e) => setEditBillData({...editBillData, curd_total: e.target.value})}
                        min="0"
                        className="mt-1 h-9"
                      />
                    </div>
                  </div>
                  
                  {/* Vijaya Products */}
                  <div className="grid grid-cols-4 gap-3">
                    <div>
                      <Label className="text-xs">V.T - Vijaya Toned (₹)</Label>
                      <Input 
                        type="number" 
                        value={editBillData.vijaya_toned_total}
                        onChange={(e) => setEditBillData({...editBillData, vijaya_toned_total: e.target.value})}
                        min="0"
                        className="mt-1 h-9"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">V.S - Vijaya Standard (₹)</Label>
                      <Input 
                        type="number" 
                        value={editBillData.vijaya_standard_total}
                        onChange={(e) => setEditBillData({...editBillData, vijaya_standard_total: e.target.value})}
                        min="0"
                        className="mt-1 h-9"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">V.G - Vijaya Gold (₹)</Label>
                      <Input 
                        type="number" 
                        value={editBillData.vijaya_gold_total}
                        onChange={(e) => setEditBillData({...editBillData, vijaya_gold_total: e.target.value})}
                        min="0"
                        className="mt-1 h-9"
                      />
                    </div>
                    <div>
                      <Label className="text-xs font-bold text-emerald-600">Final Amount (₹)</Label>
                      <Input 
                        type="number" 
                        value={editBillData.final_amount}
                        onChange={(e) => setEditBillData({...editBillData, final_amount: e.target.value})}
                        min="0"
                        className="mt-1 h-9 border-emerald-300"
                      />
                    </div>
                  </div>

                  {/* Original Values Reference */}
                  <div className="bg-gray-50 p-3 rounded-lg text-xs">
                    <p className="font-semibold mb-2">Original Values:</p>
                    <div className="grid grid-cols-4 gap-2 text-gray-600">
                      <span>Days: {editBillDialog.bill.total_deliveries || 0}</span>
                      <span>Buffalo: ₹{editBillDialog.bill.buffalo_total || 0}</span>
                      <span>Cow: ₹{editBillDialog.bill.cow_total || 0}</span>
                      <span>Curd: ₹{editBillDialog.bill.curd_total || 0}</span>
                      <span>V.T: ₹{editBillDialog.bill.vijaya_toned_total || 0}</span>
                      <span>V.S: ₹{editBillDialog.bill.vijaya_standard_total || 0}</span>
                      <span>V.G: ₹{editBillDialog.bill.vijaya_gold_total || 0}</span>
                      <span className="font-bold">Final: ₹{editBillDialog.bill.final_amount || 0}</span>
                    </div>
                  </div>

                  <div className="bg-blue-50 p-3 rounded-lg flex justify-between items-center">
                    <span className="text-sm text-blue-700">
                      <strong>Status:</strong> {editBillDialog.bill.payment_status === 'paid' ? '✅ Paid' : '⏳ Pending'}
                    </span>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => {
                        const sum = 
                          parseFloat(editBillData.buffalo_total || 0) +
                          parseFloat(editBillData.cow_total || 0) +
                          parseFloat(editBillData.curd_total || 0) +
                          parseFloat(editBillData.vijaya_toned_total || 0) +
                          parseFloat(editBillData.vijaya_standard_total || 0) +
                          parseFloat(editBillData.vijaya_gold_total || 0);
                        setEditBillData({...editBillData, final_amount: sum});
                      }}
                    >
                      🔄 Auto Calculate Amount
                    </Button>
                  </div>
                </div>
              )}
              <DialogFooter>
                <Button variant="outline" onClick={() => setEditBillDialog({ open: false, bill: null })}>Cancel</Button>
                <Button className="bg-blue-600 hover:bg-blue-700" onClick={saveBillEdits}>
                  Save Changes
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      )}

      {/* Analytics Tab */}
      {activeTab === 'analytics' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
              <CardContent className="p-4">
                <p className="text-blue-100 text-sm">Monthly Revenue (Est.)</p>
                <p className="text-3xl font-bold">₹{(analytics.totalDailyRevenue * 30).toLocaleString()}</p>
                <p className="text-blue-200 text-xs mt-1">Based on {analytics.totalDailyQuantity}L/day</p>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-emerald-500 to-emerald-600 text-white">
              <CardContent className="p-4">
                <p className="text-emerald-100 text-sm">Avg. Quantity/Customer</p>
                <p className="text-3xl font-bold">{analytics.avgQuantityPerCustomer}L</p>
                <p className="text-emerald-200 text-xs mt-1">Per day</p>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white">
              <CardContent className="p-4">
                <p className="text-purple-100 text-sm">Total Advance Collected</p>
                <p className="text-3xl font-bold">₹{analytics.totalAdvanceBalance.toLocaleString()}</p>
                <p className="text-purple-200 text-xs mt-1">From all customers</p>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-orange-500 to-orange-600 text-white">
              <CardContent className="p-4">
                <p className="text-orange-100 text-sm">Pending Collection</p>
                <p className="text-3xl font-bold">₹{(summary.pending_amount || 0).toLocaleString()}</p>
                <p className="text-orange-200 text-xs mt-1">This month</p>
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Milk Type Distribution */}
            <Card>
              <CardHeader><CardTitle>Milk Type Distribution</CardTitle></CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="flex items-center gap-2">🐃 Buffalo Milk</span>
                    <span className="font-bold">{analytics.buffaloCustomers} customers</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-4">
                    <div className="bg-blue-600 h-4 rounded-full" style={{width: `${customers.length > 0 ? (analytics.buffaloCustomers / customers.length * 100) : 0}%`}}></div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="flex items-center gap-2">🐄 Cow Milk</span>
                    <span className="font-bold">{analytics.cowCustomers} customers</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-4">
                    <div className="bg-emerald-600 h-4 rounded-full" style={{width: `${customers.length > 0 ? (analytics.cowCustomers / customers.length * 100) : 0}%`}}></div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Delivery Time Distribution */}
            <Card>
              <CardHeader><CardTitle>Delivery Time Distribution</CardTitle></CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="flex items-center gap-2">🌅 Morning (7-9 AM)</span>
                    <span className="font-bold">{analytics.morningCustomers} customers</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-4">
                    <div className="bg-orange-500 h-4 rounded-full" style={{width: `${customers.length > 0 ? (analytics.morningCustomers / customers.length * 100) : 0}%`}}></div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="flex items-center gap-2">🌆 Evening (7-8 PM)</span>
                    <span className="font-bold">{analytics.eveningCustomers} customers</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-4">
                    <div className="bg-purple-500 h-4 rounded-full" style={{width: `${customers.length > 0 ? (analytics.eveningCustomers / customers.length * 100) : 0}%`}}></div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Area-wise Distribution */}
            <Card className="lg:col-span-2">
              <CardHeader><CardTitle>Area-wise Customer Distribution</CardTitle></CardHeader>
              <CardContent>
                {analytics.byArea.length === 0 ? (
                  <p className="text-center text-gray-500 py-4">No area data available. Add areas to customers to see distribution.</p>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {analytics.byArea.map((item, i) => (
                      <div key={item.area} className="p-4 bg-gray-50 rounded-lg">
                        <p className="font-semibold text-gray-800">{item.area}</p>
                        <p className="text-2xl font-bold text-emerald-600">{item.count}</p>
                        <p className="text-sm text-gray-500">{item.quantity}L/day</p>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Quick Stats */}
          <Card className="bg-gradient-to-r from-gray-50 to-gray-100">
            <CardHeader><CardTitle>📊 Quick Stats Summary</CardTitle></CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                <div className="p-4">
                  <p className="text-3xl font-bold text-gray-800">{customers.length}</p>
                  <p className="text-sm text-gray-500">Total Customers</p>
                </div>
                <div className="p-4">
                  <p className="text-3xl font-bold text-blue-600">{analytics.totalDailyQuantity}L</p>
                  <p className="text-sm text-gray-500">Daily Delivery</p>
                </div>
                <div className="p-4">
                  <p className="text-3xl font-bold text-emerald-600">₹{analytics.totalDailyRevenue}</p>
                  <p className="text-sm text-gray-500">Daily Revenue</p>
                </div>
                <div className="p-4">
                  <p className="text-3xl font-bold text-purple-600">₹{(analytics.totalDailyRevenue * 30).toLocaleString()}</p>
                  <p className="text-sm text-gray-500">Monthly Revenue</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Payment Dialog */}
      <Dialog open={paymentDialog.open} onOpenChange={(open) => setPaymentDialog({ open, customer: paymentDialog.customer })}>
        <DialogContent>
          <DialogHeader><DialogTitle>Record Payment - {paymentDialog.customer?.name}</DialogTitle></DialogHeader>
          {paymentDialog.customer && (
            <div className="space-y-4">
              <div className="p-4 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-500">Current Advance Balance</p>
                <p className="text-xl font-bold text-emerald-600">₹{paymentDialog.customer.advance_balance || 0}</p>
              </div>
              <div className="space-y-2">
                <Label>Add Advance Payment</Label>
                <div className="flex gap-2">
                  <Input id="advanceAmount" type="number" placeholder="Enter amount" />
                  <Button onClick={() => {
                    const amt = document.getElementById('advanceAmount').value;
                    if (amt) recordPayment(paymentDialog.customer.id, amt, 'advance');
                  }} className="bg-emerald-600 hover:bg-emerald-700">Add Advance</Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ==========================================
// CUSTOMER SHOP COMPONENTS
// ==========================================

const CustomerProtectedRoute = ({ children }) => {
  const { customer, loading } = useCustomerAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center bg-blue-50"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div></div>;
  if (!customer) return <Navigate to="/shop/login" replace />;
  return children;
};

// Shop Header
const ShopHeader = () => {
  const { customer, logout, cart } = useCustomerAuth();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <header className="bg-white shadow-sm sticky top-0 z-50">
      {/* Top bar with contact */}
      <div className="bg-blue-600 text-white text-sm py-1">
        <div className="max-w-7xl mx-auto px-4 flex justify-between items-center">
          <span>🥛 Freshness at your doorsteps - Hyderabad</span>
          <div className="flex items-center gap-4">
            <a href="tel:8125995777" className="hover:underline">📞 8125995777</a>
            <a href="https://wa.me/917075769452" target="_blank" rel="noreferrer" className="hover:underline flex items-center gap-1">
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
              WhatsApp
            </a>
          </div>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-4 py-3">
        <div className="flex items-center justify-between">
          <Link to="/shop" className="flex items-center gap-2">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-cyan-400 rounded-full flex items-center justify-center">
              <span className="text-white text-xl">🥛</span>
            </div>
            <span className="font-bold text-xl text-gray-800">Freshwala</span>
          </Link>
          
          <nav className="hidden md:flex items-center gap-6">
            <Link to="/shop" className="text-gray-600 hover:text-blue-600">Home</Link>
            <Link to="/shop/products" className="text-gray-600 hover:text-blue-600">Products</Link>
            <Link to="/shop/subscriptions" className="text-gray-600 hover:text-blue-600">Subscriptions</Link>
            <Link to="/shop/pay-bill" className="text-emerald-600 font-medium hover:text-emerald-700" data-testid="nav-pay-bill">Pay Bill</Link>
          </nav>

          <div className="flex items-center gap-4">
            <Link to="/shop/cart" className="relative">
              <ShoppingCart className="w-6 h-6 text-gray-600" />
              {cart.items?.length > 0 && (
                <span className="absolute -top-2 -right-2 w-5 h-5 bg-blue-600 text-white text-xs rounded-full flex items-center justify-center">
                  {cart.items.length}
                </span>
              )}
            </Link>
            
            {customer ? (
              <div className="relative">
                <button onClick={() => setMenuOpen(!menuOpen)} className="flex items-center gap-2 text-gray-600">
                  <User className="w-6 h-6" />
                  <span className="hidden md:inline">{customer.name}</span>
                </button>
                {menuOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg py-2 z-50">
                    <Link to="/shop/orders" className="block px-4 py-2 hover:bg-gray-100" onClick={() => setMenuOpen(false)}>My Orders</Link>
                    <Link to="/shop/account" className="block px-4 py-2 hover:bg-gray-100" onClick={() => setMenuOpen(false)}>My Account</Link>
                    <Link to="/shop/my-subscription" className="block px-4 py-2 hover:bg-gray-100 text-emerald-600 font-medium" onClick={() => setMenuOpen(false)}>🥛 My Milk Subscription</Link>
                    <Link to="/shop/subscriptions" className="block px-4 py-2 hover:bg-gray-100" onClick={() => setMenuOpen(false)}>Subscribe & Save</Link>
                    <Link to="/shop/pay-bill" className="block px-4 py-2 hover:bg-gray-100 text-emerald-600 font-medium" onClick={() => setMenuOpen(false)}>Pay My Bill</Link>
                    <hr className="my-2" />
                    <button onClick={() => { logout(); navigate('/shop'); setMenuOpen(false); }} className="block w-full text-left px-4 py-2 text-red-600 hover:bg-gray-100">Logout</button>
                  </div>
                )}
              </div>
            ) : (
              <Link to="/shop/login"><Button size="sm" className="bg-blue-600 hover:bg-blue-700">Login</Button></Link>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

// Shop Layout
const ShopLayout = ({ children }) => (
  <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white">
    <ShopHeader />
    <main>{children}</main>
    <footer className="bg-gray-800 text-white py-8 mt-12">
      <div className="max-w-7xl mx-auto px-4 text-center">
        <p className="text-lg font-semibold mb-2">🥛 Freshwala Dairy</p>
        <p className="text-gray-400">Fresh dairy products delivered to your doorstep</p>
        <div className="flex justify-center gap-6 mt-4">
          <a href="tel:8125995777" className="text-blue-400 hover:text-blue-300">📞 8125995777</a>
          <a href="https://wa.me/917075769452" target="_blank" rel="noreferrer" className="text-green-400 hover:text-green-300 flex items-center gap-1">
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
              WhatsApp
            </a>
          <a href="mailto:info@freshwala.online" className="text-yellow-400 hover:text-yellow-300">✉️ Email</a>
        </div>
        <p className="text-gray-500 text-sm mt-2">Hyderabad | info@freshwala.online</p>
        <div className="mt-4">
          <Link to="/login" className="text-blue-400 hover:underline text-sm">Admin Login</Link>
        </div>
      </div>
    </footer>
  </div>
);

// Pay My Bill Page (public)
const PayBillPage = () => {
  const [phone, setPhone] = useState('');
  const [bills, setBills] = useState(null);
  const [customerName, setCustomerName] = useState('');
  const [loading, setLoading] = useState(false);
  const [showQRFor, setShowQRFor] = useState(null);
  const [successInfo, setSuccessInfo] = useState(null);

  const lookupBills = async () => {
    if (phone.replace(/\D/g, '').length < 10) { toast.error('Enter a valid 10-digit phone number'); return; }
    setLoading(true); setBills(null); setSuccessInfo(null); setShowQRFor(null);
    try {
      const res = await axios.get(`${API}/public/pending-bills`, { params: { phone } });
      setBills(res.data.bills);
      setCustomerName(res.data.customer_name);
      if (res.data.bills.length === 0) toast.success('No pending bills — all clear! 🎉');
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not find your bills'); }
    setLoading(false);
  };

  const payWithRazorpay = async (bill) => {
    try {
      const res = await axios.post(`${API}/public/bills/${bill.bill_id}/create-razorpay-order`);
      const options = {
        key: res.data.razorpay_key,
        amount: Math.round(res.data.amount * 100),
        currency: 'INR',
        name: 'Freshwala Dairy',
        description: `Milk bill - ${bill.month}`,
        order_id: res.data.razorpay_order_id,
        handler: async (response) => {
          try {
            const vr = await axios.post(`${API}/public/bills/verify-payment`, {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature
            });
            setSuccessInfo(vr.data);
            toast.success('Payment successful! 🎉');
            lookupBills();
          } catch (e) { toast.error('Payment verification failed — please contact us'); }
        },
        theme: { color: '#10B981' }
      };
      new window.Razorpay(options).open();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not start payment'); }
  };

  return (
    <ShopLayout>
      <div className="max-w-2xl mx-auto px-4 py-10" data-testid="pay-bill-page">
        <h1 className="text-3xl font-bold text-gray-800 text-center">💳 Pay My Milk Bill</h1>
        <p className="text-gray-500 text-center mt-2 mb-8">Enter your registered phone number to view and pay your pending monthly bills</p>
        <div className="flex gap-2">
          <Input 
            type="tel" 
            placeholder="10-digit phone number" 
            value={phone} 
            onChange={e => setPhone(e.target.value)} 
            onKeyDown={e => e.key === 'Enter' && lookupBills()}
            data-testid="pay-bill-phone-input"
          />
          <Button onClick={lookupBills} disabled={loading} className="bg-emerald-600 hover:bg-emerald-700" data-testid="pay-bill-lookup-btn">
            {loading ? 'Searching...' : 'Find My Bills'}
          </Button>
        </div>

        {successInfo && (
          <div className="mt-6 bg-emerald-50 border border-emerald-200 rounded-lg p-4 text-center" data-testid="pay-bill-success">
            <p className="font-bold text-emerald-700 text-lg">✅ ₹{successInfo.amount_paid} paid for {successInfo.month}!</p>
            {successInfo.loyalty && (
              <p className="text-sm text-amber-600 mt-1">🌟 You earned +{successInfo.loyalty.points_earned} loyalty points (balance: {successInfo.loyalty.new_balance})</p>
            )}
          </div>
        )}

        {bills && bills.length > 0 && (
          <div className="mt-8 space-y-4">
            <p className="font-semibold text-gray-700">Hi {customerName}, you have {bills.length} pending bill{bills.length > 1 ? 's' : ''}:</p>
            {bills.map(bill => (
              <Card key={bill.bill_id} data-testid={`pay-bill-card-${bill.bill_id}`}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between flex-wrap gap-3">
                    <div>
                      <p className="font-bold text-gray-800">{bill.month}</p>
                      <p className="text-sm text-gray-500">Bill: ₹{bill.final_amount.toLocaleString()} | Paid: ₹{bill.amount_paid.toLocaleString()}</p>
                      <p className="text-lg font-bold text-red-600">Due: ₹{bill.due_amount.toLocaleString()}</p>
                    </div>
                    <div className="flex flex-col gap-2">
                      <Button className="bg-blue-600 hover:bg-blue-700" onClick={() => payWithRazorpay(bill)} data-testid={`pay-online-btn-${bill.bill_id}`}>
                        Pay Online ₹{bill.due_amount.toLocaleString()}
                      </Button>
                      <Button variant="outline" onClick={() => setShowQRFor(showQRFor === bill.bill_id ? null : bill.bill_id)} data-testid={`show-qr-btn-${bill.bill_id}`}>
                        {showQRFor === bill.bill_id ? 'Hide UPI QR' : 'Pay via UPI QR'}
                      </Button>
                    </div>
                  </div>
                  {showQRFor === bill.bill_id && (
                    <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg text-center">
                      <p className="text-sm font-semibold text-gray-700 mb-2">Scan & pay ₹{bill.due_amount.toLocaleString()} — then send screenshot on WhatsApp</p>
                      <img src={CONFIG.PAYMENT_QR} alt="Freshwala Payment QR" className="w-52 mx-auto rounded-lg shadow bg-white p-2" data-testid="paybill-qr-img" />
                      <p className="text-xs text-gray-500 mt-2">UPI ID: {CONFIG.UPI_ID} | Paytm: {CONFIG.GPAY_NUMBER}</p>
                      <a 
                        href={`https://wa.me/${CONFIG.ADMIN_WHATSAPP}?text=${encodeURIComponent(`Hi, I paid ₹${bill.due_amount} via UPI QR for my ${bill.month} milk bill (${customerName}). Sending payment screenshot.`)}`}
                        target="_blank" rel="noreferrer"
                        className="inline-block mt-2 text-sm text-green-600 font-medium hover:underline"
                      >
                        📲 Send payment confirmation on WhatsApp
                      </a>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
        {bills && bills.length === 0 && (
          <div className="mt-8 text-center text-gray-500" data-testid="pay-bill-no-dues">
            <p className="text-4xl mb-2">🎉</p>
            <p className="font-semibold">Hi {customerName}, you have no pending bills!</p>
          </div>
        )}
      </div>
    </ShopLayout>
  );
};

// Buy Now deep link page: /buy/{product-slug-or-id}?qty=N -> add to cart -> checkout
const slugifyName = (s) => String(s).toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');

const BuyNowPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { token, loading, addToCart } = useCustomerAuth();
  const [state, setState] = useState('working');

  useEffect(() => {
    if (loading) return;
    (async () => {
      try {
        const res = await axios.get(`${SHOP_API}/products`);
        const cleanSlug = decodeURIComponent(slug).toLowerCase();
        const product = res.data.find(p => p.id === slug || slugifyName(p.name) === cleanSlug);
        if (!product) { setState('notfound'); return; }
        const qty = Math.max(1, parseInt(new URLSearchParams(window.location.search).get('qty'), 10) || 1);
        if (!token) {
          sessionStorage.setItem('pending_buy', `${slugifyName(product.name)}${qty > 1 ? `?qty=${qty}` : ''}`);
          toast.info('Login or create a quick account to complete your order');
          navigate('/shop/login');
          return;
        }
        sessionStorage.removeItem('pending_buy');
        await addToCart(product.id, qty);
        navigate('/shop/checkout');
      } catch (e) { setState('notfound'); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, token, slug]);

  return (
    <ShopLayout>
      <div className="min-h-[60vh] flex items-center justify-center px-4" data-testid="buy-now-page">
        {state === 'working' ? (
          <div className="text-center">
            <div className="animate-spin w-10 h-10 border-4 border-emerald-500 border-t-transparent rounded-full mx-auto mb-4" />
            <p className="text-gray-600 font-medium">Preparing your order...</p>
          </div>
        ) : (
          <div className="text-center" data-testid="buy-now-notfound">
            <p className="text-4xl mb-2">😕</p>
            <p className="font-semibold text-gray-700">Product not found</p>
            <Link to="/shop/products"><Button className="mt-4 bg-emerald-600 hover:bg-emerald-700">Browse All Products</Button></Link>
          </div>
        )}
      </div>
    </ShopLayout>
  );
};

// Shop Home Page
const ShopHomePage = () => {
  const [products, setProducts] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [showHolidayPopup, setShowHolidayPopup] = useState(false);
  const { addToCart, customer } = useCustomerAuth();
  const navigate = useNavigate();

  useEffect(() => { 
    fetchProducts(); 
    fetchActiveHolidays();
    fetchActiveAnnouncements();
  }, []);
  
  const fetchActiveHolidays = async () => {
    try {
      const res = await axios.get(`${API}/holidays/active`);
      setHolidays(res.data);
      if (res.data.length > 0) {
        setShowHolidayPopup(true);
      }
    } catch(e) { console.error('Failed to fetch holidays:', e); }
  };
  
  const fetchActiveAnnouncements = async () => {
    try {
      const res = await axios.get(`${API}/announcements/active`);
      setAnnouncements(res.data);
      if (res.data.length > 0 && !sessionStorage.getItem('ann_viewed')) {
        sessionStorage.setItem('ann_viewed', '1');
        axios.post(`${API}/announcements/track-views`, { ids: res.data.map(a => a.id) }).catch(() => {});
      }
    } catch(e) { console.error('Failed to fetch announcements:', e); }
  };
  
  const fetchProducts = async () => { try { const res = await axios.get(`${SHOP_API}/products`); setProducts(res.data.slice(0, 8)); } catch(e) { console.error('Failed to fetch products:', e); } };

  const handleAddToCart = (productId) => {
    if (!customer) { navigate('/shop/login'); return; }
    addToCart(productId);
  };

  return (
    <ShopLayout>
      {/* Holiday Notice Banner */}
      {holidays.length > 0 && (
        <div className="bg-orange-500 text-white py-3 px-4">
          <div className="max-w-7xl mx-auto flex items-center justify-center gap-3 flex-wrap">
            <span className="text-xl">🔔</span>
            <div className="text-center">
              {holidays.map((h, i) => (
                <span key={h.id}>
                  <strong>{h.title}</strong>: {h.message} (📅 {h.date})
                  {i < holidays.length - 1 && ' | '}
                </span>
              ))}
            </div>
          </div>
        </div>
      )}
      
      {/* Promotions & Offers Banner */}
      {announcements.length > 0 && (
        <div className="bg-gradient-to-r from-purple-600 to-pink-500 text-white py-3 px-4">
          <div className="max-w-7xl mx-auto flex items-center justify-center gap-3 flex-wrap">
            <span className="text-xl animate-bounce">🎉</span>
            <div className="text-center">
              {announcements.map((a, i) => (
                <span key={a.id}>
                  <strong>{a.title}</strong>: {a.message}
                  {a.end_date && ` (Valid till ${a.end_date})`}
                  {i < announcements.length - 1 && ' | '}
                </span>
              ))}
            </div>
            <span className="text-xl animate-bounce">💰</span>
          </div>
          {announcements.some(a => a.media_url) && (
            <div className="max-w-7xl mx-auto flex items-start justify-center gap-4 flex-wrap mt-2">
              {announcements.filter(a => a.media_url).map(a => (
                <div key={a.id} className="flex flex-col items-center gap-2">
                  {a.media_type === 'video' ? (
                    <video src={`${BACKEND_URL}${a.media_url}`} autoPlay muted loop playsInline controls ref={el => { if (el) { el.muted = true; el.play().catch(() => {}); } }} className="h-32 rounded-lg border-2 border-white/50" data-testid={`banner-media-${a.id}`} />
                  ) : (
                    <img src={`${BACKEND_URL}${a.media_url}`} alt={a.title} className="h-32 rounded-lg border-2 border-white/50 object-cover" data-testid={`banner-media-${a.id}`} />
                  )}
                  {a.cta_link && (
                    <Link to={a.cta_link} data-testid={`banner-cta-${a.id}`}>
                      <Button size="sm" className="bg-white text-purple-700 hover:bg-purple-100 font-bold rounded-full px-5">
                        {a.cta_text || 'Order Now'} <ChevronRight className="w-4 h-4 ml-1" />
                      </Button>
                    </Link>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Holiday Popup Notification */}
      <Dialog open={showHolidayPopup} onOpenChange={setShowHolidayPopup}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-orange-600">
              <Bell className="w-6 h-6" /> Important Notices
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {holidays.map(holiday => (
              <div key={holiday.id} className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                <p className="font-bold text-lg text-orange-700">🔔 {holiday.title}</p>
                <p className="text-gray-700">{holiday.message}</p>
                <p className="text-sm text-orange-500 mt-2">📅 {holiday.date}</p>
              </div>
            ))}
            {announcements.map(ann => (
              <div key={ann.id} className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                <p className="font-bold text-lg text-purple-700">
                  {ann.announcement_type === 'promotion' ? '🎉' : ann.announcement_type === 'offer' ? '💰' : '📢'} {ann.title}
                </p>
                {ann.media_url && (
                  ann.media_type === 'video' ? (
                    <video src={`${BACKEND_URL}${ann.media_url}`} controls playsInline className="w-full max-h-56 rounded-lg my-2" data-testid={`popup-media-${ann.id}`} />
                  ) : (
                    <img src={`${BACKEND_URL}${ann.media_url}`} alt={ann.title} className="w-full max-h-56 object-contain rounded-lg my-2" data-testid={`popup-media-${ann.id}`} />
                  )
                )}
                <p className="text-gray-700">{ann.message}</p>
                {ann.end_date && <p className="text-sm text-purple-500 mt-2">Valid: {ann.date} → {ann.end_date}</p>}
                {ann.cta_link && (
                  <Link to={ann.cta_link} onClick={() => setShowHolidayPopup(false)} data-testid={`popup-cta-${ann.id}`}>
                    <Button size="sm" className="mt-3 bg-purple-600 hover:bg-purple-700 w-full font-bold">
                      {ann.cta_text || 'Order Now'} <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </Link>
                )}
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button onClick={() => setShowHolidayPopup(false)} className="bg-purple-500 hover:bg-purple-600 w-full">
              Got it!
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-blue-600 to-cyan-500 text-white py-20">
        <div className="max-w-7xl mx-auto px-4">
          <div className="max-w-2xl">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">Freshness at<br/>Your Doorsteps</h1>
            <p className="text-xl mb-8 text-blue-100">Pure & fresh milk, curd, ghee, and paneer from local farms to your doorstep.</p>
            <div className="flex gap-4">
              <Link to="/shop/products"><Button size="lg" className="bg-white text-blue-600 hover:bg-blue-50">Shop Now</Button></Link>
              <Link to="/shop/subscriptions"><Button size="lg" variant="outline" className="border-white text-white hover:bg-white/20">Subscribe & Save</Button></Link>
            </div>
          </div>
        </div>
        <div className="absolute right-0 top-0 w-1/3 h-full hidden lg:block opacity-20">
          <div className="text-[200px] transform rotate-12 mt-10">🥛</div>
        </div>
      </section>

      {/* Categories */}
      <section className="py-12 max-w-7xl mx-auto px-4">
        <h2 className="text-2xl font-bold text-center mb-8">Our Categories</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
          {[
            { name: 'Milk', icon: '🥛' },
            { name: 'Curd', icon: '🥣' },
            { name: 'Ghee', icon: '🧈' },
            { name: 'Paneer', icon: '🧀' },
            { name: 'Pickles', image: 'https://customer-assets.emergentagent.com/job_15199004-28da-4fd9-96c3-06baada2ea38/artifacts/vh64s80p_image.png' },
            { name: 'Batters', image: 'https://customer-assets.emergentagent.com/job_15199004-28da-4fd9-96c3-06baada2ea38/artifacts/dz5uv2zx_image.png' },
            { name: 'Others', image: 'https://images.unsplash.com/photo-1606787366850-de6330128bfc?w=400' }
          ].map(cat => (
            <Link key={cat.name} to={`/shop/products?category=${cat.name}`} className="bg-white rounded-xl p-4 text-center shadow-sm hover:shadow-md transition-shadow">
              {cat.image ? (
                <div className="w-16 h-16 mx-auto mb-2 rounded-full overflow-hidden">
                  <img src={cat.image} alt={cat.name} className="w-full h-full object-cover" />
                </div>
              ) : (
                <div className="text-4xl mb-2">{cat.icon}</div>
              )}
              <p className="font-medium text-gray-800">{cat.name}</p>
            </Link>
          ))}
        </div>
      </section>

      {/* Featured Products */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-2xl font-bold text-center mb-8">Featured Products</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {products.map(product => (
              <Card key={product.id} className="overflow-hidden hover:shadow-lg transition-shadow">
                <div className="h-40 bg-gray-100">
                  <img src={product.image_url || defaultImages[product.category] || defaultImages.default} alt={product.name} className="w-full h-full object-cover" />
                </div>
                <CardContent className="p-4">
                  <Badge variant="outline" className="mb-2">{product.category}</Badge>
                  <h3 className="font-semibold text-lg">{product.name}</h3>
                  <p className="text-2xl font-bold text-blue-600 mt-2">₹{product.price}<span className="text-sm text-gray-500 font-normal">/{product.unit}</span></p>
                </CardContent>
                <CardFooter className="p-4 pt-0">
                  <Button className="w-full bg-blue-600 hover:bg-blue-700" onClick={() => handleAddToCart(product.id)}>
                    <ShoppingCart className="w-4 h-4 mr-2" /> Add to Cart
                  </Button>
                </CardFooter>
              </Card>
            ))}
          </div>
          <div className="text-center mt-8">
            <Link to="/shop/products"><Button variant="outline" size="lg">View All Products</Button></Link>
          </div>
        </div>
      </section>

      {/* Subscription Banner */}
      <section className="py-12 bg-gradient-to-r from-cyan-500 to-blue-600 text-white">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4">🥛 Daily Milk Subscription</h2>
          <p className="text-xl mb-6 text-blue-100">Never run out of milk! Subscribe for daily delivery and save up to 10%</p>
          <Link to="/shop/subscriptions"><Button size="lg" className="bg-white text-blue-600 hover:bg-blue-50">Start Subscription</Button></Link>
        </div>
      </section>
    </ShopLayout>
  );
};

// Shop Products Page
const ShopProductsPage = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const location = useLocation();
  const [category, setCategory] = useState(() => new URLSearchParams(location.search).get('category') || 'all');
  const { addToCart, customer } = useCustomerAuth();
  const navigate = useNavigate();

  useEffect(() => { fetchProducts(); }, [category]);
  const fetchProducts = async () => {
    try {
      const url = category === 'all' ? `${SHOP_API}/products` : `${SHOP_API}/products?category=${category}`;
      const res = await axios.get(url);
      setProducts(res.data);
    } catch(e) { toast.error('Failed to load products'); }
    finally { setLoading(false); }
  };

  const handleAddToCart = (productId) => {
    if (!customer) { navigate('/shop/login'); return; }
    addToCart(productId);
  };

  return (
    <ShopLayout>
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-8">
          <h1 className="text-3xl font-bold text-gray-800 mb-4 md:mb-0">Our Products</h1>
          <div className="flex gap-2 flex-wrap">
            {['all', 'Milk', 'Curd', 'Ghee', 'Paneer', 'Pickles', 'Batters', 'Others'].map(cat => (
              <Button key={cat} variant={category === cat ? 'default' : 'outline'} size="sm" className={category === cat ? 'bg-blue-600' : ''} onClick={() => setCategory(cat)}>
                {cat === 'all' ? 'All' : cat}
              </Button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div></div>
        ) : products.length === 0 ? (
          <div className="text-center py-12 text-gray-500">No products available</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {products.map(product => (
              <Card key={product.id} className="overflow-hidden hover:shadow-lg transition-shadow">
                <div className="h-48 bg-gray-100">
                  <img src={product.image_url || defaultImages[product.category] || defaultImages.default} alt={product.name} className="w-full h-full object-cover" />
                </div>
                <CardContent className="p-4">
                  <Badge variant="outline" className="mb-2">{product.category}</Badge>
                  <h3 className="font-semibold text-lg">{product.name}</h3>
                  {product.description && <p className="text-sm text-gray-500 mt-1">{product.description}</p>}
                  <p className="text-2xl font-bold text-blue-600 mt-2">₹{product.price}<span className="text-sm text-gray-500 font-normal">/{product.unit}</span></p>
                  <p className="text-sm text-green-600 mt-1">In Stock: {product.stock_quantity} {product.unit}</p>
                </CardContent>
                <CardFooter className="p-4 pt-0">
                  <Button className="w-full bg-blue-600 hover:bg-blue-700" onClick={() => handleAddToCart(product.id)}>
                    <ShoppingCart className="w-4 h-4 mr-2" /> Add to Cart
                  </Button>
                </CardFooter>
              </Card>
            ))}
          </div>
        )}
      </div>
    </ShopLayout>
  );
};

// Shop Login Page
const ShopLoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [phone, setPhone] = useState('');
  const [name, setName] = useState('');
  const [isRegister, setIsRegister] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login, register, customer } = useCustomerAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (customer) {
      const pendingBuy = sessionStorage.getItem('pending_buy');
      navigate(pendingBuy ? `/buy/${pendingBuy}` : '/shop');
    }
  }, [customer, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (isRegister) { await register(name, email, password, phone); toast.success('Welcome to Freshwala!'); }
      else { await login(email, password); toast.success('Welcome back!'); }
      const pendingBuy = sessionStorage.getItem('pending_buy');
      navigate(pendingBuy ? `/buy/${pendingBuy}` : '/shop');
    } catch (error) { toast.error(error.response?.data?.detail || 'Authentication failed'); }
    finally { setLoading(false); }
  };

  return (
    <ShopLayout>
      <div className="min-h-[80vh] flex items-center justify-center py-12 px-4">
        <Card className="w-full max-w-md shadow-xl">
          <CardHeader className="text-center">
            <div className="mx-auto w-16 h-16 bg-gradient-to-br from-blue-500 to-cyan-400 rounded-full flex items-center justify-center mb-4">
              <span className="text-3xl">🥛</span>
            </div>
            <CardTitle className="text-2xl text-gray-800">{isRegister ? 'Create Account' : 'Welcome Back'}</CardTitle>
            <CardDescription>{isRegister ? 'Join Freshwala for fresh dairy delivery' : 'Login to your account'}</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {isRegister && (
                <>
                  <div className="space-y-2"><Label>Full Name</Label><Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" required /></div>
                  <div className="space-y-2"><Label>Phone Number</Label><Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="10-digit mobile number" required /></div>
                </>
              )}
              <div className="space-y-2"><Label>Email</Label><Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="your@email.com" required /></div>
              <div className="space-y-2">
                <Label>Password</Label>
                <div className="relative">
                  <Input 
                    type={showPassword ? "text" : "password"} 
                    value={password} 
                    onChange={(e) => setPassword(e.target.value)} 
                    placeholder="••••••••" 
                    required 
                  />
                  <button 
                    type="button" 
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700" disabled={loading}>
                {loading ? 'Please wait...' : (isRegister ? 'Create Account' : 'Login')}
              </Button>
            </form>
            <div className="mt-4 text-center">
              <button type="button" className="text-blue-600 hover:underline text-sm" onClick={() => setIsRegister(!isRegister)}>
                {isRegister ? 'Already have an account? Login' : "Don't have an account? Register"}
              </button>
            </div>
          </CardContent>
        </Card>
      </div>
    </ShopLayout>
  );
};

// Shop Cart Page
const ShopCartPage = () => {
  const { cart, updateCartItem, clearCart, customer } = useCustomerAuth();
  const navigate = useNavigate();

  if (!customer) return <Navigate to="/shop/login" />;

  const subtotal = cart.items?.reduce((sum, item) => sum + (item.price * item.quantity), 0) || 0;
  const deliveryCharge = 15; // Fixed ₹15 delivery within 2-3 km
  const total = subtotal + deliveryCharge;
  const minOrder = 100;

  return (
    <ShopLayout>
      <div className="max-w-4xl mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold text-gray-800 mb-8">Shopping Cart</h1>
        
        {cart.items?.length === 0 ? (
          <Card className="text-center py-12">
            <CardContent>
              <ShoppingCart className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 mb-4">Your cart is empty</p>
              <Link to="/shop/products"><Button className="bg-blue-600">Browse Products</Button></Link>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-4">
              {cart.items.map(item => (
                <Card key={item.product_id}>
                  <CardContent className="p-4 flex items-center gap-4">
                    <div className="w-20 h-20 bg-gray-100 rounded-lg overflow-hidden">
                      <img src={item.image_url || defaultImages.default} alt={item.product_name} className="w-full h-full object-cover" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold">{item.product_name}</h3>
                      <p className="text-blue-600 font-bold">₹{item.price}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button variant="outline" size="sm" onClick={() => updateCartItem(item.product_id, item.quantity - 1)}><Minus className="w-4 h-4" /></Button>
                      <span className="w-8 text-center">{item.quantity}</span>
                      <Button variant="outline" size="sm" onClick={() => updateCartItem(item.product_id, item.quantity + 1)}><Plus className="w-4 h-4" /></Button>
                    </div>
                    <p className="font-bold w-20 text-right">₹{(item.price * item.quantity).toFixed(2)}</p>
                  </CardContent>
                </Card>
              ))}
              <Button variant="outline" onClick={clearCart} className="text-red-600"><Trash2 className="w-4 h-4 mr-2" /> Clear Cart</Button>
            </div>
            
            <div>
              <Card>
                <CardHeader><CardTitle>Order Summary</CardTitle></CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex justify-between"><span>Subtotal</span><span>₹{subtotal.toFixed(2)}</span></div>
                  <div className="flex justify-between"><span>Delivery (2-3 km)</span><span>₹{deliveryCharge}</span></div>
                  <hr />
                  <div className="flex justify-between text-lg font-bold"><span>Total</span><span>₹{total.toFixed(2)}</span></div>
                  {subtotal < minOrder && <p className="text-sm text-red-500 mt-2">Minimum order amount is ₹{minOrder}</p>}
                </CardContent>
                <CardFooter>
                  <Button 
                    className="w-full bg-blue-600 hover:bg-blue-700" 
                    onClick={() => navigate('/shop/checkout')}
                    disabled={subtotal < minOrder}
                  >
                    Proceed to Checkout <ChevronRight className="w-4 h-4 ml-2" />
                  </Button>
                </CardFooter>
              </Card>
            </div>
          </div>
        )}
      </div>
    </ShopLayout>
  );
};

// Shop Checkout Page
const ShopCheckoutPage = () => {
  const { cart, customer, getAuthHeaders, fetchCart } = useCustomerAuth();
  const [addresses, setAddresses] = useState([]);
  const [selectedAddress, setSelectedAddress] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('cod');
  const [loading, setLoading] = useState(false);
  const [showAddressForm, setShowAddressForm] = useState(false);
  const [addressForm, setAddressForm] = useState({ label: 'Home', full_name: '', phone: '', address_line1: '', address_line2: '', city: '', state: '', pincode: '', is_default: true });
  const navigate = useNavigate();

  // Local cities that qualify for ₹15 delivery
  const localCities = ['hyderabad', 'secunderabad', 'kukatpally', 'gachibowli', 'hitech city', 'madhapur', 'kondapur', 'miyapur', 'lb nagar', 'dilsukhnagar', 'uppal', 'habsiguda'];

  useEffect(() => { fetchAddresses(); }, []);

  const fetchAddresses = async () => {
    try {
      const res = await axios.get(`${SHOP_API}/addresses`, getAuthHeaders());
      setAddresses(res.data);
      const defaultAddr = res.data.find(a => a.is_default);
      if (defaultAddr) setSelectedAddress(defaultAddr.id);
    } catch(e) { console.error('Failed to fetch addresses:', e); }
  };

  // Calculate delivery fee based on location
  const calculateDeliveryFee = () => {
    const address = addresses.find(a => a.id === selectedAddress);
    if (!address) return { fee: 15, isLocal: true, message: 'Local delivery (Hyderabad)' };
    
    const cityLower = address.city?.toLowerCase() || '';
    const isLocal = localCities.some(c => cityLower.includes(c));
    
    if (isLocal) {
      return { fee: 15, isLocal: true, message: `Local delivery (${address.city})` };
    } else {
      // Out of city: ₹50 base + ₹10 per kg (assume 1kg per item as estimate)
      const estimatedWeight = cart.items?.reduce((sum, item) => sum + item.quantity, 0) || 1;
      const fee = 50 + (estimatedWeight * 10);
      return { fee, isLocal: false, message: `Out of city (${address.city}) - ${estimatedWeight}kg` };
    }
  };

  const addAddress = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${SHOP_API}/addresses`, addressForm, getAuthHeaders());
      toast.success('Address added!');
      setShowAddressForm(false);
      fetchAddresses();
    } catch(e) { toast.error('Failed to add address'); }
  };

  const placeOrder = async () => {
    if (!selectedAddress) { toast.error('Please select a delivery address'); return; }
    setLoading(true);
    try {
      const res = await axios.post(`${SHOP_API}/orders/create`, {
        address_id: selectedAddress,
        payment_method: paymentMethod
      }, getAuthHeaders());

      // Send WhatsApp notifications to admin numbers
      if (res.data.admin_notification_links && res.data.admin_notification_links.length > 0) {
        // Open WhatsApp for first admin number automatically
        window.open(res.data.admin_notification_links[0].link, '_blank');
        // Show toast with link for second admin
        if (res.data.admin_notification_links.length > 1) {
          setTimeout(() => {
            window.open(res.data.admin_notification_links[1].link, '_blank');
          }, 1000);
        }
      }

      if (paymentMethod === 'razorpay' && res.data.razorpay_key) {
        // Load Razorpay
        const options = {
          key: res.data.razorpay_key,
          amount: res.data.order.total_amount * 100,
          currency: 'INR',
          name: 'Freshwala Dairy',
          description: `Order #${res.data.order.order_number}`,
          order_id: res.data.order.razorpay_order_id,
          handler: async (response) => {
            try {
              await axios.post(`${SHOP_API}/orders/verify-payment`, null, {
                ...getAuthHeaders(),
                params: {
                  razorpay_order_id: response.razorpay_order_id,
                  razorpay_payment_id: response.razorpay_payment_id,
                  razorpay_signature: response.razorpay_signature
                }
              });
              toast.success('Payment successful!');
              fetchCart();
              navigate('/shop/orders');
            } catch(e) { toast.error('Payment verification failed'); }
          },
          prefill: { name: customer.name, email: customer.email, contact: customer.phone },
          theme: { color: '#2563eb' }
        };
        const rzp = new window.Razorpay(options);
        rzp.open();
      } else {
        toast.success(paymentMethod === 'upi_qr' ? 'Order placed! Complete payment via the UPI QR if not done yet.' : 'Order placed successfully!');
        fetchCart();
        navigate('/shop/orders');
      }
    } catch(e) { toast.error(e.response?.data?.detail || 'Failed to place order'); }
    finally { setLoading(false); }
  };

  const subtotal = cart.items?.reduce((sum, item) => sum + (item.price * item.quantity), 0) || 0;
  const deliveryInfo = calculateDeliveryFee();
  const deliveryCharge = deliveryInfo.fee;
  const total = subtotal + deliveryCharge;
  const minOrder = 100;

  if (!customer) return <Navigate to="/shop/login" />;
  if (cart.items?.length === 0) return <Navigate to="/shop/cart" />;

  return (
    <ShopLayout>
      <div className="max-w-4xl mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold text-gray-800 mb-8">Checkout</h1>
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-6">
            {/* Delivery Address */}
            <Card>
              <CardHeader><CardTitle className="flex items-center gap-2"><MapPin className="w-5 h-5" /> Delivery Address</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                {addresses.length === 0 ? (
                  <p className="text-gray-500">No saved addresses. Add one below.</p>
                ) : (
                  <RadioGroup value={selectedAddress} onValueChange={setSelectedAddress}>
                    {addresses.map(addr => (
                      <div key={addr.id} className="flex items-start gap-3 p-3 border rounded-lg">
                        <RadioGroupItem value={addr.id} id={addr.id} />
                        <label htmlFor={addr.id} className="flex-1 cursor-pointer">
                          <p className="font-medium">{addr.label} - {addr.full_name}</p>
                          <p className="text-sm text-gray-600">{addr.address_line1}, {addr.city}, {addr.state} - {addr.pincode}</p>
                          <p className="text-sm text-gray-500">📞 {addr.phone}</p>
                        </label>
                      </div>
                    ))}
                  </RadioGroup>
                )}
                
                {showAddressForm ? (
                  <form onSubmit={addAddress} className="space-y-3 p-4 border rounded-lg">
                    <div className="grid grid-cols-2 gap-3">
                      <Input placeholder="Full Name" value={addressForm.full_name} onChange={e => setAddressForm({...addressForm, full_name: e.target.value})} required />
                      <Input placeholder="Phone" value={addressForm.phone} onChange={e => setAddressForm({...addressForm, phone: e.target.value})} required />
                    </div>
                    <Input placeholder="Address Line 1" value={addressForm.address_line1} onChange={e => setAddressForm({...addressForm, address_line1: e.target.value})} required />
                    <Input placeholder="Address Line 2 (optional)" value={addressForm.address_line2} onChange={e => setAddressForm({...addressForm, address_line2: e.target.value})} />
                    <div className="grid grid-cols-3 gap-3">
                      <Input placeholder="City" value={addressForm.city} onChange={e => setAddressForm({...addressForm, city: e.target.value})} required />
                      <Input placeholder="State" value={addressForm.state} onChange={e => setAddressForm({...addressForm, state: e.target.value})} required />
                      <Input placeholder="Pincode" value={addressForm.pincode} onChange={e => setAddressForm({...addressForm, pincode: e.target.value})} required />
                    </div>
                    <div className="flex gap-2">
                      <Button type="submit" className="bg-blue-600">Save Address</Button>
                      <Button type="button" variant="outline" onClick={() => setShowAddressForm(false)}>Cancel</Button>
                    </div>
                  </form>
                ) : (
                  <Button variant="outline" onClick={() => setShowAddressForm(true)}><Plus className="w-4 h-4 mr-2" /> Add New Address</Button>
                )}
              </CardContent>
            </Card>

            {/* Payment Method */}
            <Card>
              <CardHeader><CardTitle className="flex items-center gap-2"><CreditCard className="w-5 h-5" /> Payment Method</CardTitle></CardHeader>
              <CardContent>
                <RadioGroup value={paymentMethod} onValueChange={setPaymentMethod}>
                  <div className="flex items-center gap-3 p-3 border rounded-lg">
                    <RadioGroupItem value="cod" id="cod" />
                    <label htmlFor="cod" className="flex-1 cursor-pointer">
                      <p className="font-medium">Cash on Delivery</p>
                      <p className="text-sm text-gray-500">Pay when you receive your order</p>
                    </label>
                  </div>
                  <div className="flex items-center gap-3 p-3 border rounded-lg mt-2">
                    <RadioGroupItem value="razorpay" id="razorpay" />
                    <label htmlFor="razorpay" className="flex-1 cursor-pointer">
                      <p className="font-medium">Pay Online (Razorpay)</p>
                      <p className="text-sm text-gray-500">UPI, Cards, Net Banking, Wallets</p>
                    </label>
                  </div>
                  <div className="flex items-center gap-3 p-3 border rounded-lg mt-2">
                    <RadioGroupItem value="upi_qr" id="upi_qr" data-testid="payment-upi-qr-radio" />
                    <label htmlFor="upi_qr" className="flex-1 cursor-pointer">
                      <p className="font-medium">Pay via UPI QR (Scan & Pay)</p>
                      <p className="text-sm text-gray-500">Scan Freshwala's Paytm QR with any UPI app</p>
                    </label>
                  </div>
                </RadioGroup>
                {paymentMethod === 'upi_qr' && (
                  <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg text-center" data-testid="checkout-upi-qr-box">
                    <p className="text-sm font-semibold text-gray-700 mb-2">Scan & pay ₹{total.toFixed(2)}, then place your order</p>
                    <img src={CONFIG.PAYMENT_QR} alt="Freshwala Payment QR" className="w-52 mx-auto rounded-lg shadow bg-white p-2" data-testid="checkout-payment-qr" />
                    <p className="text-xs text-gray-500 mt-2">UPI ID: {CONFIG.UPI_ID} | Paytm: {CONFIG.GPAY_NUMBER}</p>
                    <p className="text-xs text-gray-400">We'll confirm your payment when preparing the order</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Order Summary */}
          <div>
            <Card className="sticky top-24">
              <CardHeader><CardTitle>Order Summary</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                {cart.items?.map(item => (
                  <div key={item.product_id} className="flex justify-between text-sm">
                    <span>{item.product_name} x {item.quantity}</span>
                    <span>₹{(item.price * item.quantity).toFixed(2)}</span>
                  </div>
                ))}
                <hr />
                <div className="flex justify-between"><span>Subtotal</span><span>₹{subtotal.toFixed(2)}</span></div>
                <div className="flex justify-between">
                  <span>Delivery</span>
                  <span className={deliveryInfo.isLocal ? 'text-green-600' : 'text-orange-600'}>₹{deliveryCharge}</span>
                </div>
                <p className={`text-xs ${deliveryInfo.isLocal ? 'text-green-600' : 'text-orange-600'}`}>
                  {deliveryInfo.message}
                </p>
                {!deliveryInfo.isLocal && (
                  <p className="text-xs text-gray-500">Out of city: ₹50 base + ₹10/kg</p>
                )}
                <hr />
                <div className="flex justify-between text-lg font-bold"><span>Total</span><span>₹{total.toFixed(2)}</span></div>
                {subtotal < minOrder && <p className="text-sm text-red-500">Minimum order: ₹{minOrder}</p>}
              </CardContent>
              <CardFooter>
                <Button className="w-full bg-blue-600 hover:bg-blue-700" onClick={placeOrder} disabled={loading || !selectedAddress || subtotal < minOrder}>
                  {loading ? 'Processing...' : `Place Order - ₹${total.toFixed(2)}`}
                </Button>
              </CardFooter>
            </Card>
          </div>
        </div>
      </div>
      {/* Razorpay Script */}
      <script src="https://checkout.razorpay.com/v1/checkout.js"></script>
    </ShopLayout>
  );
};

// Simple Order Tracking Component for customer orders
const OrderTracker = ({ status }) => {
  const steps = [
    { key: 'placed', label: 'Order Placed', icon: '📋' },
    { key: 'delivered', label: 'Delivered', icon: '✅' }
  ];
  
  const getStepStatus = (stepKey) => {
    if (status === 'cancelled') return stepKey === 'placed' ? 'cancelled' : 'pending';
    if (status === 'delivered') return 'completed';
    if (stepKey === 'placed') return 'completed';
    return 'pending';
  };

  return (
    <div className="flex items-center justify-between w-full max-w-md mx-auto my-4">
      {steps.map((step, index) => {
        const stepStatus = getStepStatus(step.key);
        return (
          <div key={step.key} className="flex items-center flex-1">
            <div className="flex flex-col items-center">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center text-lg
                ${stepStatus === 'completed' ? 'bg-green-500 text-white' : 
                  stepStatus === 'cancelled' ? 'bg-red-500 text-white' : 
                  'bg-gray-200 text-gray-400'}`}>
                {step.icon}
              </div>
              <span className={`text-xs mt-1 text-center
                ${stepStatus === 'completed' ? 'text-green-600 font-medium' : 
                  stepStatus === 'cancelled' ? 'text-red-600' : 
                  'text-gray-400'}`}>
                {step.label}
              </span>
            </div>
            {index < steps.length - 1 && (
              <div className={`flex-1 h-1 mx-2 rounded
                ${status === 'delivered' ? 'bg-green-500' : 
                  status === 'cancelled' ? 'bg-red-300' : 
                  'bg-gray-200'}`} />
            )}
          </div>
        );
      })}
    </div>
  );
};

// Shop Orders Page
const ShopOrdersPage = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const { getAuthHeaders, customer } = useCustomerAuth();

  useEffect(() => { fetchOrders(); }, []);
  const fetchOrders = async () => {
    try { const res = await axios.get(`${SHOP_API}/orders`, getAuthHeaders()); setOrders(res.data); }
    catch(e) { toast.error('Failed to load orders'); }
    finally { setLoading(false); }
  };

  if (!customer) return <Navigate to="/shop/login" />;

  const statusColors = {
    placed: 'bg-yellow-100 text-yellow-800',
    confirmed: 'bg-blue-100 text-blue-800',
    preparing: 'bg-purple-100 text-purple-800',
    out_for_delivery: 'bg-orange-100 text-orange-800',
    delivered: 'bg-green-100 text-green-800',
    cancelled: 'bg-red-100 text-red-800'
  };

  return (
    <ShopLayout>
      <div className="max-w-4xl mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold text-gray-800 mb-8">My Orders</h1>
        
        {loading ? (
          <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div></div>
        ) : orders.length === 0 ? (
          <Card className="text-center py-12">
            <CardContent>
              <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 mb-4">No orders yet</p>
              <Link to="/shop/products"><Button className="bg-blue-600">Start Shopping</Button></Link>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {orders.map(order => (
              <Card key={order.id} className="overflow-hidden">
                <CardContent className="p-4">
                  <div className="flex flex-col md:flex-row md:items-center justify-between mb-2">
                    <div>
                      <p className="font-mono text-sm text-gray-500">Order #{order.order_number}</p>
                      <p className="text-sm text-gray-500">{new Date(order.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</p>
                    </div>
                    <div className="flex items-center gap-2 mt-2 md:mt-0">
                      <Badge className={statusColors[order.order_status]}>{order.order_status.replace('_', ' ')}</Badge>
                      <Badge variant="outline">{order.payment_method === 'cod' ? 'COD' : 'Paid'}</Badge>
                    </div>
                  </div>
                  
                  {/* Order Tracking Progress */}
                  <OrderTracker status={order.order_status} />
                  
                  <div className="border-t pt-4 mt-2">
                    <div className="flex flex-wrap gap-2 mb-4">
                      {order.items?.map((item, i) => (
                        <span key={i} className="text-sm bg-gray-100 px-2 py-1 rounded">{item.product_name} x {item.quantity}</span>
                      ))}
                    </div>
                    <div className="flex justify-between items-center">
                      <p className="text-sm text-gray-500">📍 {order.address?.city}</p>
                      <p className="text-xl font-bold text-blue-600">₹{order.total_amount}</p>
                    </div>
                    {order.order_status === 'delivered' && (
                      <div className="mt-3 p-3 bg-green-50 rounded-lg text-center">
                        <p className="text-green-700 font-medium">✅ Order Delivered Successfully!</p>
                        <p className="text-sm text-green-600">Thank you for shopping with Freshwala</p>
                      </div>
                    )}
                    {order.order_status === 'placed' && (
                      <div className="mt-3 p-3 bg-yellow-50 rounded-lg text-center">
                        <p className="text-yellow-700 font-medium">⏳ Order is being processed</p>
                        <p className="text-sm text-yellow-600">We'll deliver soon! Contact: {CONFIG.ADMIN_PHONE_2}</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </ShopLayout>
  );
};

// Shop Subscriptions Page
const ShopSubscriptionsPage = () => {
  const { customer, getAuthHeaders, loading: authLoading } = useCustomerAuth();
  const navigate = useNavigate();
  const [showForm, setShowForm] = useState(false);
  const [addresses, setAddresses] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adminSubscription, setAdminSubscription] = useState(null);
  const [hasAdminSub, setHasAdminSub] = useState(false);
  
  // Subscription form state
  const [subForm, setSubForm] = useState({
    milkType: 'buffalo',
    quantity: '1',
    frequency: 'daily',
    customDays: [],
    deliveryTime: 'morning',
    addressId: '',
    startDate: '',
    paymentMethod: 'cod'
  });

  const milkPricePerLiter = 100; // ₹100 per liter for both types
  const quantities = ['0.5', '1', '1.5', '2', '2.5', '3', '4', '5'];
  const frequencies = [
    { value: 'daily', label: 'Daily', description: 'Every day' },
    { value: 'alternate', label: 'Alternate Days', description: 'Mon, Wed, Fri, Sun' },
    { value: 'weekly', label: 'Weekly', description: 'Once a week' },
    { value: 'custom', label: 'Custom Days', description: 'Choose specific days' }
  ];
  const deliveryTimeSlots = [
    { value: 'morning', label: 'Morning', time: '7:00 AM - 9:00 AM', icon: '🌅' },
    { value: 'evening', label: 'Evening', time: '7:00 PM - 8:00 PM', icon: '🌆' }
  ];
  const weekDays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  useEffect(() => {
    if (customer) {
      fetchAllData();
    }
  }, [customer]);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      // First check for admin subscription (linked by phone)
      const adminRes = await axios.get(`${SHOP_API}/my-milk-subscription`, getAuthHeaders());
      if (adminRes.data?.has_subscription) {
        setHasAdminSub(true);
        setAdminSubscription(adminRes.data);
      }
      
      // Also fetch shop subscriptions and addresses
      const [addrRes, subRes] = await Promise.all([
        axios.get(`${SHOP_API}/addresses`, getAuthHeaders()),
        axios.get(`${SHOP_API}/subscriptions`, getAuthHeaders())
      ]);
      setAddresses(addrRes.data);
      setSubscriptions(subRes.data);
      if (addrRes.data.length > 0) {
        const defaultAddr = addrRes.data.find(a => a.is_default) || addrRes.data[0];
        setSubForm(prev => ({ ...prev, addressId: defaultAddr.id }));
      }
    } catch(e) { console.error('Failed to load subscription data:', e); }
    finally { setLoading(false); }
  };

  const fetchAddresses = async () => {
    try {
      const res = await axios.get(`${SHOP_API}/addresses`, getAuthHeaders());
      setAddresses(res.data);
      if (res.data.length > 0) {
        const defaultAddr = res.data.find(a => a.is_default) || res.data[0];
        setSubForm(prev => ({ ...prev, addressId: defaultAddr.id }));
      }
    } catch(e) { console.error('Failed to fetch addresses:', e); }
  };

  const fetchSubscriptions = async () => {
    try {
      const res = await axios.get(`${SHOP_API}/subscriptions`, getAuthHeaders());
      setSubscriptions(res.data);
    } catch(e) { console.error('Failed to fetch subscriptions:', e); }
  };

  const toggleDay = (day) => {
    setSubForm(prev => ({
      ...prev,
      customDays: prev.customDays.includes(day) 
        ? prev.customDays.filter(d => d !== day)
        : [...prev.customDays, day]
    }));
  };

  const calculatePrice = () => {
    const qty = parseFloat(subForm.quantity);
    const pricePerDelivery = qty * milkPricePerLiter;
    
    let deliveriesPerMonth = 30;
    if (subForm.frequency === 'alternate') deliveriesPerMonth = 15;
    else if (subForm.frequency === 'weekly') deliveriesPerMonth = 4;
    else if (subForm.frequency === 'custom') deliveriesPerMonth = subForm.customDays.length * 4;
    
    return {
      perDelivery: pricePerDelivery,
      monthly: pricePerDelivery * deliveriesPerMonth,
      deliveriesPerMonth
    };
  };

  const handleSubscribe = async () => {
    if (!customer) { navigate('/shop/login'); return; }
    if (!subForm.addressId) { toast.error('Please select a delivery address'); return; }
    if (!subForm.startDate) { toast.error('Please select a start date'); return; }
    if (subForm.frequency === 'custom' && subForm.customDays.length === 0) {
      toast.error('Please select at least one delivery day'); return;
    }

    setLoading(true);
    try {
      // Find product ID for selected milk type
      const productsRes = await axios.get(`${SHOP_API}/products`);
      const milkProduct = productsRes.data.find(p => 
        p.name.toLowerCase().includes(subForm.milkType) && p.category === 'Milk'
      );
      
      if (!milkProduct) { toast.error('Product not found'); return; }

      await axios.post(`${SHOP_API}/subscriptions`, {
        product_id: milkProduct.id,
        quantity: parseFloat(subForm.quantity),
        frequency: subForm.frequency === 'custom' ? `custom:${subForm.customDays.join(',')}` : subForm.frequency,
        delivery_time: subForm.deliveryTime,
        address_id: subForm.addressId,
        start_date: subForm.startDate,
        payment_method: subForm.paymentMethod
      }, getAuthHeaders());

      toast.success('Subscription created successfully! 🎉');
      setShowForm(false);
      fetchSubscriptions();
    } catch(e) {
      toast.error(e.response?.data?.detail || 'Failed to create subscription');
    } finally {
      setLoading(false);
    }
  };

  const price = calculatePrice();

  if (authLoading || loading) {
    return <ShopLayout><div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div></div></ShopLayout>;
  }
  
  return (
    <ShopLayout>
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="text-center mb-10">
          <h1 className="text-3xl font-bold text-gray-800 mb-2">🥛 Freshwala Milk Subscriptions</h1>
          <p className="text-gray-600">Fresh Buffalo & Cow Milk delivered to your doorstep</p>
          <p className="text-blue-600 font-medium mt-2">📞 Call: 8125995777 | 💬 WhatsApp: <a href="https://wa.me/917075769452" target="_blank" className="underline">7075769452</a></p>
        </div>

        {/* Show Existing Admin Subscription if customer has one */}
        {hasAdminSub && adminSubscription?.subscription && (
          <Card className="mb-8 border-2 border-emerald-300 bg-emerald-50">
            <CardHeader className="bg-emerald-100">
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle className="text-emerald-700 flex items-center gap-2">
                    <CheckCircle className="w-5 h-5" /> You Have an Active Subscription!
                  </CardTitle>
                  <CardDescription>Your milk delivery is already set up</CardDescription>
                </div>
                <Badge className="bg-green-500 text-white">{adminSubscription.subscription.status?.toUpperCase() || 'ACTIVE'}</Badge>
              </div>
            </CardHeader>
            <CardContent className="p-6">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-white p-4 rounded-lg">
                  <p className="text-sm text-gray-500">Customer Name</p>
                  <p className="font-bold text-lg">{adminSubscription.subscription.name}</p>
                </div>
                <div className="bg-white p-4 rounded-lg">
                  <p className="text-sm text-gray-500">Area</p>
                  <p className="font-bold">{adminSubscription.subscription.area}</p>
                </div>
                <div className="bg-white p-4 rounded-lg">
                  <p className="text-sm text-gray-500">Delivery Time</p>
                  <p className="font-bold">{adminSubscription.subscription.delivery_time || 'Morning'}</p>
                </div>
                <div className="bg-white p-4 rounded-lg">
                  <p className="text-sm text-gray-500">Daily Rate</p>
                  <p className="font-bold text-emerald-600">₹{(
                    (adminSubscription.subscription.buffalo_qty || 0) * (adminSubscription.subscription.buffalo_price || 100) +
                    (adminSubscription.subscription.cow_qty || 0) * (adminSubscription.subscription.cow_price || 100) +
                    (adminSubscription.subscription.curd_qty || 0) * (adminSubscription.subscription.curd_price || 120) +
                    (adminSubscription.subscription.vijaya_toned_qty || 0) * (adminSubscription.subscription.vijaya_toned_price || 60) +
                    (adminSubscription.subscription.vijaya_standard_qty || 0) * (adminSubscription.subscription.vijaya_standard_price || 64) +
                    (adminSubscription.subscription.vijaya_gold_qty || 0) * (adminSubscription.subscription.vijaya_gold_price || 78)
                  ).toFixed(0)}/day</p>
                </div>
              </div>
              
              <div className="bg-white p-4 rounded-lg mb-6">
                <p className="text-sm text-gray-500 mb-2">Daily Products</p>
                <div className="flex flex-wrap gap-2">
                  {adminSubscription.subscription.buffalo_qty > 0 && <Badge className="bg-blue-100 text-blue-700">🐃 Buffalo: {adminSubscription.subscription.buffalo_qty}L</Badge>}
                  {adminSubscription.subscription.cow_qty > 0 && <Badge className="bg-green-100 text-green-700">🐄 Cow: {adminSubscription.subscription.cow_qty}L</Badge>}
                  {adminSubscription.subscription.curd_qty > 0 && <Badge className="bg-yellow-100 text-yellow-700">🥣 Curd: {adminSubscription.subscription.curd_qty}L</Badge>}
                  {adminSubscription.subscription.vijaya_toned_qty > 0 && <Badge className="bg-purple-100 text-purple-700">🥛 V.Toned: {adminSubscription.subscription.vijaya_toned_qty}L</Badge>}
                  {adminSubscription.subscription.vijaya_standard_qty > 0 && <Badge className="bg-indigo-100 text-indigo-700">🥛 V.Std: {adminSubscription.subscription.vijaya_standard_qty}L</Badge>}
                  {adminSubscription.subscription.vijaya_gold_qty > 0 && <Badge className="bg-amber-100 text-amber-700">🥛 V.Gold: {adminSubscription.subscription.vijaya_gold_qty}L</Badge>}
                </div>
              </div>

              {/* Recent Deliveries */}
              {adminSubscription.deliveries?.length > 0 && (
                <div className="mb-6">
                  <h3 className="font-semibold text-gray-700 mb-3">Recent Deliveries</h3>
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Date</TableHead>
                          <TableHead>Products</TableHead>
                          <TableHead className="text-right">Amount</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {adminSubscription.deliveries.slice(0, 7).map((d, i) => (
                          <TableRow key={i}>
                            <TableCell>{d.date}</TableCell>
                            <TableCell className="text-sm">
                              {[
                                d.buffalo_qty > 0 && `🐃 ${d.buffalo_qty}L`,
                                d.cow_qty > 0 && `🐄 ${d.cow_qty}L`,
                                d.curd_qty > 0 && `🥣 ${d.curd_qty}L`,
                                d.vijaya_toned_qty > 0 && `🥛 V.T ${d.vijaya_toned_qty}L`
                              ].filter(Boolean).join(' | ')}
                            </TableCell>
                            <TableCell className="text-right font-medium">₹{d.total_amount}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}

              {/* Pending Bills - PAY NOW */}
              {adminSubscription.bills?.filter(b => b.payment_status !== 'paid').length > 0 && (
                <div className="bg-gradient-to-r from-red-50 to-orange-50 p-4 rounded-lg border-2 border-red-300 mb-4">
                  <h3 className="font-bold text-red-700 mb-4 text-lg flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5" /> Pending Bills - Pay Now
                  </h3>
                  {adminSubscription.bills.filter(b => b.payment_status !== 'paid').map((bill, i) => {
                    const dueAmount = bill.final_amount - (bill.amount_paid || 0);
                    const upiId = "paytm.s274py4@pty";
                    const customerName = adminSubscription.subscription?.name || "Customer";
                    const upiLink = `upi://pay?pa=${upiId}&pn=Freshwala%20Dairy&am=${dueAmount}&cu=INR&tn=Milk%20Bill%20${bill.month}%20-%20${encodeURIComponent(customerName)}`;
                    const qrCodeUrl = "https://customer-assets.emergentagent.com/job_2e7b4cc3-7749-4aa3-9a1d-6120acc6272a/artifacts/bb75uq8t_image.png"; // Freshwala Paytm QR
                    
                    const handleUPIPayment = () => {
                      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
                      if (isMobile) {
                        window.location.href = upiLink;
                        setTimeout(() => { toast.info('If UPI app didn\'t open, scan QR or copy UPI ID'); }, 2000);
                      } else {
                        toast.info('Scan the QR code with your phone\'s UPI app');
                      }
                    };
                    
                    return (
                      <div key={i} className="bg-white p-4 rounded-lg mb-3 last:mb-0 shadow">
                        <div className="flex justify-between items-start mb-3">
                          <div>
                            <p className="font-bold text-gray-800">{bill.month}</p>
                            <p className="text-sm text-gray-500">{bill.total_deliveries} deliveries</p>
                          </div>
                          <div className="text-right">
                            <p className="text-2xl font-bold text-red-600">₹{dueAmount}</p>
                            <p className="text-xs text-gray-500">Amount Due</p>
                          </div>
                        </div>
                        
                        {/* UPI Pay Button */}
                        <Button 
                          className="w-full h-12 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-lg mb-2"
                          onClick={handleUPIPayment}
                        >
                          <CreditCard className="w-5 h-5 mr-2" /> Pay ₹{dueAmount} via UPI
                        </Button>
                        
                        {/* QR Code */}
                        <div className="bg-gray-50 p-3 rounded-lg text-center mb-2">
                          <p className="text-xs text-gray-600 mb-1">📱 Scan to Pay</p>
                          <img src={qrCodeUrl} alt="UPI QR" className="mx-auto w-32 h-32 border rounded" />
                        </div>
                        
                        {/* Alternative options */}
                        <div className="flex gap-2">
                          <Button 
                            variant="outline" 
                            size="sm"
                            className="flex-1 border-green-500 text-green-700"
                            onClick={() => {
                              const msg = `Hi, I want to pay ₹${dueAmount} for bill ${bill.month}. Customer: ${customerName}`;
                              window.open(`https://wa.me/917075769452?text=${encodeURIComponent(msg)}`, '_blank');
                            }}
                          >
                            💬 WhatsApp
                          </Button>
                          <Button 
                            variant="outline"
                            size="sm"
                            className="flex-1 border-blue-500 text-blue-700"
                            onClick={() => {
                              navigator.clipboard?.writeText(upiId);
                              toast.success(`UPI ID: ${upiId}`);
                            }}
                          >
                            📋 Copy UPI
                          </Button>
                        </div>
                        <p className="text-center text-xs text-gray-400 mt-2 break-all">UPI: {upiId}</p>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* All Bills History */}
              {adminSubscription.bills?.length > 0 && (
                <div className="mb-4">
                  <h3 className="font-semibold text-gray-700 mb-3">Monthly Bills History</h3>
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Month</TableHead>
                          <TableHead>Days</TableHead>
                          <TableHead className="text-right">Total</TableHead>
                          <TableHead className="text-right">Paid</TableHead>
                          <TableHead>Status</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {adminSubscription.bills.map((bill, i) => (
                          <TableRow key={i}>
                            <TableCell className="font-medium">{bill.month}</TableCell>
                            <TableCell>{bill.total_deliveries}</TableCell>
                            <TableCell className="text-right">₹{bill.final_amount}</TableCell>
                            <TableCell className="text-right">₹{bill.amount_paid || 0}</TableCell>
                            <TableCell>
                              <Badge className={bill.payment_status === 'paid' ? 'bg-green-500' : bill.payment_status === 'partial' ? 'bg-yellow-500' : 'bg-red-500'}>
                                {bill.payment_status?.toUpperCase()}
                              </Badge>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}

              <div className="mt-4 p-4 bg-blue-50 rounded-lg text-center">
                <p className="text-gray-600">Need to change your subscription?</p>
                <p className="font-semibold text-blue-600">📞 Call: 8125995777 | 💬 WhatsApp us</p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Create Subscription Button - Only show if no admin subscription */}
        {customer && !showForm && !hasAdminSub && (
          <div className="text-center mb-8">
            <Button size="lg" className="bg-blue-600 hover:bg-blue-700" onClick={() => setShowForm(true)}>
              <Plus className="w-5 h-5 mr-2" /> Create Custom Subscription
            </Button>
          </div>
        )}

        {/* Subscription Form - Only show if no admin subscription */}
        {showForm && customer && !hasAdminSub && (
          <Card className="mb-8 border-2 border-blue-200">
            <CardHeader className="bg-gradient-to-r from-blue-50 to-cyan-50">
              <CardTitle className="flex items-center gap-2">
                <Package className="w-5 h-5" /> Create Your Milk Subscription
              </CardTitle>
              <CardDescription>Customize your daily milk delivery</CardDescription>
            </CardHeader>
            <CardContent className="p-6 space-y-6">
              {/* Milk Type */}
              <div className="space-y-2">
                <Label className="text-base font-semibold">1. Select Milk Type</Label>
                <div className="grid grid-cols-2 gap-4">
                  <div 
                    className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${subForm.milkType === 'buffalo' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-blue-300'}`}
                    onClick={() => setSubForm({...subForm, milkType: 'buffalo'})}
                  >
                    <div className="text-3xl mb-2">🐃</div>
                    <h3 className="font-semibold">Buffalo Milk</h3>
                    <p className="text-blue-600 font-bold">₹100/Liter</p>
                  </div>
                  <div 
                    className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${subForm.milkType === 'cow' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-blue-300'}`}
                    onClick={() => setSubForm({...subForm, milkType: 'cow'})}
                  >
                    <div className="text-3xl mb-2">🐄</div>
                    <h3 className="font-semibold">Cow Milk</h3>
                    <p className="text-blue-600 font-bold">₹100/Liter</p>
                  </div>
                </div>
              </div>

              {/* Quantity */}
              <div className="space-y-2">
                <Label className="text-base font-semibold">2. Select Quantity (per delivery)</Label>
                <div className="flex flex-wrap gap-2">
                  {quantities.map(qty => (
                    <Button
                      key={qty}
                      type="button"
                      variant={subForm.quantity === qty ? 'default' : 'outline'}
                      className={subForm.quantity === qty ? 'bg-blue-600' : ''}
                      onClick={() => setSubForm({...subForm, quantity: qty})}
                    >
                      {qty}L
                    </Button>
                  ))}
                </div>
                <p className="text-sm text-gray-500">Price: ₹{parseFloat(subForm.quantity) * milkPricePerLiter} per delivery</p>
              </div>

              {/* Frequency */}
              <div className="space-y-2">
                <Label className="text-base font-semibold">3. Delivery Frequency</Label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {frequencies.map(freq => (
                    <div
                      key={freq.value}
                      className={`p-3 border-2 rounded-lg cursor-pointer text-center transition-all ${subForm.frequency === freq.value ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-blue-300'}`}
                      onClick={() => setSubForm({...subForm, frequency: freq.value})}
                    >
                      <p className="font-semibold">{freq.label}</p>
                      <p className="text-xs text-gray-500">{freq.description}</p>
                    </div>
                  ))}
                </div>
                
                {/* Custom Days Selection */}
                {subForm.frequency === 'custom' && (
                  <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                    <p className="text-sm font-medium mb-2">Select delivery days:</p>
                    <div className="flex flex-wrap gap-2">
                      {weekDays.map(day => (
                        <Button
                          key={day}
                          type="button"
                          size="sm"
                          variant={subForm.customDays.includes(day) ? 'default' : 'outline'}
                          className={subForm.customDays.includes(day) ? 'bg-blue-600' : ''}
                          onClick={() => toggleDay(day)}
                        >
                          {day}
                        </Button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Start Date */}
              <div className="space-y-2">
                <Label className="text-base font-semibold">4. Delivery Time Slot</Label>
                <div className="grid grid-cols-2 gap-4">
                  {deliveryTimeSlots.map(slot => (
                    <div
                      key={slot.value}
                      className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${subForm.deliveryTime === slot.value ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-blue-300'}`}
                      onClick={() => setSubForm({...subForm, deliveryTime: slot.value})}
                    >
                      <div className="text-2xl mb-1">{slot.icon}</div>
                      <h3 className="font-semibold">{slot.label}</h3>
                      <p className="text-sm text-blue-600 font-medium">{slot.time}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Start Date */}
              <div className="space-y-2">
                <Label className="text-base font-semibold">5. Start Date</Label>
                <Input 
                  type="date" 
                  value={subForm.startDate} 
                  onChange={(e) => setSubForm({...subForm, startDate: e.target.value})}
                  min={new Date().toISOString().split('T')[0]}
                  className="max-w-xs"
                />
              </div>

              {/* Delivery Address */}
              <div className="space-y-2">
                <Label className="text-base font-semibold">6. Delivery Address</Label>
                {addresses.length === 0 ? (
                  <div className="p-4 bg-yellow-50 rounded-lg">
                    <p className="text-yellow-800">No saved addresses. Please add an address first.</p>
                    <Button variant="link" className="p-0 text-blue-600" onClick={() => navigate('/shop/account')}>
                      Add Address →
                    </Button>
                  </div>
                ) : (
                  <Select value={subForm.addressId} onValueChange={(v) => setSubForm({...subForm, addressId: v})}>
                    <SelectTrigger><SelectValue placeholder="Select address" /></SelectTrigger>
                    <SelectContent>
                      {addresses.map(addr => (
                        <SelectItem key={addr.id} value={addr.id}>
                          {addr.label} - {addr.address_line1}, {addr.city}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>

              {/* Payment Method */}
              <div className="space-y-2">
                <Label className="text-base font-semibold">7. Payment Method</Label>
                <div className="flex gap-4">
                  <div
                    className={`p-3 border-2 rounded-lg cursor-pointer flex-1 ${subForm.paymentMethod === 'cod' ? 'border-blue-500 bg-blue-50' : 'border-gray-200'}`}
                    onClick={() => setSubForm({...subForm, paymentMethod: 'cod'})}
                  >
                    <p className="font-medium">💵 Cash on Delivery</p>
                    <p className="text-xs text-gray-500">Pay monthly to delivery person</p>
                  </div>
                  <div
                    className={`p-3 border-2 rounded-lg cursor-pointer flex-1 ${subForm.paymentMethod === 'razorpay' ? 'border-blue-500 bg-blue-50' : 'border-gray-200'}`}
                    onClick={() => setSubForm({...subForm, paymentMethod: 'razorpay'})}
                  >
                    <p className="font-medium">💳 Pay Online</p>
                    <p className="text-xs text-gray-500">Monthly auto-debit</p>
                  </div>
                </div>
              </div>

              {/* Price Summary */}
              <div className="p-4 bg-gradient-to-r from-blue-50 to-cyan-50 rounded-lg">
                <h3 className="font-semibold mb-2">📋 Subscription Summary</h3>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <span>Milk Type:</span>
                  <span className="font-medium capitalize">{subForm.milkType} Milk</span>
                  <span>Quantity:</span>
                  <span className="font-medium">{subForm.quantity} Liters</span>
                  <span>Frequency:</span>
                  <span className="font-medium capitalize">{subForm.frequency === 'custom' ? `Custom (${subForm.customDays.length} days/week)` : subForm.frequency}</span>
                  <span>Delivery Time:</span>
                  <span className="font-medium">{subForm.deliveryTime === 'morning' ? '🌅 7:00 AM - 9:00 AM' : '🌆 7:00 PM - 8:00 PM'}</span>
                  <span>Per Delivery:</span>
                  <span className="font-medium">₹{price.perDelivery}</span>
                  <span className="text-base font-semibold">Est. Monthly:</span>
                  <span className="text-base font-bold text-blue-600">₹{price.monthly}</span>
                </div>
              </div>
            </CardContent>
            <CardFooter className="flex gap-3">
              <Button variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
              <Button className="flex-1 bg-blue-600 hover:bg-blue-700" onClick={handleSubscribe} disabled={loading}>
                {loading ? 'Creating...' : 'Start Subscription'} 🚀
              </Button>
            </CardFooter>
          </Card>
        )}

        {/* My Active Subscriptions */}
        {customer && subscriptions.length > 0 && (
          <div className="mb-8">
            <h2 className="text-xl font-bold text-gray-700 mb-4">My Subscriptions</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {subscriptions.map(sub => (
                <Card key={sub.id} className={sub.status === 'active' ? 'border-green-200' : 'border-gray-200'}>
                  <CardContent className="p-4">
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="font-semibold">{sub.product_name}</h3>
                        <p className="text-sm text-gray-600">{sub.quantity}L - {sub.frequency}</p>
                        <p className="text-sm text-gray-500">₹{sub.price_per_unit * sub.quantity}/delivery</p>
                      </div>
                      <Badge className={sub.status === 'active' ? 'bg-green-500' : sub.status === 'paused' ? 'bg-yellow-500' : 'bg-gray-500'}>
                        {sub.status}
                      </Badge>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Price List */}
        <Card className="mb-8">
          <CardHeader className="bg-gray-50">
            <CardTitle>📋 Freshwala Price List</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-0">
              {[
                { name: 'Buffalo Milk', price: '₹100/L', icon: '🐃' },
                { name: 'Cow Milk', price: '₹100/L', icon: '🐄' },
                { name: 'Pot Curd', price: '₹120/Kg', icon: '🥣' },
                { name: 'Buffalo Ghee', price: '₹800/Kg', icon: '🧈' },
                { name: 'Cow Ghee', price: '₹900/Kg', icon: '✨' },
                { name: 'Fresh Paneer', price: '₹480/Kg', icon: '🧀' },
                { name: 'Junnu Box', price: '₹40/100g', icon: '🍮' },
                { name: 'Malai', price: '₹400/Kg', icon: '🥛' },
                { name: 'Butter (Maska)', price: '₹550/Kg', icon: '🧈' },
                { name: 'Cova (Khoya)', price: '₹300/Kg', icon: '🥧' },
                { name: 'Kalakand', price: '₹120/250g', icon: '🍬' },
              ].map((item, i) => (
                <div key={i} className="p-4 border-b border-r flex items-center gap-3">
                  <span className="text-2xl">{item.icon}</span>
                  <div>
                    <p className="font-medium text-sm">{item.name}</p>
                    <p className="text-blue-600 font-bold">{item.price}</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {!customer && (
          <Card className="text-center py-8 bg-gradient-to-r from-blue-50 to-cyan-50">
            <CardContent>
              <p className="text-gray-600 mb-4">Login to create your custom milk subscription</p>
              <Link to="/shop/login"><Button className="bg-blue-600 hover:bg-blue-700">Login / Register</Button></Link>
            </CardContent>
          </Card>
        )}
        
        {/* Contact for subscription */}
        <div className="text-center mt-8 p-6 bg-gray-50 rounded-lg">
          <p className="text-gray-600 mb-2">For subscription inquiries, contact us:</p>
          <div className="flex justify-center gap-6 mt-2">
            <a href="tel:8125995777" className="text-xl font-bold text-blue-600 hover:underline">📞 8125995777</a>
            <a href="https://wa.me/917075769452" target="_blank" rel="noreferrer" className="text-xl font-bold text-green-600 hover:underline">💬 WhatsApp</a>
          </div>
        </div>
      </div>
    </ShopLayout>
  );
};

// Shop Account Page
const ShopAccountPage = () => {
  const { customer, getAuthHeaders, loading: authLoading } = useCustomerAuth();
  const [addresses, setAddresses] = useState([]);
  const [showAddressForm, setShowAddressForm] = useState(false);
  const [addressForm, setAddressForm] = useState({
    label: 'Home',
    full_name: '',
    phone: '',
    address_line1: '',
    address_line2: '',
    city: 'Hyderabad',
    state: 'Telangana',
    pincode: '',
    is_default: false
  });

  useEffect(() => { if (customer) fetchAddresses(); }, [customer]);
  const fetchAddresses = async () => {
    try { const res = await axios.get(`${SHOP_API}/addresses`, getAuthHeaders()); setAddresses(res.data); } catch(e) { console.error('Failed to fetch addresses:', e); }
  };
  const deleteAddress = async (id) => {
    try { await axios.delete(`${SHOP_API}/addresses/${id}`, getAuthHeaders()); toast.success('Deleted'); fetchAddresses(); } catch(e) { toast.error('Failed'); }
  };
  const addAddress = async (e) => {
    e.preventDefault();
    if (!addressForm.full_name || !addressForm.phone || !addressForm.address_line1 || !addressForm.pincode) {
      toast.error('Please fill all required fields');
      return;
    }
    try {
      await axios.post(`${SHOP_API}/addresses`, addressForm, getAuthHeaders());
      toast.success('Address added!');
      setShowAddressForm(false);
      setAddressForm({ label: 'Home', full_name: '', phone: '', address_line1: '', address_line2: '', city: 'Hyderabad', state: 'Telangana', pincode: '', is_default: false });
      fetchAddresses();
    } catch(e) { toast.error('Failed to add address'); }
  };

  if (authLoading) return <ShopLayout><div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div></div></ShopLayout>;
  if (!customer) return <Navigate to="/shop/login" />;

  return (
    <ShopLayout>
      <div className="max-w-4xl mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold text-gray-800 mb-8">My Account</h1>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card>
            <CardHeader><CardTitle>Profile</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center gap-3"><User className="w-5 h-5 text-gray-400" /><span>{customer.name}</span></div>
              <div className="flex items-center gap-3"><Mail className="w-5 h-5 text-gray-400" /><span>{customer.email}</span></div>
              <div className="flex items-center gap-3"><Phone className="w-5 h-5 text-gray-400" /><span>{customer.phone}</span></div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle>Saved Addresses</CardTitle>
                <Button size="sm" onClick={() => setShowAddressForm(!showAddressForm)} className="bg-emerald-600 hover:bg-emerald-700">
                  <Plus className="w-4 h-4 mr-1" /> Add
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              {showAddressForm && (
                <form onSubmit={addAddress} className="space-y-3 p-4 border-2 border-emerald-200 rounded-lg bg-emerald-50">
                  <h4 className="font-medium text-emerald-700">Add New Address</h4>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-xs">Label</Label>
                      <Select value={addressForm.label} onValueChange={v => setAddressForm({...addressForm, label: v})}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Home">Home</SelectItem>
                          <SelectItem value="Office">Office</SelectItem>
                          <SelectItem value="Other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs">Full Name *</Label>
                      <Input value={addressForm.full_name} onChange={e => setAddressForm({...addressForm, full_name: e.target.value})} placeholder="Full name" />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs">Phone *</Label>
                    <Input value={addressForm.phone} onChange={e => setAddressForm({...addressForm, phone: e.target.value})} placeholder="Phone number" />
                  </div>
                  <div>
                    <Label className="text-xs">Address Line 1 *</Label>
                    <Input value={addressForm.address_line1} onChange={e => setAddressForm({...addressForm, address_line1: e.target.value})} placeholder="House/Flat no, Building, Street" />
                  </div>
                  <div>
                    <Label className="text-xs">Address Line 2</Label>
                    <Input value={addressForm.address_line2} onChange={e => setAddressForm({...addressForm, address_line2: e.target.value})} placeholder="Area, Landmark (optional)" />
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <Label className="text-xs">City</Label>
                      <Input value={addressForm.city} onChange={e => setAddressForm({...addressForm, city: e.target.value})} />
                    </div>
                    <div>
                      <Label className="text-xs">State</Label>
                      <Input value={addressForm.state} onChange={e => setAddressForm({...addressForm, state: e.target.value})} />
                    </div>
                    <div>
                      <Label className="text-xs">Pincode *</Label>
                      <Input value={addressForm.pincode} onChange={e => setAddressForm({...addressForm, pincode: e.target.value})} placeholder="500XXX" />
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <input type="checkbox" checked={addressForm.is_default} onChange={e => setAddressForm({...addressForm, is_default: e.target.checked})} />
                    <Label className="text-xs">Set as default address</Label>
                  </div>
                  <div className="flex gap-2">
                    <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700">Save Address</Button>
                    <Button type="button" variant="outline" onClick={() => setShowAddressForm(false)}>Cancel</Button>
                  </div>
                </form>
              )}
              {addresses.length === 0 && !showAddressForm ? (
                <p className="text-gray-500">No saved addresses. Click "Add" to add one.</p>
              ) : (
                addresses.map(addr => (
                  <div key={addr.id} className="p-3 border rounded-lg">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-medium">{addr.label} {addr.is_default && <Badge className="ml-2">Default</Badge>}</p>
                        <p className="text-sm text-gray-600">{addr.full_name}</p>
                        <p className="text-sm text-gray-600">{addr.address_line1}</p>
                        {addr.address_line2 && <p className="text-sm text-gray-600">{addr.address_line2}</p>}
                        <p className="text-sm text-gray-600">{addr.city}, {addr.state} - {addr.pincode}</p>
                        <p className="text-sm text-gray-500">{addr.phone}</p>
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => deleteAddress(addr.id)}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                    </div>
                  </div>
                ))
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </ShopLayout>
  );
};

// Customer Milk Subscription Page - Shows subscription from admin panel
const ShopMyMilkSubscriptionPage = () => {
  const { customer, getAuthHeaders, loading: authLoading } = useCustomerAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { if (customer) fetchSubscription(); }, [customer]);
  
  const fetchSubscription = async () => {
    try {
      const res = await axios.get(`${SHOP_API}/my-milk-subscription`, getAuthHeaders());
      setData(res.data);
    } catch(e) { toast.error('Failed to load subscription'); }
    finally { setLoading(false); }
  };

  if (authLoading || loading) return <ShopLayout><div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div></div></ShopLayout>;
  if (!customer) return <Navigate to="/shop/login" />;

  return (
    <ShopLayout>
      <div className="max-w-4xl mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold text-gray-800 mb-2">My Milk Subscription</h1>
        <p className="text-gray-500 mb-6">View your daily milk delivery subscription details</p>

        {!data?.has_subscription ? (
          <Card className="p-8 text-center">
            <Package className="w-16 h-16 mx-auto text-gray-400 mb-4" />
            <h2 className="text-xl font-semibold text-gray-700 mb-2">No Active Subscription</h2>
            <p className="text-gray-500 mb-4">You don't have an active milk subscription linked to this phone number.</p>
            <p className="text-sm text-gray-400">Contact Freshwala at <strong>8125995777</strong> to start your daily milk subscription!</p>
          </Card>
        ) : (
          <div className="space-y-6">
            {/* Subscription Details */}
            <Card>
              <CardHeader className="bg-emerald-50">
                <div className="flex justify-between items-center">
                  <CardTitle className="text-emerald-700">Subscription Details</CardTitle>
                  <Badge className={data.subscription?.status === 'active' ? 'bg-green-500' : 'bg-gray-500'}>
                    {data.subscription?.status?.toUpperCase() || 'ACTIVE'}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="p-6">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Name</p>
                    <p className="font-semibold">{data.subscription?.name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Phone</p>
                    <p className="font-semibold">{data.subscription?.phone}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Area</p>
                    <p className="font-semibold">{data.subscription?.area}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Daily Rate</p>
                    <p className="font-semibold text-emerald-600">₹{(
                      (data.subscription?.buffalo_qty || 0) * (data.subscription?.buffalo_price || 100) +
                      (data.subscription?.cow_qty || 0) * (data.subscription?.cow_price || 100) +
                      (data.subscription?.curd_qty || 0) * (data.subscription?.curd_price || 120) +
                      (data.subscription?.vijaya_toned_qty || 0) * (data.subscription?.vijaya_toned_price || 60) +
                      (data.subscription?.vijaya_standard_qty || 0) * (data.subscription?.vijaya_standard_price || 64) +
                      (data.subscription?.vijaya_gold_qty || 0) * (data.subscription?.vijaya_gold_price || 78)
                    ).toFixed(0)}/day</p>
                  </div>
                </div>
                <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                  <p className="text-sm text-gray-500">Daily Products</p>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {data.subscription?.buffalo_qty > 0 && <Badge variant="outline">🐃 Buffalo: {data.subscription?.buffalo_qty}L</Badge>}
                    {data.subscription?.cow_qty > 0 && <Badge variant="outline">🐄 Cow: {data.subscription?.cow_qty}L</Badge>}
                    {data.subscription?.curd_qty > 0 && <Badge variant="outline">🥣 Curd: {data.subscription?.curd_qty}L</Badge>}
                    {data.subscription?.vijaya_toned_qty > 0 && <Badge variant="outline">🥛 V.Toned: {data.subscription?.vijaya_toned_qty}L</Badge>}
                    {data.subscription?.vijaya_standard_qty > 0 && <Badge variant="outline">🥛 V.Standard: {data.subscription?.vijaya_standard_qty}L</Badge>}
                    {data.subscription?.vijaya_gold_qty > 0 && <Badge variant="outline">🥛 V.Gold: {data.subscription?.vijaya_gold_qty}L</Badge>}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Recent Deliveries */}
            <Card>
              <CardHeader>
                <CardTitle>Recent Deliveries (Last 30)</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {data.deliveries?.length === 0 ? (
                  <p className="p-6 text-gray-500 text-center">No recent deliveries found</p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>🐃</TableHead>
                        <TableHead>🐄</TableHead>
                        <TableHead>🥣</TableHead>
                        <TableHead>🥛 V.T</TableHead>
                        <TableHead className="text-right">Amount</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {data.deliveries?.slice(0, 10).map((d, i) => (
                        <TableRow key={i}>
                          <TableCell>{d.date}</TableCell>
                          <TableCell>{d.buffalo_qty || 0}L</TableCell>
                          <TableCell>{d.cow_qty || 0}L</TableCell>
                          <TableCell>{d.curd_qty || 0}L</TableCell>
                          <TableCell>{d.vijaya_toned_qty || 0}L</TableCell>
                          <TableCell className="text-right font-medium">₹{d.total_amount}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>

            {/* PENDING BILL - PAY NOW (Prominent Section) */}
            {data.bills?.filter(b => b.payment_status !== 'paid').length > 0 && (
              <Card className="border-2 border-red-400 bg-gradient-to-r from-red-50 to-orange-50">
                <CardHeader className="bg-red-100 pb-2">
                  <CardTitle className="text-red-700 flex items-center gap-2">
                    <AlertTriangle className="w-6 h-6" />
                    Pending Payment - Pay Now
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  {data.bills?.filter(b => b.payment_status !== 'paid').map((bill, i) => {
                    const dueAmount = bill.final_amount - (bill.amount_paid || 0);
                    const upiId = "paytm.s274py4@pty"; // Freshwala Paytm Business UPI
                    const customerName = data.subscription?.name || "Customer";
                    const upiLink = `upi://pay?pa=${upiId}&pn=Freshwala%20Dairy&am=${dueAmount}&cu=INR&tn=Milk%20Bill%20${bill.month}%20-%20${encodeURIComponent(customerName)}`;
                    const qrCodeUrl = "https://customer-assets.emergentagent.com/job_2e7b4cc3-7749-4aa3-9a1d-6120acc6272a/artifacts/bb75uq8t_image.png"; // Freshwala Paytm QR
                    
                    const handleUPIPayment = () => {
                      // Try to open UPI app
                      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
                      
                      if (isMobile) {
                        // On mobile, try to open UPI app
                        window.location.href = upiLink;
                        
                        // Fallback message after 2 seconds if app didn't open
                        setTimeout(() => {
                          toast.info('If UPI app didn\'t open, use the QR code or copy UPI ID below');
                        }, 2000);
                      } else {
                        // On desktop, show message to scan QR
                        toast.info('Scan the QR code below with your phone\'s UPI app (GPay/PhonePe/Paytm)');
                      }
                    };
                    
                    return (
                      <div key={i} className="mb-4 last:mb-0">
                        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4">
                          <div>
                            <p className="text-lg font-bold text-gray-800">Bill: {bill.month}</p>
                            <p className="text-sm text-gray-600">{bill.total_deliveries} deliveries</p>
                            <p className="text-sm text-gray-500">Total: ₹{bill.final_amount} | Paid: ₹{bill.amount_paid || 0}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-3xl font-bold text-red-600">₹{dueAmount}</p>
                            <p className="text-sm text-gray-500">Amount Due</p>
                          </div>
                        </div>
                        
                        {/* Payment Buttons */}
                        <div className="space-y-3">
                          {/* UPI Pay Button */}
                          <Button 
                            className="w-full h-14 text-lg bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 shadow-lg"
                            onClick={handleUPIPayment}
                          >
                            <CreditCard className="w-6 h-6 mr-2" />
                            Pay ₹{dueAmount} via UPI
                          </Button>
                          
                          {/* QR Code for Payment */}
                          <div className="bg-white p-4 rounded-lg border text-center">
                            <p className="text-sm font-medium text-gray-700 mb-2">📱 Scan QR Code to Pay</p>
                            <img 
                              src={qrCodeUrl} 
                              alt="UPI QR Code" 
                              className="mx-auto w-40 h-40 border-2 border-gray-200 rounded-lg"
                            />
                            <p className="text-xs text-gray-500 mt-2">Open GPay/PhonePe/Paytm → Scan QR</p>
                          </div>
                          
                          {/* Alternative Payment Options */}
                          <div className="grid grid-cols-2 gap-2">
                            <Button 
                              variant="outline" 
                              className="h-12 border-green-500 text-green-700 hover:bg-green-50"
                              onClick={() => {
                                const msg = `Hi, I want to pay ₹${dueAmount} for my milk bill (${bill.month}). Customer: ${customerName}`;
                                window.open(`https://wa.me/917075769452?text=${encodeURIComponent(msg)}`, '_blank');
                              }}
                            >
                              💬 Pay via WhatsApp
                            </Button>
                            <Button 
                              variant="outline"
                              className="h-12 border-blue-500 text-blue-700 hover:bg-blue-50"
                              onClick={() => {
                                navigator.clipboard?.writeText(upiId);
                                toast.success(`UPI ID copied: ${upiId}`);
                              }}
                            >
                              📋 Copy UPI ID
                            </Button>
                          </div>
                          
                          {/* UPI ID Display */}
                          <div className="bg-gray-50 p-3 rounded-lg border text-center">
                            <p className="text-sm text-gray-500">Or pay manually to UPI ID:</p>
                            <p className="font-mono text-base font-bold text-blue-600 break-all">{upiId}</p>
                            <p className="text-xs text-gray-400 mt-1">Works with GPay, PhonePe, Paytm, BHIM, any UPI app</p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </CardContent>
              </Card>
            )}

            {/* Monthly Bills */}
            <Card>
              <CardHeader>
                <CardTitle>Monthly Bills</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {data.bills?.length === 0 ? (
                  <p className="p-6 text-gray-500 text-center">No bills found</p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Month</TableHead>
                        <TableHead>Days</TableHead>
                        <TableHead className="text-right">Total</TableHead>
                        <TableHead className="text-right">Paid</TableHead>
                        <TableHead>Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {data.bills?.map((bill, i) => (
                        <TableRow key={i}>
                          <TableCell className="font-medium">{bill.month}</TableCell>
                          <TableCell>{bill.total_deliveries}</TableCell>
                          <TableCell className="text-right">₹{bill.final_amount}</TableCell>
                          <TableCell className="text-right">₹{bill.amount_paid || 0}</TableCell>
                          <TableCell>
                            <Badge className={bill.payment_status === 'paid' ? 'bg-green-500' : bill.payment_status === 'partial' ? 'bg-yellow-500' : 'bg-red-500'}>
                              {bill.payment_status?.toUpperCase()}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>

            {/* Contact */}
            <Card className="bg-blue-50">
              <CardContent className="p-4 text-center">
                <p className="text-gray-600">For any changes to your subscription, please contact:</p>
                <p className="font-semibold text-blue-600 mt-1">📞 8125995777 | 📱 WhatsApp</p>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </ShopLayout>
  );
};

// ==========================================
// MAIN APP
// ==========================================

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <CustomerAuthProvider>
          <Routes>
            {/* Admin Routes */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/dashboard" element={<ProtectedRoute><Layout><DashboardPage /></Layout></ProtectedRoute>} />
            <Route path="/products" element={<ProtectedRoute><Layout><ProductsPage /></Layout></ProtectedRoute>} />
            <Route path="/suppliers" element={<ProtectedRoute><Layout><SuppliersPage /></Layout></ProtectedRoute>} />
            <Route path="/purchases" element={<ProtectedRoute><Layout><PurchasesPage /></Layout></ProtectedRoute>} />
            <Route path="/sales" element={<ProtectedRoute><Layout><SalesPage /></Layout></ProtectedRoute>} />
            <Route path="/reports" element={<ProtectedRoute><Layout><ReportsPage /></Layout></ProtectedRoute>} />
            <Route path="/customer-analytics" element={<ProtectedRoute><Layout><CustomerAnalyticsPage /></Layout></ProtectedRoute>} />
            <Route path="/alerts" element={<ProtectedRoute><Layout><AlertsPage /></Layout></ProtectedRoute>} />
            <Route path="/customer-orders" element={<ProtectedRoute><Layout><CustomerOrdersPage /></Layout></ProtectedRoute>} />
            <Route path="/subscriptions-manage" element={<ProtectedRoute><Layout><SubscriptionManagePage /></Layout></ProtectedRoute>} />
            
            {/* Customer Shop Routes */}
            <Route path="/shop" element={<ShopHomePage />} />
            <Route path="/shop/login" element={<ShopLoginPage />} />
            <Route path="/shop/products" element={<ShopProductsPage />} />
            <Route path="/shop/cart" element={<ShopCartPage />} />
            <Route path="/shop/checkout" element={<ShopCheckoutPage />} />
            <Route path="/shop/orders" element={<ShopOrdersPage />} />
            <Route path="/shop/subscriptions" element={<ShopSubscriptionsPage />} />
            <Route path="/shop/pay-bill" element={<PayBillPage />} />
            <Route path="/buy/:slug" element={<BuyNowPage />} />
            <Route path="/shop/my-subscription" element={<ShopMyMilkSubscriptionPage />} />
            <Route path="/shop/account" element={<ShopAccountPage />} />
            
            {/* Default redirect */}
            <Route path="/" element={<Navigate to="/shop" replace />} />
          </Routes>
          <Toaster position="top-right" richColors />
        </CustomerAuthProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
